import * as React from 'react';
import React__default, { useState, useMemo } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { Formik, Form, Field, FastField } from 'formik';
import { isValid, isFuture, isPast, differenceInYears } from 'date-fns';
import parsePhoneNumber from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';

/**
 * Brand Colors
 * Sync with Sketch
 */
var colors = {
  beige: '#F7F4F1',
  purpleDark: '#474676',
  purpleMid: '#5e5f93',
  purpleBright: '#a8b1f5',
  purpleLight: '#d5d8f3',
  purpleLightest: '#eeeffd',
  greyDark: '#696883',
  grey: '#979797',
  greyIcon: '#a0a1bb',
  greyText: '#74758B',
  greyMid: '#777997',
  greyLight: '#e4e4eb',
  greyLightest: '#f5f5f8',
  pinkDark: '#f37283',
  pink: '#f17a8b',
  pinkMid: '#ffb7a6',
  pinkLight: '#ffd0c7',
  pinkLightest: '#ffebe7',
  pinkLightest2: '#fff6f4',
  pinkLightest3: '#ffeae6',
  green: '#75e5d0',
  greenLight: '#baf2e7',
  yellowDark: '#ffc880',
  yellowMid: '#ffdcad',
  yellowLight: '#FFEDD5',
  blue: '#D7F0FC',
  blueLight: '#effaff',
  white: '#fff',
  shroud: 'rgba(0,0,0,.6)',
  red: '#d04354'
};
var shadows = {
  box1: '0 0 10px 3px rgba(0, 0, 0, 0.1)',
  box2: '0 0 10px 3px rgba(0, 0, 0, 0.05)',
  avatar: '0 2px 7px 0 rgba(0,0,0,0.1), 0 2px 37px 0 rgba(0,0,0,0.09)'
};
var cardStyle = "\n  box-shadow: ".concat(shadows.box2, ";\n  border-radius: 1.5rem;\n\n  &:hover {\n    transform: translateY(-2px);\n    transition: all 0.2s;\n    box-shadow: ").concat(shadows.box1, ";\n  }\n");
var cardStyleNoHover = "\n  box-shadow: ".concat(shadows.box2, ";\n  border-radius: 1.5rem;\n");
var fixedStyle = {
  image: "\n    box-shadow: 0 2px 10px 0 rgba(144, 144, 144, 0.1), 0 2px 17px 0 rgba(0, 0, 0, 0.15), inset 0 1px 3px 0 rgba(0, 0, 0, 0.5);\n    border-radius: 1.5rem;\n    overflow: hidden;\n  "
  /**
   * Brand font constants
   */

};
var fontFamily = {
  base: '"Muli", sans-serif'
};
var fontWeight = {
  medium: '400',
  bold: '700',
  moreBold: '800',
  extraBold: '900'
};
var fontSmoothing = "\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n";
var fontBase = "\n  color: ".concat(colors.purpleDark, ";\n  font-family: ").concat(fontFamily.base, ";\n  font-weight: ").concat(fontWeight.medium, ";\n  ").concat(fontSmoothing, ";\n");
/**
 * Media Query Config
 */

var MQ_XS = 450;
var MQ_SM = 650;
var MQ_MD = 960;
var MQ_LG = 1280;
var MQ_XL = 1920;
/**
 * Styled Component Media Query Helpers
 */

var mq = {
  xs: function xs(styles) {
    var mqWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MQ_XS;
    return "@media only screen and (max-width: ".concat(mqWidth, "px) { ").concat(styles, " }");
  },
  sm: function sm(styles) {
    var mqWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MQ_SM;
    return "@media only screen and (max-width: ".concat(mqWidth, "px) { ").concat(styles, " }");
  },
  md: function md(styles) {
    var mqWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MQ_MD;
    return "@media only screen and (max-width: ".concat(mqWidth, "px) { ").concat(styles, " }");
  },
  lg: function lg(styles) {
    var mqWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MQ_LG;
    return "@media only screen and (max-width: ".concat(mqWidth, "px) { ").concat(styles, " }");
  },
  xl: function xl(styles) {
    var mqWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MQ_XL;
    return "@media only screen and (max-width: ".concat(mqWidth, "px) { ").concat(styles, " }");
  }
  /**
   * Brand typography
   * Sync with Sketch
   */

};
var text = {
  titleXL: "\n    ".concat(fontBase, ";\n    font-size: 4.8rem;\n    line-height: 5.5rem;\n\n    ").concat(mq.md("\n      font-size: 3.4rem;\n      line-height: 4.0rem;\n    "), "\n  "),
  titleXLBold: "\n    ".concat(fontBase, ";\n    font-size: 4.8rem;\n    line-height: 5.5rem;\n    font-weight: ").concat(fontWeight.moreBold, ";\n\n    ").concat(mq.md("\n      font-size: 2.8rem;\n      line-height: 3.4rem;\n    "), "\n  "),
  titleLarge: "\n    ".concat(fontBase, ";\n    font-size: 4.0rem;\n    line-height: 4.6rem;\n    font-weight: ").concat(fontWeight.bold, ";\n\n    ").concat(mq.md("\n      font-size: 3.0rem;\n      line-height: 3.4rem;\n    "), "\n  "),
  title1: "\n    ".concat(fontBase, ";\n    font-size: 3.4rem;\n    line-height: 4.0rem;\n    font-weight: ").concat(fontWeight.bold, ";\n\n    ").concat(mq.md("\n      font-size: 2.8rem;\n      line-height: 3.2rem;\n    "), "\n  "),
  title1Bold: "\n    ".concat(fontBase, ";\n    font-size: 3.4rem;\n    line-height: 4.0rem;\n    font-weight: ").concat(fontWeight.moreBold, ";\n\n    ").concat(mq.md("\n      font-size: 2.1rem;\n      line-height: 2.7rem;\n    "), "\n  "),
  title1title2: "\n    ".concat(fontBase, ";\n    font-size: 3.4rem;\n    line-height: 4.0rem;\n    font-weight: ").concat(fontWeight.bold, ";\n\n    ").concat(mq.md("\n      font-size: 2.8rem;\n      line-height: 3.2rem;\n    "), "\n  "),
  title2: "\n    ".concat(fontBase, ";\n    font-size: 3.0rem;\n    line-height: 3.6rem;\n    font-weight: ").concat(fontWeight.bold, ";\n  "),
  title3: "\n    ".concat(fontBase, ";\n    font-size: 2.0rem;\n    line-height: 2.6rem;\n    font-weight: ").concat(fontWeight.bold, ";\n  "),
  title4: "\n    ".concat(fontBase, ";\n    font-size: 2.0rem;\n    line-height: 2.6rem;\n  "),
  body: "\n    ".concat(fontBase, ";\n    color: ").concat(colors.greyMid, ";\n    font-size: 1.7rem;\n    line-height: 2.3rem;\n    "),
  button: "\n    ".concat(fontBase, ";\n    color: ").concat(colors.pinkDark, ";\n    font-size: 1.7rem;\n    line-height: 2.3rem;\n    font-weight: ").concat(fontWeight.bold, ";\n  "),
  headline: "\n    ".concat(fontBase, ";\n    font-size: 1.7rem;\n    line-height: 2.3rem;\n    font-weight: ").concat(fontWeight.bold, ";\n  "),
  caption1: "\n    ".concat(fontBase, ";\n    font-size: 1.5rem;\n    line-height: 2.1rem;\n  "),
  caption1Bold: "\n    ".concat(fontBase, ";\n    font-size: 1.5rem;\n    line-height: 2.1rem;\n    font-weight: ").concat(fontWeight.bold, ";\n  "),
  caption2: "\n    ".concat(fontBase, ";\n    color: ").concat(colors.greyMid, ";\n    font-size: 1.5rem;\n    line-height: 2.1rem;\n  "),
  footnote: "\n    ".concat(fontBase, ";\n    font-size: 1.3rem;\n    line-height: 1.9rem;\n  ")
  /**
   * Layoutable
   */

};
var layoutable = function layoutable(p) {
  var marginTop = p.spacerTop2 ? 2 : p.spacerTop ? 1 : 0;
  var marginBottom = p.spacerBottom2 ? 2 : p.spacerBottom ? 1 : 0;
  var paddingHorizontal = p.padded ? 2 : 0;
  return "\n    margin-top: ".concat(marginTop, "rem;\n    margin-bottom: ").concat(marginBottom, "rem;\n    padding-left: ").concat(paddingHorizontal, "rem;\n    padding-right: ").concat(paddingHorizontal, "rem;\n  ");
};
/**
 * Theme
 */

var theme = {
  colors: colors,
  shadows: shadows,
  fixedStyle: fixedStyle,
  text: text,
  fontFamily: fontFamily,
  mq: mq,
  layoutable: layoutable,
  gridWidth: 1142,
  fontSmoothing: fontSmoothing,
  fontWeight: fontWeight
  /**
   * Lightbox Theme
   */

};
var LightboxTheme = {
  footerCount: {
    fontSize: '1.4rem',
    fontFamily: fontFamily.base,
    color: 'rgb(233, 143, 156)'
  },
  footerCaption: {
    fontSize: '1.4rem',
    fontFamily: fontFamily.base,
    color: 'rgb(233, 143, 156)'
  },
  arrow: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    fill: '#ffffff',
    opacity: 0.7,
    transition: 'opacity 200ms',
    ':hover': {
      opacity: 1
    }
  },
  arrow__size__medium: {
    borderRadius: 40,
    height: 40,
    marginTop: -20,
    '@media (min-width: 768px)': {
      height: 70,
      padding: 15
    }
  },
  arrow__direction__left: {
    marginLeft: 10
  },
  arrow__direction__right: {
    marginRight: 10
  },
  close: {
    fill: '#f37283'
  }
};

var ThemeWrapper = function ThemeWrapper(_ref) {
  var children = _ref.children;
  return React__default.createElement(ThemeProvider, {
    theme: theme
  }, children);
};

var BlobTopWhite = function BlobTopWhite() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "35",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 1440 35",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    d: "M1440 0v2c-237.85 21.358-475.7 32.037-713.55 32.037C484.3 34.037 242.15 23.357 0 2V0h1440z",
    fill: "#FFF",
    fillRule: "nonzero"
  }));
};
var BlobTopWhiteOpposite = function BlobTopWhiteOpposite() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "35",
    viewBox: "0 0 1440 35",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 35v-2C237.85 11.642 475.7.963 713.55.963 955.7.963 1197.85 11.643 1440 33v2H0z",
    fill: "#FFF",
    fillRule: "nonzero"
  }));
};
var BlobTopWhiteLarge = function BlobTopWhiteLarge() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "574",
    viewBox: "0 0 1440 574",
    xmlns: "http://www.w3.org/2000/svg",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    d: "M1440 552.189c-61.565 18.23-115.668 22.732-161.926 20.787-210.708-8.863-342.372-154.963-509.369-295.963-116.543-98.398-250.29-194.318-440.138-239.375a918.159 918.159 0 0 0-24.679-5.5C261.207 23.245 218.801 19 177.185 19A608.633 608.633 0 0 0 0 45.323V0h1440v552.189z",
    fill: "#FFF",
    fillRule: "nonzero"
  }));
};
var BlobBottomWhite = function BlobBottomWhite() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "289",
    viewBox: "0 0 1440 289",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1440 289H0v-26.32c61.565 8.981 115.668 11.199 161.926 10.24 210.708-4.366 342.372-76.343 509.369-145.808 116.543-48.477 250.29-95.732 440.138-117.93 8.241-.964 16.467-1.866 24.679-2.71A1242.262 1242.262 0 0 1 1262.815 0c61.29 0 120.878 4.534 177.185 12.968V289z",
    fill: "#FFF",
    fillRule: "nonzero"
  }));
};
var BlobTop = function BlobTop() {
  var color = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '#F5F5F8';
  return React__default.createElement("svg", {
    width: "1440",
    height: "39",
    viewBox: "0 0 1440 39",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1440 38.037C1202.15 16.679 964.3 6 726.45 6 484.3 6 242.15 16.679 0 38.037V0h1440v38.037z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var BlobTopSlim = function BlobTopSlim() {
  var color = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '#ffffff';
  return React__default.createElement("svg", {
    width: "1440",
    height: "165",
    viewBox: "0 0 1440 165",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1440 13.782c-61.565-5.16-115.668-6.434-161.926-5.884-210.708 2.51-342.372 43.863-509.369 83.774-116.543 27.852-250.29 55.002-440.138 67.755-8.241.554-16.467 1.073-24.679 1.557a2155.603 2155.603 0 0 1-126.703 3.72c-61.29 0-120.878-2.606-177.185-7.452V0h1440v13.782z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var BlobBottomSlimReverse = function BlobBottomSlimReverse() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "165",
    viewBox: "0 0 1440 165",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 150.922c61.565 5.16 115.668 6.434 161.926 5.884 210.708-2.51 342.372-43.863 509.369-83.774C787.838 45.18 921.585 18.03 1111.433 5.277a2995.9 2995.9 0 0 1 24.679-1.557A2155.602 2155.602 0 0 1 1262.815 0c61.29 0 120.878 2.606 177.185 7.452v157.252H0v-13.782z",
    fill: "#FFF",
    fillRule: "nonzero"
  }));
};
var BlobBottomSlim = function BlobBottomSlim() {
  var color = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '#ffffff';
  return React__default.createElement("svg", {
    width: "1440",
    height: "175",
    viewBox: "0 0 1440 175",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 175V7.45C56.307 2.606 115.896 0 177.185 0c41.616 0 84.022 1.201 126.703 3.719 8.212.485 16.438 1.003 24.68 1.557C518.413 18.029 652.161 45.18 768.704 73.03c166.997 39.911 298.661 81.265 509.37 83.774 46.257.55 100.36-.724 161.925-5.884V175H0z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var BlobTopSlimReverse = function BlobTopSlimReverse() {
  var color = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '#ffffff';
  return React__default.createElement("svg", {
    width: "1440",
    height: "175",
    viewBox: "0 0 1440 175",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1440 0v167.55c-56.307 4.844-115.896 7.45-177.185 7.45a2155.05 2155.05 0 0 1-126.703-3.719 3043.601 3043.601 0 0 1-24.68-1.557c-189.845-12.753-323.593-39.904-440.136-67.754-166.997-39.911-298.661-81.265-509.37-83.774-46.257-.55-100.36.724-161.925 5.884V0H1440z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var BlobCenterPink = function BlobCenterPink() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "1227",
    viewBox: "0 0 1440 1227",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 1226.7V26.301A609.087 609.087 0 0 1 177.185 0c41.616 0 84.022 4.241 126.703 13.128a918.795 918.795 0 0 1 24.68 5.495c189.846 45.02 323.594 140.863 440.137 239.182 166.997 140.886 298.661 286.868 509.37 295.724 46.257 1.943 100.36-2.555 161.925-20.77v451.6c-94.699-18.217-177.92-22.714-249.073-20.771-290.708 0-427.719 128.315-605.41 184.237-179.733 56.564-346.734 78.546-585.517 78.874z",
    fill: "#FFF6F5",
    fillRule: "evenodd",
    opacity: ".9"
  }));
};
var BlobCenterPurple = function BlobCenterPurple() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "866",
    viewBox: "0 0 1440 866",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 918h-7V-50h7v200.252c56.307 4.846 115.896 7.452 177.185 7.451 41.616 0 84.022-1.201 126.703-3.719 8.212-.484 16.438-1.003 24.68-1.557 189.846-12.753 323.594-39.903 440.137-67.755C935.702 44.76 1067.366 3.407 1278.075.898c46.257-.55 100.36.724 161.925 5.884v853.139c-61.565 5.16-115.668 6.434-161.926 5.884-210.708-2.509-342.372-43.863-509.369-83.774-116.543-27.851-250.29-55.002-440.138-67.755a3034.204 3034.204 0 0 0-24.679-1.557A2155.604 2155.604 0 0 0 177.185 709c-61.29 0-120.878 2.605-177.185 7.45V918z",
    fill: "#474775",
    fillRule: "evenodd"
  }));
};
var BlobLeftGrey = function BlobLeftGrey() {
  return React__default.createElement("svg", {
    width: "26",
    height: "515",
    viewBox: "0 0 26 515",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 515V0h2.003C18 86.602 26 173.205 26 259.807 26 344.87 18 429.936 2.003 515H0z",
    fill: "#f5f5f8",
    fillRule: "evenodd"
  }));
};
var BlobTopGrey = function BlobTopGrey() {
  return React__default.createElement("svg", {
    width: "1440",
    height: "354",
    viewBox: "0 0 1440 354",
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1439.5.5H.5v340.107c57.008 8.513 116.448 12.894 176.685 12.893 42.155 0 84.529-2.145 126.652-6.467 8.265-.849 16.488-1.75 24.672-2.708 89.099-10.412 169.97-26.771 247.478-49.07 63.875-18.376 122.955-39.843 192.526-68.767a3451.825 3451.825 0 0 0 17.815-7.468c10.478-4.425 103.389-44.484 130.208-55.53 62.897-25.91 113.755-43.635 166.586-56.925 62.555-15.735 125.894-24.423 194.942-25.853 51.866-1.074 105.17 1.985 161.436 10.158V.5z",
    fill: "#F5F5F8",
    fillRule: "evenodd"
  }));
};
var BlobYellowStandalone = function BlobYellowStandalone() {
  return React__default.createElement("svg", {
    width: "410",
    height: "392",
    viewBox: "0 0 410 392",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M306.606.002c50.78-.598 109.75 107.44 102.84 204.477-.697 9.783-9.862 116.323-90.42 164.225-3.05 1.815-12.245 7.137-25.166 11.949-69.137 25.756-166.983 7.886-228.048-55.86-55.957-58.413-86.605-161.175-49.689-201.61 36.896-40.41 110.043 15.642 192.66-31.724C271.079 55.744 270.387.43 306.606.002z",
    fill: "#FFFAF2",
    fillRule: "evenodd"
  }));
};
var BlobPurpleBottom = function BlobPurpleBottom() {
  var light = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  return React__default.createElement("svg", {
    width: "1440",
    height: "33",
    viewBox: "0 0 1440 33",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1440 33H0V.963C242.15 22.321 484.3 33 726.45 33 964.3 33 1202.15 22.321 1440 .963V33z",
    fill: light ? '#5e5e97' : '#474775',
    fillRule: "evenodd"
  }));
};
var BlobYellowGrad = function BlobYellowGrad() {
  return React__default.createElement("svg", {
    width: "874",
    height: "836",
    viewBox: "0 0 874 836",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("linearGradient", {
    x1: "50%",
    y1: "58.849%",
    x2: "87.83%",
    y2: "105.564%",
    id: "yellowGrad"
  }, React__default.createElement("stop", {
    stopColor: "#FFC880",
    offset: "0%"
  }), React__default.createElement("stop", {
    stopColor: "#FFE3BD",
    offset: "100%"
  }))), React__default.createElement("path", {
    d: "M653.594.005C761.841-1.27 887.548 229.138 872.817 436.083c-1.484 20.864-21.02 248.076-192.747 350.235-6.503 3.87-26.103 15.22-53.647 25.483-147.38 54.928-355.958 16.818-486.13-119.13C21.008 568.096-44.325 348.94 34.37 262.707c78.65-86.178 234.577 33.36 410.693-67.655C577.862 118.882 576.385.918 653.594.005z",
    fill: "url(#yellowGrad)",
    fillRule: "evenodd"
  }));
};
var BlobPurpleGrad = function BlobPurpleGrad() {
  return React__default.createElement("svg", {
    width: "761",
    height: "728",
    viewBox: "0 0 761 728",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("linearGradient", {
    x1: "70.421%",
    y1: "48.152%",
    x2: "120.677%",
    y2: "72.794%",
    id: "purpleBlob"
  }, React__default.createElement("stop", {
    stopColor: "#D5D8F3",
    offset: "0%"
  }), React__default.createElement("stop", {
    stopColor: "#E5E7F5",
    offset: "100%"
  }))), React__default.createElement("path", {
    d: "M569.09.005c94.252-1.112 203.706 199.532 190.88 379.742-1.292 18.168-18.303 216.027-167.827 304.99-5.662 3.37-22.728 13.254-46.71 22.19-128.326 47.832-309.937 14.645-423.28-103.74-103.86-108.482-160.746-299.325-92.226-374.42 68.481-75.044 204.249 29.05 357.595-58.914C503.15 103.524 501.864.799 569.09.005z",
    fill: "url(#purpleBlob)",
    transform: "matrix(1 0 0 -1 0 728)",
    fillRule: "evenodd"
  }));
};
var BlobGreenGrad = function BlobGreenGrad() {
  return React__default.createElement("svg", {
    width: "620",
    height: "618",
    viewBox: "0 0 620 618",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("linearGradient", {
    x1: "50%",
    y1: "58.849%",
    x2: "87.83%",
    y2: "105.564%",
    id: "greenBlob"
  }, React__default.createElement("stop", {
    stopColor: "#75E5D0",
    offset: "0%"
  }), React__default.createElement("stop", {
    stopColor: "#CFFFF6",
    offset: "100%"
  }))), React__default.createElement("path", {
    d: "M463.648.004c76.789-.947 165.963 169.931 155.513 323.406-1.053 15.474-14.912 183.98-136.732 259.744-4.613 2.87-18.517 11.289-38.056 18.9-104.548 40.735-252.51 12.472-344.852-88.35-84.618-92.39-130.963-254.92-75.14-318.874 55.794-63.912 166.406 24.74 291.34-50.175C409.925 88.166 408.877.68 463.648.004z",
    transform: "scale(-1 1) rotate(1 150.902 -35234.6)",
    fill: "url(#greenBlob)",
    fillRule: "evenodd"
  }));
};
var BlobPinkGrad = function BlobPinkGrad() {
  return React__default.createElement("svg", {
    width: "886",
    height: "626",
    viewBox: "0 0 886 626",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("linearGradient", {
    x1: "76.538%",
    y1: "30.57%",
    x2: "78.378%",
    y2: "68.275%",
    id: "blobPinkGrad"
  }, React__default.createElement("stop", {
    stopColor: "#FFD0C7",
    offset: "0%"
  }), React__default.createElement("stop", {
    stopColor: "#FFEBE7",
    offset: "100%"
  }))), React__default.createElement("path", {
    d: "M593.844 1.747c116.38-20.285 301.537 139.122 291.786 320.956-5.137 95.753-64.932 209.171-158.23 237.532-114.757 34.887-174.278-91.101-321.402-74.226-149.509 17.146-188.128 158.723-272.92 137.907C24.203 597.185-48.017 332.025 38 237.71c70.599-77.406 210.563 29.964 368.657-60.768C521.237 111.176 511.419 16.116 593.844 1.747z",
    fill: "url(#blobPinkGrad)",
    fillRule: "evenodd"
  }));
};
var BlobProfile = function BlobProfile() {
  return React__default.createElement("svg", {
    width: "210",
    height: "201",
    viewBox: "0 0 210 201",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M204.45 72.256c16.709 19.417-5.973 77.238-45.773 106.044-4.012 2.905-48.222 33.923-92.901 18.348-1.693-.59-6.736-2.414-12.791-5.843C20.583 172.457-4.25 128.87.609 84.609 5.06 44.049 34.89-1.121 62.519.02 90.13 1.164 92.17 47.596 137.31 64.141c34.037 12.474 55.221-5.735 67.14 8.115z",
    fill: "#A8B1F5",
    fillRule: "evenodd",
    fillOpacity: ".4"
  }));
};
var BlobBar = function BlobBar() {
  return React__default.createElement("svg", {
    width: "770",
    height: "477",
    viewBox: "0 0 770 477",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M768.969 218.815c-7.338 116.68-159.685 248.527-357.335 257.491C212.87 485.323 48.61 367.67 10.727 238.873c-28.873-98.163 2.207-222.74 73.206-237.5C167.08-15.913 217.52 135.458 385.268 153.007c150.265 15.72 249.713-91.1 331.472-51.5 57.843 28.019 53.11 103.307 52.229 117.307",
    fill: "#FFF",
    fillRule: "evenodd",
    fillOpacity: ".1"
  }));
};
var PanelBlob = function PanelBlob() {
  return React__default.createElement("svg", {
    width: "545",
    height: "279",
    viewBox: "0 0 545 279",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M252.569 3.299C66.016-29.202-230.787 226.206-215.157 517.546c8.234 153.419 104.085 335.142 253.638 380.583 183.954 55.897 279.364-145.965 515.201-118.927 239.659 27.472 301.565 254.31 437.483 220.96 174.527-42.831 290.293-467.68 152.41-618.795-113.168-124.023-337.528 48.01-590.948-97.365C368.956 178.63 384.695 26.321 252.57 3.3z",
    fill: "#F5F5F8",
    fillRule: "evenodd",
    fillOpacity: ".7"
  }));
};

var FlowArrowRight = function FlowArrowRight() {
  return React__default.createElement("svg", {
    width: "379",
    height: "228",
    viewBox: "0 0 379 228",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M297.899 183.471l-53.006 4.772c-90.76 8.17-170.959-58.782-179.129-149.542a165 165 0 0 1 2.367-46.277l2.637-13.564c6.125-15.003 16.982-28.55 32.57-40.639",
    stroke: "#FFC880",
    strokeWidth: "5",
    strokeLinecap: "round",
    strokeDasharray: "1,18,1,1"
  }), React__default.createElement("path", {
    d: "M294.507 182.702l.483 3.03c.09.563.605.947 1.152.86l13.666-2.175-2.836 4.01a1.05 1.05 0 0 0 .228 1.439l2.427 1.812a.984.984 0 0 0 1.402-.222l7.244-10.246a1.05 1.05 0 0 0-.228-1.438l-10.053-7.474a.985.985 0 0 0-1.4.223l-1.744 2.48a1.047 1.047 0 0 0 .23 1.436l3.92 2.916-13.662 2.174a1.017 1.017 0 0 0-.829 1.175",
    fill: "#FFC880"
  })));
};
var FlowArrowLeft = function FlowArrowLeft() {
  return React__default.createElement("svg", {
    width: "334",
    height: "248",
    viewBox: "0 0 334 248",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M50 190.79l-.002-3.068c-.001-.57-.45-1.03-1.004-1.029l-13.838.01 3.428-3.517a1.05 1.05 0 0 0 0-1.457l-2.113-2.169a.984.984 0 0 0-1.42 0l-8.758 8.987a1.05 1.05 0 0 0 .001 1.456l8.76 8.955a.985.985 0 0 0 1.418-.003l2.11-2.175a1.047 1.047 0 0 0-.002-1.455l-3.417-3.493 13.835-.01A1.017 1.017 0 0 0 50 190.79",
    fill: "#FFC880"
  }), React__default.createElement("path", {
    d: "M46.252 189.692c37.08-.02 92.293-5.428 165.638-16.223 62.522-9.202 105.746-67.345 96.544-129.867-6.122-41.595-34.562-76.482-74.07-90.862",
    stroke: "#FFC880",
    strokeWidth: "5",
    strokeLinecap: "round",
    strokeDasharray: "1,18,1,1"
  })));
};
var FlowArrowDown = function FlowArrowDown() {
  return React__default.createElement("svg", {
    width: "20",
    height: "87",
    viewBox: "0 0 20 87",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M8.467 63l3.069.002c.57.001 1.03.45 1.029 1.004l-.01 13.838 3.517-3.428a1.05 1.05 0 0 1 1.457 0l2.169 2.113a.984.984 0 0 1 0 1.42l-8.987 8.758a1.05 1.05 0 0 1-1.456-.001l-8.954-8.76a.985.985 0 0 1 .002-1.418l2.175-2.11a1.047 1.047 0 0 1 1.455.002l3.493 3.417.01-13.835A1.017 1.017 0 0 1 8.468 63",
    fill: "#FFC880"
  }), React__default.createElement("path", {
    d: "M10.5 66.5v-74",
    stroke: "#FFC880",
    strokeWidth: "5",
    strokeLinecap: "round",
    strokeDasharray: "1,18,1,1"
  })));
};

var IconArrowLeft = function IconArrowLeft(_ref) {
  var _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'currentColor' : _ref$color,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 24 : _ref$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 0.875,
    viewBox: "0 0 24 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M24 11.49v-3a1 1 0 0 0-1-1H9.16l3.43-3.43a1 1 0 0 0 0-1.42L10.48.52a1 1 0 0 0-1.42 0L.3 9.29a1 1 0 0 0 0 1.42l8.76 8.73a1 1 0 0 0 1.42 0l2.11-2.12a1 1 0 0 0 0-1.42l-3.42-3.4H23a1 1 0 0 0 1-1.01z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var IconArrowRight = function IconArrowRight(_ref2) {
  var _ref2$color = _ref2.color,
      color = _ref2$color === void 0 ? 'currentColor' : _ref2$color,
      _ref2$size = _ref2.size,
      size = _ref2$size === void 0 ? 24 : _ref2$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 0.875,
    viewBox: "0 0 24 21",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M0 12.15L.002 8.96c.001-.593.45-1.072 1.004-1.07l13.838.01-3.428-3.66a1.123 1.123 0 0 1 0-1.515L13.529.468a.958.958 0 0 1 1.42 0l8.758 9.348c.391.419.39 1.098-.001 1.515l-8.76 9.316a.958.958 0 0 1-1.418-.002l-2.11-2.263a1.121 1.121 0 0 1 .002-1.514l3.417-3.634-13.835-.01C.448 13.221 0 12.742 0 12.15",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconAssessment = function IconAssessment(_ref3) {
  var _ref3$color = _ref3.color,
      color = _ref3$color === void 0 ? 'currentColor' : _ref3$color,
      _ref3$size = _ref3.size,
      size = _ref3$size === void 0 ? 24 : _ref3$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 49 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6 4V8C4.97433 8 4.12899 8.77208 4.01346 9.76676L4 10V42C4 43.0257 4.77208 43.871 5.76676 43.9865L6 44H30C31.0257 44 31.871 43.2279 31.9865 42.2332L32 42V37.094L36 33.094V44C36 46.2091 34.2091 48 32 48H4C1.79086 48 0 46.2091 0 44V8C0 5.79086 1.79086 4 4 4H6ZM23.5395 30.7972L29.2374 36.4129L22.8025 38.0133C22.3768 38.1195 21.9927 37.7963 22.0001 37.3909L22.0175 37.2532L23.5395 30.7972ZM37.9185 16.518L43.5758 22.1744L30.8467 34.9012L25.1923 29.2451L37.9185 16.518ZM41.5913 12.7456C42.4935 11.8223 43.9114 11.7559 44.874 12.5499L45.073 12.7332L47.5477 15.2753C48.2324 15.9787 48.2813 17.0835 47.6971 17.8528L47.5379 18.0377L44.7526 20.8978L39.2035 15.1975L41.5913 12.7456ZM32 4C34.2091 4 36 5.79086 36 8V14.9L32 18.902V10C32 8.89543 31.1046 8 30 8V4H32ZM24 0C25.1046 0 26 0.89543 26 2V6C26 7.10457 25.1046 8 24 8H12C10.8954 8 10 7.10457 10 6V2C10 0.89543 10.8954 0 12 0H24Z",
    fill: color
  }));
};
var IconMeal = function IconMeal(_ref4) {
  var _ref4$color = _ref4.color,
      color = _ref4$color === void 0 ? 'currentColor' : _ref4$color,
      _ref4$size = _ref4.size,
      size = _ref4$size === void 0 ? 24 : _ref4$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M17.387.823a.75.75 0 00-.75-.75h-2.63a.75.75 0 00-.75.748v12.66a.5.5 0 01-.5.5h-1.063a.5.5 0 01-.5-.5V.823a.75.75 0 00-.75-.75h-2.63a.75.75 0 00-.75.75v1.226C7.065 2.056 6 14.026 6 15.122c0 4.825 6.079 6.822 6.079 6.822s-.869 17.964-.869 22.01v3.047c0 .552.447.999 1 .999H18.525a1 1 0 00.999-1v-3.046c0-.57-1.061-22.01-1.061-22.01s6.022-1.997 6.022-6.822c0-.03-.905-13.073-.905-13.073V.823a.75.75 0 00-.75-.75h-2.629a.75.75 0 00-.75.75V13.48a.5.5 0 01-.5.5h-1.064a.5.5 0 01-.5-.5V.823zm18.705 25.574c-1.253-.231-2.366-.68-3.02-1.675a5.755 5.755 0 01-.824-2.006c-1.057-5.377-.656-10.67 1.312-15.837.871-2.287 2.218-4.34 4.204-5.983.433-.359 1.015-.665 1.58-.775 1.376-.267 2.508.598 2.636 1.9.02.199.02.4.02.6V47.384a.5.5 0 01-.5.5h-6.566a1 1 0 01-.995-1.106l2.153-20.381z",
    fill: color
  }));
};
var IconBadgeVerified = function IconBadgeVerified(_ref5) {
  var _ref5$color = _ref5.color,
      color = _ref5$color === void 0 ? 'currentColor' : _ref5$color,
      _ref5$size = _ref5.size,
      size = _ref5$size === void 0 ? 25 : _ref5$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 25 25",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M25 12.5c0 1.069-1.253 1.957-1.517 2.943-.273 1.019.36 2.416-.156 3.308-.516.892-2.053 1.052-2.79 1.787-.735.735-.88 2.264-1.786 2.789-.906.525-2.288-.113-3.308.156C14.457 23.747 13.569 25 12.5 25c-1.068 0-1.958-1.253-2.943-1.517-1.019-.273-2.416.36-3.308-.156-.892-.516-1.052-2.053-1.787-2.79-.735-.735-2.264-.88-2.79-1.786-.524-.906.114-2.288-.155-3.308C1.253 14.457 0 13.569 0 12.5s1.253-1.958 1.517-2.943c.272-1.019-.36-2.416.156-3.308.516-.892 2.054-1.052 2.79-1.787.734-.735.88-2.264 1.786-2.789.906-.525 2.288.113 3.308-.156C10.543 1.253 11.433 0 12.5 0c1.067 0 1.957 1.253 2.943 1.517 1.019.273 2.416-.36 3.308.156.892.516 1.052 2.054 1.787 2.79.735.734 2.264.88 2.79 1.786.524.906-.114 2.288.155 3.308.26.985 1.517 1.874 1.517 2.943",
    fill: color
  }), React__default.createElement("path", {
    d: "M10.528 17.895l-3.335-2.532a.476.476 0 0 1-.085-.685l.942-1.154a.519.519 0 0 1 .71-.082l1.765 1.34 6.394-7.601a.518.518 0 0 1 .712-.072l1.182.925c.217.17.25.478.074.687l-7.654 9.098a.518.518 0 0 1-.705.076",
    fill: "#FFF"
  })));
};
var IconBell = function IconBell(_ref6) {
  var _ref6$color = _ref6.color,
      color = _ref6$color === void 0 ? 'currentColor' : _ref6$color,
      _ref6$size = _ref6.size,
      size = _ref6$size === void 0 ? 24 : _ref6$size;
  return React__default.createElement("svg", {
    width: size * 0.8333,
    height: size,
    viewBox: "0 0 20 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M18.998 10.7l-.006-.001c.786-3.19-.711-6.433-3.503-7.997l.207-.687c.211-.706-.073-1.495-.73-1.836-.887-.46-1.935.012-2.204.908l-.21.7c-3.391-.323-6.637 1.72-7.7 5.005l-.006-.002-2.28 8.014a1.794 1.794 0 01-1.442 1.274c-.517.085-.93.473-1.072.972a1.37 1.37 0 00.961 1.693l4.285 1.184c-.454 1.712.564 3.472 2.3 3.952 1.734.479 3.533-.503 4.049-2.197l4.414 1.22a1.294 1.294 0 001.59-.885l.041-.147c.142-.496-.05-1.006-.44-1.348a1.762 1.762 0 01-.535-1.807l2.281-8.014z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconBook = function IconBook(_ref7) {
  var _ref7$color = _ref7.color,
      color = _ref7$color === void 0 ? 'currentColor' : _ref7$color,
      _ref7$size = _ref7.size,
      size = _ref7$size === void 0 ? 24 : _ref7$size;
  return React__default.createElement("svg", {
    width: size * 0.95,
    height: size,
    viewBox: "0 0 45 50",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    d: "M20.2 18.316a1.643 1.643 0 001.653-1.529c.075-1.011-.699-1.785-1.815-1.788-2.707-.006-5.412-.002-8.118-.002-1.15 0-2.298-.006-3.447.003-.651.006-1.182.272-1.501.844-.642 1.15.142 2.463 1.482 2.473 1.935.015 3.87.004 5.805.004 1.98-.002 3.96.007 5.94-.005zm-9.974-9.973c-.635 0-1.272-.025-1.905.013-1.229.073-1.926 1.284-1.392 2.383.319.657.88.925 1.582.926 4.976.003 9.952.002 14.928.001.105 0 .212-.004.316-.019.896-.121 1.527-.886 1.456-1.761-.075-.93-.763-1.538-1.782-1.54-2.48-.008-4.96-.003-7.44-.004h-5.763zM42.3.057c.771.064 1.34.708 1.41 1.474.015.163.014.329.014.493 0 15.316.209 26.399.026 41.7-.025 2.104-1.47 3.93-2.48 4.574-2.448 1.784-3.453 1.675-5.858 1.687-2.405.012-17.964-.012-30.095 0-1.965.002-3.467-.788-4.545-2.395-.583-.868-.764-1.858-.764-2.87C-.002 31.547-.003 18.374.006 5.2.008 2.16 2.166.016 5.24.01c9.938-.012 19.876 0 29.814-.01 1.329-.001 1.953.869 1.951 1.954-.021 13.143-.013 26.286-.014 39.428 0 1.32-.634 1.942-1.981 1.942H5.515c-.212 0-.425-.005-.635.017-.867.09-1.493.776-1.504 1.635-.01.836.63 1.55 1.488 1.653.164.02.332.02.498.02 9.908 0 19.815-.004 29.723.003 1.998.002 3.528-.81 4.578-2.49.542-.866.703-1.833.703-2.84-.004-8.947-.002-17.893-.002-26.84 0-4.076.008-8.153-.008-12.229-.002-.726-.107-1.45-.167-2.208.667 0 1.393-.047 2.11.012z",
    id: "ba"
  })), React__default.createElement("g", {
    transform: "translate(.583)",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("mask", {
    id: "bb",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#ba"
  })), React__default.createElement("use", {
    fill: "#000",
    xlinkHref: "#ba"
  }), React__default.createElement("g", {
    mask: "url(#bb)"
  }, React__default.createElement("path", {
    fill: color,
    d: "M-2.083 0h50v50h-50z"
  }))));
};
var IconCalendar = function IconCalendar(_ref8) {
  var _ref8$size = _ref8.size,
      size = _ref8$size === void 0 ? 20 : _ref8$size,
      _ref8$color = _ref8.color,
      color = _ref8$color === void 0 ? 'currentColor' : _ref8$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M18 3h4a2 2 0 012 2v17a2 2 0 01-2 2H2a2 2 0 01-2-2V5a2 2 0 012-2h4V1.5a1.5 1.5 0 113 0V3h6V1.5a1.5 1.5 0 113 0V3zm-3 2H9v1.5a1.5 1.5 0 11-3 0V5H2v4h20V5h-4v1.5a1.5 1.5 0 11-3 0V5zM2 11v11h20V11H2z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconWallet = function IconWallet(_ref9) {
  var _ref9$size = _ref9.size,
      size = _ref9$size === void 0 ? 20 : _ref9$size,
      _ref9$color = _ref9.color,
      color = _ref9$color === void 0 ? 'currentColor' : _ref9$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 44 44",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M3.66667 5.5C1.64162 5.5 0 7.14162 0 9.16667V12.8333H44V9.16667C44 7.14162 42.3584 5.5 40.3333 5.5H3.66667Z",
    fill: color
  }), React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M0 17.4167H44V34.8333C44 36.8584 42.3584 38.5 40.3333 38.5H3.66667C1.64162 38.5 0 36.8584 0 34.8333V17.4167ZM31.1667 30.25C30.1541 30.25 29.3333 31.0708 29.3333 32.0833V33C29.3333 34.0125 30.1541 34.8333 31.1667 34.8333H38.5C39.5125 34.8333 40.3333 34.0125 40.3333 33V32.0833C40.3333 31.0708 39.5125 30.25 38.5 30.25H31.1667Z",
    fill: color
  }));
};
var IconBookLight = function IconBookLight(_ref10) {
  var _ref10$size = _ref10.size,
      size = _ref10$size === void 0 ? 20 : _ref10$size,
      _ref10$color = _ref10.color,
      color = _ref10$color === void 0 ? 'currentColor' : _ref10$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 44 44",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M15.5835 9.16691C15.0772 9.16691 14.6668 9.57732 14.6668 10.0836V15.5836C14.6668 16.0898 15.0772 16.5002 15.5835 16.5002H32.0835C32.5898 16.5002 33.0002 16.0898 33.0002 15.5836V10.0836C33.0002 9.57732 32.5898 9.16691 32.0835 9.16691H15.5835Z",
    fill: color
  }), React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M38.5002 0.000244141C40.5252 0.000244141 42.1668 1.64187 42.1668 3.66691V40.3336C42.1668 42.3586 40.5252 44.0002 38.5002 44.0002H5.50016C3.47512 44.0002 1.8335 42.3586 1.8335 40.3336V34.8336H4.5835C5.08976 34.8336 5.50016 34.4232 5.50016 33.9169V32.0836C5.50016 31.5773 5.08976 31.1669 4.5835 31.1669H1.8335V12.8336H4.5835C5.08976 12.8336 5.50016 12.4232 5.50016 11.9169V10.0836C5.50016 9.57732 5.08976 9.16691 4.5835 9.16691H1.8335V3.66691C1.8335 1.64187 3.47512 0.000244141 5.50016 0.000244141H38.5002ZM36.6668 3.66691C37.6794 3.66691 38.5002 4.48772 38.5002 5.50024V38.5002C38.5002 39.5128 37.6794 40.3336 36.6668 40.3336H9.16683V3.66691H36.6668Z",
    fill: color
  }));
};
var IconDoc = function IconDoc(_ref11) {
  var _ref11$size = _ref11.size,
      size = _ref11$size === void 0 ? 20 : _ref11$size,
      _ref11$color = _ref11.color,
      color = _ref11$color === void 0 ? 'currentColor' : _ref11$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 45 45",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M40.9776 0.993317C42.0822 0.993317 42.9776 1.88875 42.9776 2.99332V42.9933C42.9776 44.0979 42.0822 44.9933 40.9776 44.9933H4.64428C3.53971 44.9933 2.64429 44.0979 2.64429 42.9933V2.99332C2.64429 1.88875 3.53972 0.993317 4.64429 0.993317H40.9776ZM38.3757 4.88847C38.928 4.88847 39.3757 5.33618 39.3757 5.88847V40.5551C39.3757 41.1074 38.928 41.5551 38.3757 41.5551H7.24619C6.69391 41.5551 6.24619 41.1074 6.24619 40.5551V5.88847C6.24619 5.33619 6.69391 4.88847 7.24619 4.88847H38.3757ZM32.4417 35.4527L32.5941 35.809C32.821 36.2672 33.3407 36.4352 33.7541 36.1824L35.1997 35.2954C35.5959 35.0611 35.7539 34.5251 35.5644 34.0744C35.443 33.7904 35.3348 33.5582 35.2398 33.3779C33.367 29.8253 30.1831 28.9883 26.7864 32.9522L26.5663 33.2031C25.2406 34.678 23.6797 34.4031 21.8835 32.3783C21.5889 32.0383 21.1127 31.9964 20.7689 32.274L19.4212 33.3815L19.3571 33.4533C19.0454 33.8459 19.0804 34.4437 19.4338 34.7904C23.1192 38.3325 26.357 38.5954 29.147 35.5792L29.3571 35.339C30.9275 33.5845 31.5798 33.8112 32.3916 35.3511C32.4069 35.3801 32.4236 35.414 32.4417 35.4527ZM9.97762 14.3266C9.97762 14.0505 10.2015 13.8266 10.4776 13.8266H35.1443C35.4204 13.8266 35.6443 14.0505 35.6443 14.3266V16.9933C35.6443 17.2695 35.4204 17.4933 35.1443 17.4933H10.4776C10.2015 17.4933 9.97762 17.2695 9.97762 16.9933V14.3266ZM10.4776 21.16C10.2015 21.16 9.97762 21.3838 9.97762 21.66V24.3267C9.97762 24.6028 10.2015 24.8267 10.4776 24.8267H26.5887C26.8649 24.8267 27.0887 24.6028 27.0887 24.3267V21.66C27.0887 21.3838 26.8649 21.16 26.5887 21.16H10.4776Z",
    fill: color
  }));
};
var IconReward = function IconReward(_ref12) {
  var _ref12$size = _ref12.size,
      size = _ref12$size === void 0 ? 20 : _ref12$size,
      _ref12$color = _ref12.color,
      color = _ref12$color === void 0 ? 'currentColor' : _ref12$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 44 44",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M31.4979 9.67846C30.9563 10.1742 30.3699 10.624 29.7388 11.026L40.1652 11.0177C41.2704 11.0168 42.1668 11.9125 42.1668 13.0177V42.0002C42.1668 43.1048 41.2714 44.0002 40.1668 44.0002H3.8335C2.72893 44.0002 1.8335 43.1048 1.8335 42.0002V13.0263C1.8335 11.9177 2.73515 11.0206 3.84368 11.0263L14.2464 11.0793C13.6173 10.679 13.0289 10.2292 12.4893 9.73171C9.23776 6.7979 8.33986 3.01039 10.4553 1.10287C11.2575 0.407053 12.3346 0.0306886 13.4483 0.0563915C15.5475 0.0563915 18.0009 1.13408 19.9963 2.93512C20.7638 3.61625 21.4296 4.38366 21.9773 5.22084C22.5311 4.36347 23.2071 3.57769 23.989 2.88188C25.9945 1.08084 28.4377 0.00131379 30.541 0.00131379C31.6506 -0.0225532 32.7297 0.353811 33.5299 1.04779C35.6392 2.93512 34.7495 6.74466 31.4979 9.67846ZM13.1185 2.28757C12.754 2.26569 12.3936 2.36779 12.1066 2.57198C11.3552 3.26843 11.8643 5.66408 14.3808 7.91753C15.9954 9.32502 17.9012 10.2384 19.3386 10.2384C19.7031 10.2585 20.0634 10.1582 20.3505 9.95219C21.1018 9.27944 20.5826 6.85462 18.0763 4.61029C16.4617 3.20098 14.5559 2.28757 13.1185 2.28757ZM25.149 12.852C25.4251 12.852 25.649 13.0759 25.649 13.352V39.8915C25.649 40.1677 25.4251 40.3915 25.149 40.3915H18.8077C18.5316 40.3915 18.3077 40.1677 18.3077 39.8915V13.352C18.3077 13.0759 18.5316 12.852 18.8077 12.852H25.149ZM30.8382 2.28757C29.3987 2.28757 27.4523 3.2028 25.8804 4.61029C23.3659 6.86191 22.8569 9.25574 23.6082 9.95037C23.8933 10.1582 24.2536 10.2585 24.6201 10.2366C26.0576 10.2366 28.004 9.32502 29.5759 7.91753C32.0904 5.66408 32.6015 3.24656 31.8481 2.57198C31.5631 2.36961 31.2027 2.26569 30.8382 2.28757Z",
    fill: color
  }), React__default.createElement("mask", {
    id: "mask0_2468_2259",
    style: {
      maskType: 'alpha'
    },
    maskUnits: "userSpaceOnUse",
    x: "1",
    y: "0",
    width: "42",
    height: "45"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M31.4979 9.67846C30.9563 10.1742 30.3699 10.624 29.7388 11.026L40.1652 11.0177C41.2704 11.0168 42.1668 11.9125 42.1668 13.0177V42.0002C42.1668 43.1048 41.2714 44.0002 40.1668 44.0002H3.8335C2.72893 44.0002 1.8335 43.1048 1.8335 42.0002V13.0263C1.8335 11.9177 2.73515 11.0206 3.84368 11.0263L14.2464 11.0793C13.6173 10.679 13.0289 10.2292 12.4893 9.73171C9.23776 6.7979 8.33986 3.01039 10.4553 1.10287C11.2575 0.407053 12.3346 0.0306886 13.4483 0.0563915C15.5475 0.0563915 18.0009 1.13408 19.9963 2.93512C20.7638 3.61625 21.4296 4.38366 21.9773 5.22084C22.5311 4.36347 23.2071 3.57769 23.989 2.88188C25.9945 1.08084 28.4377 0.00131379 30.541 0.00131379C31.6506 -0.0225532 32.7297 0.353811 33.5299 1.04779C35.6392 2.93512 34.7495 6.74466 31.4979 9.67846ZM13.1185 2.28757C12.754 2.26569 12.3936 2.36779 12.1066 2.57198C11.3552 3.26843 11.8643 5.66408 14.3808 7.91753C15.9954 9.32502 17.9012 10.2384 19.3386 10.2384C19.7031 10.2585 20.0634 10.1582 20.3505 9.95219C21.1018 9.27944 20.5826 6.85462 18.0763 4.61029C16.4617 3.20098 14.5559 2.28757 13.1185 2.28757ZM25.149 12.852C25.4251 12.852 25.649 13.0759 25.649 13.352V39.8915C25.649 40.1677 25.4251 40.3915 25.149 40.3915H18.8077C18.5316 40.3915 18.3077 40.1677 18.3077 39.8915V13.352C18.3077 13.0759 18.5316 12.852 18.8077 12.852H25.149ZM30.8382 2.28757C29.3987 2.28757 27.4523 3.2028 25.8804 4.61029C23.3659 6.86191 22.8569 9.25574 23.6082 9.95037C23.8933 10.1582 24.2536 10.2585 24.6201 10.2366C26.0576 10.2366 28.004 9.32502 29.5759 7.91753C32.0904 5.66408 32.6015 3.24656 31.8481 2.57198C31.5631 2.36961 31.2027 2.26569 30.8382 2.28757Z",
    fill: "white"
  })), React__default.createElement("g", {
    mask: "url(#mask0_2468_2259)"
  }));
};
var IconEducation = function IconEducation(_ref13) {
  var _ref13$color = _ref13.color,
      color = _ref13$color === void 0 ? 'currentColor' : _ref13$color,
      _ref13$size = _ref13.size,
      size = _ref13$size === void 0 ? 26 : _ref13$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 45 44",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M43.2498 13.0357C43.6368 12.8539 43.9481 12.5426 44.1299 12.1556C44.5603 11.2392 44.1663 10.1473 43.2498 9.71683L22.9315 0.173912C22.4378 -0.0579708 21.8664 -0.0579708 21.3727 0.173912L1.05439 9.71683C0.667398 9.89859 0.356103 10.2099 0.174346 10.5969C-0.256093 11.5133 0.137916 12.6052 1.05439 13.0357L21.3727 22.5786C21.8664 22.8105 22.4378 22.8105 22.9315 22.5786L38.614 15.213V36.4132C38.0616 37.1239 37.5091 37.8346 36.9567 38.5453C36.3142 39.3718 35.6717 40.1982 35.0294 41.0248C34.8486 41.2569 34.9963 41.5874 35.1834 41.7285C36.3462 42.6056 37.8781 43.0396 39.5298 43.0417C41.2032 43.0441 42.617 42.5928 43.8761 41.7285C44.0712 41.5946 44.2109 41.2569 44.0302 41.0248L40.4473 36.4153V14.3519L43.2498 13.0357ZM20.2037 26.901L9.3081 21.7837V31.8745C9.3081 32.1786 9.44503 32.465 9.68393 32.6531C10.5746 33.3543 12.8156 35.0953 13.839 35.6774C14.5367 36.0743 16.9011 37.2027 18.9308 38.1578C20.9791 39.1216 23.3436 39.1638 25.4019 38.2215C26.8367 37.5646 28.4299 36.803 29.4681 36.2101C31.2884 35.1707 33.662 33.3602 34.5708 32.6517C34.8106 32.4649 34.9473 32.1786 34.9473 31.8747V21.8067L24.1006 26.901C22.8663 27.4808 21.4379 27.4808 20.2037 26.901Z",
    fill: color
  }));
};
var IconUser = function IconUser(_ref14) {
  var _ref14$color = _ref14.color,
      color = _ref14$color === void 0 ? 'currentColor' : _ref14$color,
      _ref14$size = _ref14.size,
      size = _ref14$size === void 0 ? 26 : _ref14$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 45 45",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M20.6872 26.8705L21.0199 26.9261L21.6359 27.0008L22.0675 27.0375C22.3133 27.0527 22.5614 27.0605 22.8114 27.0605L23.4109 27.0455L24.0045 26.999L24.3199 26.9623L24.7231 26.9072C24.9051 26.8776 25.0857 26.844 25.2647 26.8063C25.9732 26.6326 26.6211 26.4339 27.2077 26.2093L27.7055 26.0044V26.0044L28.1716 25.7792L28.5385 25.5834V25.5834L28.943 25.3483L29.1269 25.2338C31.4128 24.1413 33.3716 22.4744 34.8163 20.4175L35.1405 19.936L35.1961 19.846C36.0486 18.4995 37.2856 17.7271 38.9071 17.5288L39.2872 17.4932L39.656 17.5039C40.8174 17.5765 41.9602 18.0158 42.8961 18.8218L43.2001 19.1041C45.2621 21.166 45.3446 24.4579 43.4476 26.6183L43.2001 26.8823L37.4109 32.6715V32.6715C36.4526 33.6298 36.3962 35.1926 37.2417 37.3601L37.4109 37.7736L37.3532 37.723L37.4004 37.921V37.921L37.4283 38.0718L37.4633 38.3484L37.4766 38.5827L37.4759 38.7887L37.4633 38.9987V38.9987L37.4118 39.368V39.368L37.3334 39.6964V39.6964L37.2433 39.9702V39.9702L37.1482 40.2016V40.2016L36.9821 40.5273V40.5273L36.8331 40.7665L36.742 40.8961V40.8961L36.5621 41.1216V41.1216L36.3842 41.3144L36.2012 41.488V41.488L36.0364 41.6266C35.9604 41.6869 35.8817 41.7448 35.8004 41.8001L35.6687 41.8854L35.5069 41.9773L35.2802 42.0989C31.3908 43.9733 27.2856 44.9372 23.1742 44.9908H22.4479C18.3365 44.9372 14.2313 43.9733 10.3411 42.0989L10.1134 41.9773L9.95339 41.8854L9.76469 41.7606V41.7606L9.48893 41.5467V41.5467L9.35457 41.4278V41.4278L9.15134 41.224V41.224L8.96446 41.0062L8.78928 40.7674V40.7674L8.69061 40.6135V40.6135L8.58926 40.4358V40.4358L8.47386 40.2016V40.2016L8.37877 39.9702V39.9702L8.2738 39.6433V39.6433L8.21033 39.3685V39.3685L8.17369 39.1378V39.1378L8.14614 38.7878V38.7878L8.14803 38.5094V38.5094L8.16927 38.2458L8.19363 38.0717V38.0717L8.26884 37.723V37.723L8.21122 37.7736C9.22936 35.3903 9.22936 33.6897 8.21122 32.6715L2.42195 26.8823C0.274064 24.7344 0.274064 21.252 2.42195 19.1041C3.40811 18.1179 4.67559 17.5845 5.96607 17.5039L6.3367 17.4932L6.71501 17.5288C8.33651 17.7271 9.5735 18.4995 10.426 19.846L10.4815 19.936C11.951 22.2146 14.0334 24.0573 16.4952 25.2338L16.7147 25.3695V25.3695L17.1564 25.6236L17.6025 25.8555L17.8041 25.9524V25.9524L17.986 26.0359C18.6808 26.3331 19.4718 26.5891 20.3592 26.8035L20.6872 26.8705ZM22.8118 0.993164C28.886 0.993164 33.811 5.9182 33.811 11.9924C33.811 18.0681 28.886 22.9932 22.8118 22.9932C16.7361 22.9932 11.811 18.0681 11.811 11.9924C11.811 5.9182 16.7361 0.993164 22.8118 0.993164Z",
    fill: color
  }));
};
var IconTiney = function IconTiney(_ref15) {
  var _ref15$color = _ref15.color,
      color = _ref15$color === void 0 ? 'currentColor' : _ref15$color,
      _ref15$size = _ref15.size,
      size = _ref15$size === void 0 ? 26 : _ref15$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 1.04,
    viewBox: "0 0 26 27",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    d: "M1.458 7.039c-2.571 5.12-1.178 10.98 2.746 14.656a1.349 1.349 0 0 0-.038.133c-.163.76-.887 1.717-1.617 2.524-.592.654-.097 1.695.806 1.694 3.257-.001 8.942-.144 12.41-1.017 4.176-.817 7.795-3.66 9.32-7.849 2.51-6.898-1.666-14.649-9.252-16.526a12.959 12.959 0 0 0-3.117-.38c-4.706 0-9.144 2.56-11.258 6.765zm9.933 10.786c-2.19-1.508-2.524-4.863-1.665-5.403.442-.276 1.057.283 1.94.843.748.474 1.69.948 2.883.913 2.301-.066 3.416-1.964 4.182-1.474.724.464.496 2.64-.624 4.026-.916 1.135-2.528 1.862-4.126 1.862-.911 0-1.818-.236-2.59-.767z",
    id: "chat-hole"
  })), React__default.createElement("use", {
    fill: color,
    xlinkHref: "#chat-hole",
    fillRule: "evenodd"
  }));
};
var IconCheck = function IconCheck(_ref16) {
  var _ref16$color = _ref16.color,
      color = _ref16$color === void 0 ? 'currentColor' : _ref16$color,
      _ref16$size = _ref16.size,
      size = _ref16$size === void 0 ? 20 : _ref16$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 20 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("circle", {
    fill: color,
    cx: "10",
    cy: "10",
    r: "10"
  }), React__default.createElement("path", {
    d: "M7.94 14.496l-2.78-2.11a.397.397 0 0 1-.07-.57l.785-.963a.432.432 0 0 1 .592-.068l1.47 1.117 5.329-6.335a.432.432 0 0 1 .593-.06l.985.772c.18.141.209.397.062.572l-6.378 7.582a.432.432 0 0 1-.588.063",
    fill: "#FFF"
  })));
};
var IconCheckSolo = function IconCheckSolo(_ref17) {
  var _ref17$color = _ref17.color,
      color = _ref17$color === void 0 ? 'currentColor' : _ref17$color,
      _ref17$size = _ref17.size,
      size = _ref17$size === void 0 ? 24 : _ref17$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    d: "M7.056 21.79l-6.67-5.064a.952.952 0 01-.17-1.37L2.1 13.049a1.038 1.038 0 011.422-.163l3.527 2.679L19.838.362c.352-.42.988-.484 1.423-.143l2.364 1.85c.434.34.501.954.15 1.372L8.466 21.64a1.036 1.036 0 01-1.411.151",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconChevronDown = function IconChevronDown(_ref18) {
  var _ref18$color = _ref18.color,
      color = _ref18$color === void 0 ? 'currentColor' : _ref18$color,
      _ref18$size = _ref18.size,
      size = _ref18$size === void 0 ? 24 : _ref18$size;
  var height = size * 0.58;
  return React__default.createElement("svg", {
    width: size,
    height: height,
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M.333 2.816l10.679 10.64a1.4 1.4 0 001.976 0l10.679-10.64a1 1 0 000-1.417L22.608.344a1 1 0 00-1.412 0L12 9.508 2.804.344a1 1 0 00-1.412 0L.333 1.4a1 1 0 000 1.417z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconClock = function IconClock(_ref19) {
  var _ref19$size = _ref19.size,
      size = _ref19$size === void 0 ? 24 : _ref19$size,
      _ref19$color = _ref19.color,
      color = _ref19$color === void 0 ? 'currentColor' : _ref19$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    d: "M12 24C5.373 24 0 18.627 0 12S5.373 0 12 0s12 5.373 12 12-5.373 12-12 12zm0-2c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-10h3.5a1.5 1.5 0 110 3h-4c-.094 0-.186-.009-.276-.025A1.003 1.003 0 0110 14V7a1 1 0 012 0v5z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconClose = function IconClose(_ref20) {
  var _ref20$color = _ref20.color,
      color = _ref20$color === void 0 ? 'currentColor' : _ref20$color,
      _ref20$size = _ref20.size,
      size = _ref20$size === void 0 ? 22 : _ref20$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 20 20",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "guardian-enquiry-flow",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    id: "make-enquiry---min-chars",
    transform: "translate(-976.000000, -203.000000)",
    fill: color
  }, React__default.createElement("g", {
    id: "Icon-/-Add",
    transform: "translate(974.000000, 201.000000)"
  }, React__default.createElement("path", {
    d: "M21.6997198,18.0746767 L15.624757,11.9994803 L21.6997198,5.92428382 C22.0996583,5.52535549 22.0996583,4.87722514 21.6997198,4.47829682 L19.5205679,2.29906109 C19.1216549,1.90013276 18.4735495,1.90013276 18.0746365,2.29906109 L11.9996737,8.37425754 L5.92471087,2.29906109 C5.52579788,1.90013276 4.87769246,1.90013276 4.47877947,2.29906109 L2.29962755,4.47829682 C1.89968908,4.87722514 1.89968908,5.52535549 2.29962755,5.92428382 L8.37459037,11.9994803 L2.29962755,18.0746767 C1.89968908,18.4746306 1.89968908,19.1217354 2.29962755,19.5206637 L4.47877947,21.6998995 C4.87769246,22.0998533 5.52579788,22.0998533 5.92471087,21.6998995 L11.9996737,15.624703 L18.0746365,21.6998995 C18.4735495,22.0998533 19.1216549,22.0998533 19.5205679,21.6998995 L21.6997198,19.5206637 C22.0996583,19.1217354 22.0996583,18.4746306 21.6997198,18.0746767"
  })))));
};
var IconCurrencyPound = function IconCurrencyPound(_ref21) {
  var _ref21$color = _ref21.color,
      color = _ref21$color === void 0 ? 'currentColor' : _ref21$color,
      _ref21$size = _ref21.size,
      size = _ref21$size === void 0 ? 15 : _ref21$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 1.46,
    viewBox: "0 0 32 50",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M1.076 50v-7.507c3.085-2.62 5.668-4.958 5.668-9.065 0-.78-.071-1.487-.215-2.196H0v-6.657h4.52c-.86-2.337-1.578-5.1-1.578-9.065C2.942 5.949 9.542 0 19.516 0 23.82 0 27.839 1.204 32 3.329l-3.013 8.002c-3.086-1.487-6.17-2.407-9.471-2.407-4.305 0-6.53 2.407-6.53 6.586 0 3.329.933 6.09 2.081 9.065h11.48v6.657h-9.901c.071.709.143 1.417.143 2.125 0 3.329-1.435 6.232-3.946 8.286h18.798V50H1.076z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconFacebook = function IconFacebook(_ref22) {
  var _ref22$color = _ref22.color,
      color = _ref22$color === void 0 ? 'currentColor' : _ref22$color,
      _ref22$size = _ref22.size,
      size = _ref22$size === void 0 ? 32 : _ref22$size;
  return React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    fill: color,
    d: "M19 6h5V0h-5c-3.86 0-7 3.14-7 7v3H8v6h4v16h6V16h5l1-6h-6V7c0-.542.458-1 1-1z"
  }));
};
var IconGrid = function IconGrid(_ref23) {
  var _ref23$size = _ref23.size,
      size = _ref23$size === void 0 ? 24 : _ref23$size,
      _ref23$color = _ref23.color,
      color = _ref23$color === void 0 ? 'currentColor' : _ref23$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 20 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fill: color,
    d: "M8 4H5a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V5a1 1 0 00-1-1zm7 0h-3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V5a1 1 0 00-1-1zm-7 7H5a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1v-3a1 1 0 00-1-1zm7 0h-3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1v-3a1 1 0 00-1-1z"
  }));
};
var IconHamburger = function IconHamburger(_ref24) {
  var _ref24$size = _ref24.size,
      size = _ref24$size === void 0 ? 26 : _ref24$size,
      _ref24$color = _ref24.color,
      color = _ref24$color === void 0 ? 'currentColor' : _ref24$color;
  return React__default.createElement("svg", {
    width: size,
    height: size * 0.78,
    viewBox: "0 0 26 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: color,
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M13.993 11.999V12h-1.986v-.001L.945 11.992c-.523 0-.945-.36-.945-.802L.002 8.8c0-.442.424-.8.947-.8L13 8.007 25.05 8c.524 0 .947.358.948.8L26 11.19c0 .443-.422.801-.945.802l-11.062.007zM13.993 19.999V20h-1.986v-.001L.945 19.992c-.523 0-.945-.36-.945-.802l.002-2.39c0-.442.424-.8.947-.8L13 16.007 25.05 16c.524 0 .947.358.948.8L26 19.19c0 .443-.422.801-.945.802l-11.062.007zM13.993 3.999V4h-1.986v-.001L.945 3.992C.422 3.992 0 3.632 0 3.19L.002.8c0-.442.424-.8.947-.8L13 .007 25.05 0c.524 0 .947.358.948.8L26 3.19c0 .443-.422.801-.945.802l-11.062.007z"
  })));
};
var IconHeart = function IconHeart(_ref25) {
  var _ref25$size = _ref25.size,
      size = _ref25$size === void 0 ? 26 : _ref25$size,
      _ref25$color = _ref25.color,
      color = _ref25$color === void 0 ? 'currentColor' : _ref25$color;
  return React__default.createElement("svg", {
    width: size,
    height: size * 0.92,
    viewBox: "0 0 50 46",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    d: "M35.712.46a14.29 14.29 0 0110.12 4.2c5.558 5.657 5.558 14.724 0 20.381L25.003 45.833 4.176 25.041c-5.568-5.653-5.568-14.728 0-20.38a14.29 14.29 0 0120.238 0l.59.606.59-.607A14.29 14.29 0 0135.711.46z",
    id: "ah"
  })), React__default.createElement("g", {
    transform: "translate(0 .083)",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("mask", {
    id: "bh",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#ah"
  })), React__default.createElement("use", {
    fill: color,
    xlinkHref: "#ah"
  }), React__default.createElement("g", {
    mask: "url(#bh)",
    fill: color
  }, React__default.createElement("path", {
    d: "M0-4.167h50v50H0z"
  }))));
};
var IconHome = function IconHome(_ref26) {
  var _ref26$size = _ref26.size,
      size = _ref26$size === void 0 ? 24 : _ref26$size,
      _ref26$color = _ref26.color,
      color = _ref26$color === void 0 ? 'currentColor' : _ref26$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    d: "M12.425.193a.68.68 0 00-.948 0L.197 11.246c-.422.413-.123 1.12.475 1.12h1.373v10.977c0 .363.3.657.67.657h6.032c.37 0 .67-.294.67-.657v-5.848c0-1.31.975-2.489 2.307-2.602 1.501-.128 2.76 1.029 2.76 2.473v5.977c0 .363.3.657.67.657h5.927c.37 0 .67-.294.67-.657V12.367h1.479c.597 0 .896-.708.473-1.121L12.425.193z",
    id: "aho"
  })), React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("mask", {
    id: "bho",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#aho"
  })), React__default.createElement("use", {
    fill: color,
    xlinkHref: "#aho"
  }), React__default.createElement("g", {
    mask: "url(#bho)",
    fill: color
  }, React__default.createElement("path", {
    d: "M0 0h24v24H0z"
  }))));
};
var IconInstagram = function IconInstagram(_ref27) {
  var _ref27$color = _ref27.color,
      color = _ref27$color === void 0 ? 'currentColor' : _ref27$color,
      _ref27$size = _ref27.size,
      size = _ref27$size === void 0 ? 32 : _ref27$size;
  return React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    fill: color,
    d: "M16 0c-4.347 0-4.889.02-6.596.096C7.7.176 6.54.444 5.52.84c-1.052.408-1.945.956-2.835 1.845S1.246 4.466.84 5.52C.444 6.54.175 7.7.096 9.404.016 11.111 0 11.653 0 16s.02 4.889.096 6.596c.08 1.703.348 2.864.744 3.884a7.854 7.854 0 0 0 1.845 2.835A7.807 7.807 0 0 0 5.52 31.16c1.021.395 2.181.665 3.884.744 1.707.08 2.249.096 6.596.096s4.889-.02 6.596-.096c1.703-.08 2.864-.349 3.884-.744a7.867 7.867 0 0 0 2.835-1.845 7.799 7.799 0 0 0 1.845-2.835c.395-1.02.665-2.181.744-3.884.08-1.707.096-2.249.096-6.596s-.02-4.889-.096-6.596c-.08-1.703-.349-2.865-.744-3.884a7.859 7.859 0 0 0-1.845-2.835A7.786 7.786 0 0 0 26.48.84C25.46.444 24.299.175 22.596.096 20.889.016 20.347 0 16 0zm0 2.88c4.271 0 4.78.021 6.467.095 1.56.073 2.407.332 2.969.553.749.289 1.28.636 1.843 1.195a4.95 4.95 0 0 1 1.195 1.841c.219.563.48 1.409.551 2.969.076 1.688.093 2.195.093 6.467s-.02 4.78-.099 6.467c-.081 1.56-.341 2.407-.561 2.969-.299.749-.639 1.28-1.199 1.843a5.002 5.002 0 0 1-1.84 1.195c-.56.219-1.42.48-2.98.551-1.699.076-2.199.093-6.479.093-4.281 0-4.781-.02-6.479-.099-1.561-.081-2.421-.341-2.981-.561-.759-.299-1.28-.639-1.839-1.199a4.867 4.867 0 0 1-1.2-1.84c-.22-.56-.479-1.42-.56-2.98-.06-1.68-.081-2.199-.081-6.459 0-4.261.021-4.781.081-6.481.081-1.56.34-2.419.56-2.979.28-.76.639-1.28 1.2-1.841.559-.559 1.08-.919 1.839-1.197.56-.221 1.401-.481 2.961-.561 1.7-.06 2.2-.08 6.479-.08l.06.04zm0 4.904A8.215 8.215 0 0 0 7.784 16c0 4.54 3.68 8.216 8.216 8.216 4.54 0 8.216-3.68 8.216-8.216 0-4.54-3.68-8.216-8.216-8.216zm0 13.549c-2.947 0-5.333-2.387-5.333-5.333s2.387-5.333 5.333-5.333 5.333 2.387 5.333 5.333-2.387 5.333-5.333 5.333zM26.461 7.46a1.921 1.921 0 0 1-3.84 0 1.92 1.92 0 0 1 3.84 0z"
  }));
};
var IconLinkedIn = function IconLinkedIn(_ref28) {
  var _ref28$color = _ref28.color,
      color = _ref28$color === void 0 ? 'currentColor' : _ref28$color,
      _ref28$size = _ref28.size,
      size = _ref28$size === void 0 ? 32 : _ref28$size;
  return React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 16 16",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    fill: color,
    d: "M6 6h2.767v1.418h.04C9.192 6.727 10.134 6 11.539 6 14.46 6 15 7.818 15 10.183V15h-2.885v-4.27c0-1.018-.021-2.329-1.5-2.329-1.502 0-1.732 1.109-1.732 2.255V15H6V6zM1 6h3v9H1V6zM4 3.5a1.5 1.5 0 1 1-3.001-.001A1.5 1.5 0 0 1 4 3.5z"
  }));
};
var IconLocationPin = function IconLocationPin(_ref29) {
  var _ref29$size = _ref29.size,
      size = _ref29$size === void 0 ? 24 : _ref29$size,
      _ref29$color = _ref29.color,
      color = _ref29$color === void 0 ? 'currentColor' : _ref29$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 26 26",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M13.47 1.206c4.65 0 8.42 3.77 8.42 8.42 0 5.442-7.016 15.168-8.42 15.168-1.403 0-8.419-9.726-8.419-15.169a8.42 8.42 0 018.42-8.42zm0 12.395a4 4 0 100-8 4 4 0 000 8z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var IconMail = function IconMail(_ref30) {
  var _ref30$color = _ref30.color,
      color = _ref30$color === void 0 ? 'currentColor' : _ref30$color,
      _ref30$size = _ref30.size,
      size = _ref30$size === void 0 ? 26 : _ref30$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 0.85,
    viewBox: "0 0 26 22",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: color,
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M23.077 1.888c.393-.354.2-.98-.335-1.074A55.223 55.223 0 0 0 13.229 0a55.184 55.184 0 0 0-9.401.814c-.535.093-.728.72-.335 1.074l8.412 7.59a2.083 2.083 0 0 0 2.76 0l8.412-7.59z"
  }), React__default.createElement("path", {
    d: "M11.665 13.58L3.273 6.478C2.019 5.418-.057 6.24 0 7.774l.437 11.61c.03.786.658 1.459 1.53 1.618a61.428 61.428 0 0 0 22.063 0c.872-.159 1.501-.83 1.531-1.62L26 7.775c.057-1.534-2.018-2.356-3.272-1.296l-8.392 7.102c-.747.631-1.923.631-2.67 0"
  })));
};
var IconMoney = function IconMoney(_ref31) {
  var _ref31$color = _ref31.color,
      color = _ref31$color === void 0 ? 'currentColor' : _ref31$color,
      _ref31$size = _ref31.size,
      size = _ref31$size === void 0 ? 24 : _ref31$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M12.603.015c6.619.333 11.715 5.969 11.382 12.588-.333 6.619-5.969 11.715-12.588 11.382C4.778 23.652-.317 18.016.015 11.397.348 4.778 5.985-.318 12.603.015zm.127 5.14c-2.54.005-4.219 1.544-4.214 4.01a6.494 6.494 0 00.407 2.34l-1.151.002.003 1.718 1.664-.004c.037.183.055.366.056.567.002 1.06-.655 1.664-1.44 2.342l.005 1.937 7.786-.016-.005-2.156-4.788.01a2.724 2.724 0 001-2.14c0-.184-.019-.366-.037-.549l2.522-.005-.004-1.718-2.924.006c-.294-.767-.533-1.48-.535-2.338-.002-1.079.563-1.701 1.66-1.704.84-.001 1.627.234 2.413.617l.764-2.067c-1.062-.546-2.086-.855-3.182-.852z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconObservation = function IconObservation(_ref32) {
  var _ref32$color = _ref32.color,
      color = _ref32$color === void 0 ? 'currentColor' : _ref32$color,
      _ref32$size = _ref32.size,
      size = _ref32$size === void 0 ? 24 : _ref32$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M2.283 2.282c3.738-3.043 15.696-3.043 19.434 0 3.434 2.794 2.754 14.436-.441 17.491-3.32 3.174-15.232 3.174-18.552 0-3.195-3.055-3.875-14.697-.441-17.491zM12 6.702a7.438 7.438 0 00-6.514 4.174l-.105.2.105.202.126.247A7.438 7.438 0 0012 15.452a7.437 7.437 0 006.514-4.174l.105-.201-.105-.201-.126-.247A7.438 7.438 0 0012 6.702zm0 .875a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm-3.815 1.36a4.375 4.375 0 000 4.28 7.206 7.206 0 01-1.811-2.14 7.206 7.206 0 011.811-2.14zm7.63 0a7.206 7.206 0 011.811 2.14 7.206 7.206 0 01-1.811 2.14 4.375 4.375 0 000-4.28zM12 9.328a1.75 1.75 0 100 3.5 1.75 1.75 0 000-3.5zm0 .875a.875.875 0 110 1.75.875.875 0 010-1.75z",
    fill: color
  }));
};
var IconShow = function IconShow(_ref33) {
  var _ref33$color = _ref33.color,
      color = _ref33$color === void 0 ? 'currentColor' : _ref33$color,
      _ref33$size = _ref33.size,
      size = _ref33$size === void 0 ? 24 : _ref33$size;
  return React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size * 18 / 24,
    viewBox: "0 0 24 18",
    version: "1.1"
  }, React__default.createElement("g", {
    id: "guardian-enquiry-flow",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    id: "password-set",
    transform: "translate(-857.000000, -493.000000)",
    fill: "#474676"
  }, React__default.createElement("g", {
    id: "Group-3",
    transform: "translate(420.000000, 134.000000)"
  }, React__default.createElement("g", {
    id: "Input/-Input-field-/-Populated",
    transform: "translate(125.000000, 308.000000)"
  }, React__default.createElement("g", {
    id: "Icon-/-Eye",
    transform: "translate(312.000000, 48.000000)"
  }, React__default.createElement("path", {
    fill: color,
    d: "M12,3 C16.3473399,3 20.2954724,5.82258629 23.8443975,11.4677589 C24.0261983,11.7569441 24.0463986,12.1159943 23.9049984,12.4207053 L23.8443983,12.5321822 L23.6018331,12.9127248 C20.1158251,18.3042416 16.2485474,21 12,21 C7.6526601,21 3.70452759,18.1774137 0.155602456,12.5322411 C-0.0261983288,12.2430559 -0.0463986428,11.8840057 0.0950015998,11.5792947 L0.155601717,11.4678178 L0.398166938,11.0872752 C3.88417491,5.69575841 7.7514526,3 12,3 Z M12,7 C9.19882251,7 6.92801948,9.23857625 6.92801948,12 C6.92801948,14.7614237 9.19882251,17 12,17 C14.8011775,17 17.0719805,14.7614237 17.0719805,12 C17.0719805,9.23857625 14.8011775,7 12,7 Z M12,10 C13.120471,10 14.0287922,10.8954305 14.0287922,12 C14.0287922,13.1045695 13.120471,14 12,14 C10.879529,14 9.97120779,13.1045695 9.97120779,12 C9.97120779,10.8954305 10.879529,10 12,10 Z"
  })))))));
};
var IconSnapshot = function IconSnapshot(_ref34) {
  var _ref34$color = _ref34.color,
      color = _ref34$color === void 0 ? 'currentColor' : _ref34$color,
      _ref34$size = _ref34.size,
      size = _ref34$size === void 0 ? 24 : _ref34$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    fill: "none",
    viewBox: "0 0 48 48",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M18.672.494c-.21-.659-1.135-.659-1.344 0l-3.879 12.194a.708.708 0 0 1-.478.466L.512 16.738c-.675.194-.685 1.152-.016 1.362l12.451 3.895a.71.71 0 0 1 .479.518l3.886 16.936c.169.735 1.207.735 1.376 0l3.887-16.936a.71.71 0 0 1 .478-.518l12.45-3.895c.67-.21.66-1.168-.015-1.362l-12.459-3.584a.708.708 0 0 1-.478-.466L18.672.493Zm20.664 27.753a.352.352 0 0 0-.672 0l-1.94 6.097a.354.354 0 0 1-.238.233l-6.23 1.792c-.337.097-.343.576-.008.681l6.226 1.947c.12.038.21.136.239.26l1.943 8.468c.084.367.604.367.688 0l1.943-8.468a.354.354 0 0 1 .24-.26l6.225-1.947c.335-.105.33-.584-.008-.681l-6.23-1.792a.354.354 0 0 1-.239-.233l-1.939-6.097Z",
    fill: color
  }));
};
var IconNappy = function IconNappy(_ref35) {
  var _ref35$size = _ref35.size,
      size = _ref35$size === void 0 ? 24 : _ref35$size,
      _ref35$color = _ref35.color,
      color = _ref35$color === void 0 ? 'currentColor' : _ref35$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M22.05 2.01L24 2l1.95.01 1.872.029 1.793.048 1.714.068 1.636.088 1.557.107c6.07.466 10.255 1.397 12.552 2.796.577.35.926.958.926 1.612V10h-9a1 1 0 00-.984.82L38 11v2a1 1 0 00.82.984L39 14h9v11.113l-.007.498c-10.608 3.712-16.172 8.593-19.27 18.103l-.266.85A2 2 0 0126.538 46H21.46a2 2 0 01-1.92-1.441l-.056-.189C16.41 34.318 11.02 29.28.003 25.497L0 25.113V14h9a1 1 0 00.984-.82L10 13v-2a1 1 0 00-.82-.984L9 10H0V6.758c0-.654.35-1.262.926-1.613 2.297-1.398 6.481-2.33 12.552-2.795l1.558-.107 1.635-.088.848-.036 2.204-.07 2.327-.04zM.742 28.975l.173.069c7.264 2.94 12.166 7.518 14.788 13.793l.263.655c.112.289-.061.603-.386.702a.694.694 0 01-.27.026l-.18-.04C7.918 41.938 2.3 36.677.193 30.101l-.177-.584c-.071-.25.1-.506.382-.57a.588.588 0 01.344.027zm46.744-.04l.116.014c.242.054.402.25.398.462l-.016.107-.177.584c-2.043 6.377-7.387 11.517-14.286 13.866l-.651.211-.18.041a.694.694 0 01-.27-.026c-.278-.085-.445-.328-.417-.577l.03-.125.264-.655c2.528-6.051 7.177-10.524 14.019-13.472l.77-.321.172-.07.112-.03.116-.01z",
    fill: color
  }));
};
var IconSleep = function IconSleep(_ref36) {
  var _ref36$size = _ref36.size,
      size = _ref36$size === void 0 ? 24 : _ref36$size,
      _ref36$color = _ref36.color,
      color = _ref36$color === void 0 ? 'currentColor' : _ref36$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M48 7.75C48 7.88807 47.8881 8 47.75 8H41.25C41.1119 8 41 7.88807 41 7.75V6.42193C41 6.36102 41.0222 6.30221 41.0625 6.25654L45.0089 1.78304H41.3126C41.1746 1.78304 41.0626 1.67111 41.0626 1.53304V0.25C41.0626 0.111929 41.1746 0 41.3126 0H47.5934C47.7315 0 47.8434 0.111929 47.8434 0.25V1.51483C47.8434 1.57581 47.8211 1.63469 47.7807 1.68038L43.7718 6.21696H47.75C47.8881 6.21696 48 6.32889 48 6.46696V7.75ZM16.3289 13.1413C16.3289 25.5984 26.3811 35.6969 38.7811 35.6969C39.3449 35.6969 39.9039 35.676 40.4573 35.635C40.6941 35.6175 40.9484 35.5905 41.2202 35.5541L41.6411 35.4925C42.7546 35.3162 43.7996 36.08 43.9751 37.1986C44.061 37.7458 43.9228 38.3046 43.592 38.7479C43.253 39.2021 42.9365 39.5921 42.6426 39.9177C38.1612 44.8822 31.6897 48 24.4933 48C10.966 48 0 36.9834 0 23.3938C0 12.898 6.54143 3.93697 15.7521 0.400904L16.4656 0.14026L16.6062 0.0932589C17.5893 -0.228934 18.6463 0.310525 18.967 1.29817C19.0969 1.69824 19.0888 2.12802 18.9483 2.51946L18.8482 2.74918C17.2386 5.85978 16.3289 9.3938 16.3289 13.1413ZM38.3063 24C38.5824 24 38.8063 23.7761 38.8063 23.5V21.8254C38.8063 21.5493 38.5824 21.3254 38.3063 21.3254H32.4157L38.443 14.5564C38.5245 14.4648 38.5696 14.3465 38.5696 14.2239V12.5C38.5696 12.2239 38.3457 12 38.0696 12H28.8211C28.5449 12 28.3211 12.2239 28.3211 12.5V14.1746C28.3211 14.4507 28.5449 14.6746 28.8211 14.6746H34.2856L28.3527 21.349C28.2713 21.4406 28.2264 21.5588 28.2264 21.6812V23.5C28.2264 23.7761 28.4503 24 28.7264 24H38.3063Z",
    fill: color
  }));
};
var IconPencil = function IconPencil(_ref37) {
  var _ref37$size = _ref37.size,
      size = _ref37$size === void 0 ? 24 : _ref37$size,
      _ref37$color = _ref37.color,
      color = _ref37$color === void 0 ? 'currentColor' : _ref37$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 30 30",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "guardian-enquiry-flow",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    id: "account-photo-added",
    transform: "translate(-1041.000000, -183.000000)"
  }, React__default.createElement("g", {
    id: "Group-2",
    transform: "translate(915.000000, 93.000000)"
  }, React__default.createElement("g", {
    id: "Buttons-/-[6]-Round-/-Small-/-Active",
    transform: "translate(126.000000, 90.000000)"
  }, React__default.createElement("path", {
    d: "M15,0 C23.2842712,-1.52179594e-15 30,6.71572875 30,15 C30,23.2842712 23.2842712,30 15,30 C6.71572875,30 1.01453063e-15,23.2842712 0,15 C-1.01453063e-15,6.71572875 6.71572875,1.52179594e-15 15,0 Z",
    id: "Container",
    fill: color
  }), React__default.createElement("g", {
    id: "Icon",
    transform: "translate(8.000000, 8.000000)",
    fill: "#FFFFFF"
  }, React__default.createElement("path", {
    d: "M8.70388198,2.33341082 L11.5512544,5.19156262 L4.34376856,12.4264013 L1.49801052,9.56824953 L8.70388198,2.33341082 Z M13.7118593,1.64409013 L12.4420408,0.369452562 C11.9512997,-0.123150854 11.1544377,-0.123150854 10.6620294,0.369452562 L9.44567026,1.59042789 L12.2930427,4.44860626 L13.7118593,3.02440512 C14.0924793,2.64231404 14.0924793,2.02615465 13.7118593,1.64409013 Z M0.00792356555,13.585612 C-0.04389496,13.8197068 0.166660806,14.0294677 0.399897101,13.972538 L3.57281582,13.2003065 L0.727057781,10.3421546 L0.00792356555,13.585612 Z",
    id: "Shape"
  })))))));
};
var IconTips = function IconTips(_ref38) {
  var _ref38$size = _ref38.size,
      size = _ref38$size === void 0 ? 24 : _ref38$size,
      _ref38$color = _ref38.color,
      color = _ref38$color === void 0 ? 'currentColor' : _ref38$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 44 44",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M32.3286 4.40691C38.0767 10.2458 38.1179 19.6976 32.421 25.4845C32.3885 25.5175 32.3544 25.5517 32.3189 25.5868C30.5938 27.5741 28.6214 31.631 28.1674 34.0517C28.0853 34.9207 28.0244 35.5548 27.9975 35.6384C27.8001 36.2518 27.2369 36.6667 26.6017 36.6667H17.5758C16.9073 36.6667 16.229 36.2956 16.0605 35.6384C16.0475 35.5876 16.0117 35.2324 15.9581 34.6994L15.9581 34.6994L15.9149 34.2718C15.5484 31.808 13.5499 27.4955 11.8782 25.5945C11.8012 25.5191 11.7319 25.4505 11.672 25.3896C5.92455 19.5514 5.88196 10.0976 11.5776 4.31201C17.2741 -1.47441 26.5809 -1.43149 32.3286 4.40691ZM16.5002 40.8333C16.5002 40.5572 16.724 40.3333 17.0002 40.3333H27.0002C27.2763 40.3333 27.5002 40.5572 27.5002 40.8333V43C27.5002 43.5523 27.0524 44 26.5002 44H17.5002C16.9479 44 16.5002 43.5523 16.5002 43V40.8333ZM19.4654 11C20.3577 11 21.4291 11.9285 21.6564 12.7852V12.8125C21.6564 12.9506 21.7683 13.0625 21.9064 13.0625H22.0939C22.232 13.0625 22.3439 12.9506 22.3439 12.8125V12.7852C22.5761 11.6948 23.6146 11 24.5433 11C25.3529 11 25.9425 11.3298 26.5311 11.9285C27.1088 12.5154 27.4514 13.2981 27.4948 14.1327C27.5493 15.1853 27.1765 15.9161 26.8449 16.4341C26.4145 17.1097 25.336 18.1415 24.5433 18.8195C23.2423 19.934 22.5161 20.4101 22.2023 20.5934C22.0835 20.6628 21.9425 20.6631 21.8234 20.5942C21.5075 20.4113 20.775 19.9348 19.4654 18.8162C18.6669 18.1359 17.5828 17.1036 17.1557 16.4346C16.664 15.6656 16.4632 14.9555 16.5057 14.1332C16.5492 13.2995 16.8913 12.5163 17.469 11.9285C18.0581 11.3298 18.6563 11 19.4654 11Z",
    fill: color
  }));
};
var IconSuccess = function IconSuccess(_ref39) {
  var _ref39$size = _ref39.size,
      size = _ref39$size === void 0 ? 24 : _ref39$size,
      _ref39$color = _ref39.color,
      color = _ref39$color === void 0 ? 'currentColor' : _ref39$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "guardian-enquiry-flow",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    id: "enquiry-confirmation---add-phone-copy",
    transform: "translate(-855.000000, -523.000000)",
    fill: color
  }, React__default.createElement("g", {
    id: "Group",
    transform: "translate(420.000000, 175.000000)"
  }, React__default.createElement("g", {
    id: "Group-3",
    transform: "translate(125.000000, 333.000000)"
  }, React__default.createElement("g", {
    id: "Icon-/-Success",
    transform: "translate(310.000000, 15.000000)"
  }, React__default.createElement("path", {
    d: "M11.9999457,0 C13.0246718,0 13.8787914,1.20293399 14.8252777,1.45612605 C15.8032772,1.71801141 17.1442232,1.11056778 18.0005162,1.6060853 C18.8568091,2.10160283 19.0111157,3.57837544 19.716362,4.28361858 C20.4216084,4.98886172 21.8896943,5.13012768 22.3940161,5.99945667 C22.8981205,6.86878566 22.2853495,8.19559902 22.543976,9.17468079 C22.7938006,10.1200761 24,10.9741918 24,12 C24,13.0258082 22.7971692,13.8788373 22.543976,14.8253192 C22.2819808,15.8033143 22.8894272,17.1442543 22.3940161,18.0005433 C21.8983877,18.8568324 20.422695,19.0111383 19.716362,19.7163814 C19.010029,20.4216246 18.8698491,21.8897039 18.0005162,22.3939147 C17.1311832,22.8981255 15.8043639,22.2852486 14.8252777,22.5438739 C13.8787914,22.797066 13.0258672,24 11.9999457,24 C10.9742415,24 10.1201219,22.797066 9.17461367,22.5438739 C8.19661413,22.2819886 6.8556681,22.8894322 5.99948383,22.3939147 C5.1431909,21.8983972 4.98888431,20.4227112 4.28363798,19.7163814 C3.57828298,19.0100516 2.11030567,18.8698723 1.60598391,18.0005433 C1.10177081,17.1312143 1.71465052,15.804401 1.45602398,14.8253192 C1.20283077,13.8788373 0,13.0258082 0,12 C0,10.9741918 1.20283077,10.1200761 1.45602398,9.17468079 C1.71791052,8.19668568 1.11046414,6.85574572 1.60598391,5.99945667 C2.10150367,5.14316762 3.57828298,4.98886172 4.28363798,4.28361858 C4.98888431,3.57837544 5.13015091,2.11029612 5.99948383,1.6060853 C6.86881676,1.10187449 8.19563613,1.71475143 9.17461367,1.45612605 C10.1210999,1.20293399 10.9753282,0 11.9999457,0 Z M15.8588537,7.09114933 C15.6776454,6.9493724 15.4123802,6.97585372 15.2658084,7.15063046 L15.2658084,7.15063046 L9.93727076,13.484963 L8.46732934,12.3686734 C8.28443146,12.2297483 8.01916616,12.2603037 7.8751288,12.43671 L7.8751288,12.43671 L7.09031525,13.3985932 C6.94627789,13.5754069 6.97795766,13.8304425 7.16085554,13.9693675 L7.16085554,13.9693675 L9.93980514,16.0793179 C10.1210134,16.2170208 10.3828996,16.1889099 10.5277817,16.0161702 L10.5277817,16.0161702 L16.9059758,8.43395613 C17.0525476,8.2595868 17.0246694,8.00373648 16.8438835,7.86195955 L16.8438835,7.86195955 Z"
  })))))));
};
var IconHide = function IconHide(_ref40) {
  var _ref40$color = _ref40.color,
      color = _ref40$color === void 0 ? 'currentColor' : _ref40$color,
      _ref40$size = _ref40.size,
      size = _ref40$size === void 0 ? 24 : _ref40$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 23 / 24,
    viewBox: "0 0 24 23",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "guardian-enquiry-flow",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    id: "log-in---entered",
    transform: "translate(-857.000000, -474.000000)",
    fill: color
  }, React__default.createElement("g", {
    id: "Group",
    transform: "translate(418.000000, 217.000000)"
  }, React__default.createElement("g", {
    id: "Input/-Input-field-/-Populated",
    transform: "translate(125.000000, 209.000000)"
  }, React__default.createElement("g", {
    id: "Combined-Shape",
    transform: "translate(314.000000, 48.000000)"
  }, React__default.createElement("path", {
    d: "M2.48103771,1.03204964 L6.28816403,4.76962017 C8.10868167,3.58987339 10.012627,3 12,3 C16.3473399,3 20.2954724,5.82258629 23.8443975,11.4677589 C24.0261983,11.7569441 24.0463986,12.1159943 23.9049984,12.4207053 L23.8443983,12.5321822 L23.6018331,12.9127248 C22.3086963,14.9127112 20.9630951,16.5417496 19.5650296,17.7998398 L23.2280369,21.3936861 C23.4251209,21.5871093 23.4280885,21.9036779 23.2346652,22.1007618 C23.2324998,22.1029682 23.230314,22.1051545 23.2281081,22.1073203 L22.5649873,22.7583836 C22.3705215,22.9493135 22.0589727,22.9493446 21.8644688,22.7584534 L1.11746956,2.39681696 C0.920385634,2.20339374 0.91741804,1.88682516 1.11084125,1.68974124 C1.11300665,1.68753486 1.11519246,1.68534861 1.11739841,1.68318277 L1.78051915,1.03211947 C1.97498503,0.841189544 2.28653376,0.841158485 2.48103771,1.03204964 Z M3.66249788,6.93844397 L7.18403096,10.427219 C7.01792711,10.9217671 6.92801948,11.450533 6.92801948,12 C6.92801948,14.7614237 9.19882251,17 12,17 C12.5482336,17 13.0761515,16.9142524 13.570706,16.7556196 L16.6773374,19.8326324 C15.1732598,20.6108775 13.6141473,21 12,21 C7.6526601,21 3.70452759,18.1774137 0.155602456,12.5322411 C-0.0261983288,12.2430559 -0.0463986428,11.8840057 0.0950015998,11.5792947 L0.155601717,11.4678178 L0.398166938,11.0872752 C1.45146906,9.45822099 2.53957937,8.07527723 3.66249788,6.93844397 Z M12,7 C11.0186543,7 10.1024032,7.27474811 9.32608108,7.7504721 L11.6481128,10.0299759 C11.7624042,10.0102731 11.8799851,10 12,10 C13.120471,10 14.0287922,10.8954305 14.0287922,12 C14.0287922,12.1151437 14.0189218,12.2280148 13.9999697,12.3378358 L16.3225345,14.6171171 C16.7978291,13.8558491 17.0719805,12.9594859 17.0719805,12 C17.0719805,9.23857625 14.8011775,7 12,7 Z"
  })))))));
};
var IconPhone = function IconPhone(_ref41) {
  var _ref41$color = _ref41.color,
      color = _ref41$color === void 0 ? 'currentColor' : _ref41$color,
      _ref41$size = _ref41.size,
      size = _ref41$size === void 0 ? 24 : _ref41$size;
  return React__default.createElement("svg", {
    width: size,
    height: size * 1.08,
    viewBox: "0 0 24 26",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    id: "Screens",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    id: "become-a-provider",
    transform: "translate(-969.000000, -5480.000000)",
    fill: color
  }, React__default.createElement("g", {
    id: "Group-3",
    transform: "translate(948.000000, 5466.000000)"
  }, React__default.createElement("g", {
    id: "Icon-/-Phone-/-Dark",
    transform: "translate(21.000000, 14.000000)"
  }, React__default.createElement("path", {
    d: "M23.3799471,23.372121 C19.9191083,26.6009277 14.7387827,26.8760034 10.9593081,24.0507429 C9.20373323,22.7387261 7.36091034,21.1050958 5.58092302,19.0786252 C3.82046567,17.0742218 2.43850771,15.0462218 1.35289588,13.137079 C-1.00152742,8.99630588 -0.224361819,3.72813782 3.22925938,0.505885714 C4.00557586,-0.2184 5.2051833,-0.157878992 5.90889921,0.641129412 L9.46972299,4.68402017 C10.1734389,5.48302857 10.1144244,6.71770084 9.33832023,7.44198655 L7.19915121,9.43786891 C6.57079551,10.0240706 6.40627671,10.9637849 6.77989361,11.7474992 C7.30401868,12.8464908 8.06929647,14.1497681 9.18547695,15.4541378 C10.4468585,16.9287092 11.7706512,17.9542891 12.8715473,18.6534487 C13.5869388,19.107684 14.505909,19.0154824 15.1287453,18.4343059 L17.2708863,16.4358017 C18.043594,15.7147933 19.2378943,15.7750958 19.938426,16.5703899 L23.510713,20.6266084 C24.2112447,21.4219025 24.1526548,22.6511126 23.3799471,23.372121",
    id: "Fill-1"
  }))))));
};
var IconPlay = function IconPlay(_ref42) {
  var _ref42$color = _ref42.color,
      color = _ref42$color === void 0 ? 'currentColor' : _ref42$color,
      _ref42$size = _ref42.size,
      size = _ref42$size === void 0 ? 20 : _ref42$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 22 28",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M2.458.19C1.097-.51-.402.844.098 2.321v23.356c-.5 1.477.999 2.832 2.36 2.133l18.605-12.253c1.25-.641 1.25-2.475 0-3.116L2.458.189z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconPlus = function IconPlus(_ref43) {
  var _ref43$color = _ref43.color,
      color = _ref43$color === void 0 ? 'currentColor' : _ref43$color,
      _ref43$size = _ref43.size,
      size = _ref43$size === void 0 ? 24 : _ref43$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M14.526 22.993v-8.467h8.465c.558 0 1.009-.45 1.009-1.007V10.48c0-.557-.45-1.007-1.009-1.007h-8.465V1.007c0-.556-.45-1.007-1.008-1.007h-3.036c-.558 0-1.008.45-1.008 1.007v8.467H1.009C.45 9.474 0 9.924 0 10.48v3.038c0 .557.45 1.007 1.009 1.007h8.465v8.467c0 .556.45 1.007 1.008 1.007h3.036c.558 0 1.008-.45 1.008-1.007",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconProfile = function IconProfile(_ref44) {
  _ref44.color;
      var _ref44$size = _ref44.size,
      size = _ref44$size === void 0 ? 32 : _ref44$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 44 44",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("circle", {
    cx: "22",
    cy: "22",
    r: "22",
    fill: "#E4E4EB"
  }), React__default.createElement("circle", {
    cx: "22",
    cy: "22",
    r: "20",
    fill: "#E4E4EB"
  }), React__default.createElement("mask", {
    id: "mask0_304_19008",
    masktype: "luminance",
    maskUnits: "userSpaceOnUse",
    x: "2",
    y: "2",
    width: "40",
    height: "40"
  }, React__default.createElement("circle", {
    cx: "22",
    cy: "22",
    r: "20",
    fill: "white"
  })), React__default.createElement("g", {
    mask: "url(#mask0_304_19008)"
  }, React__default.createElement("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M35.6385 41.8368C35.7212 39.6778 35.6059 37.2106 34.3944 35.4685C33.8583 34.6974 33.1082 34.0677 32.0641 33.6707C29.7754 32.8003 28.0232 33.9946 25.9212 34.6538C23.6496 35.3662 21.3502 35.1972 19.098 34.4677C13.3559 32.6079 9.0921 27.5353 5.33484 30.4773C5.20617 30.578 5.10775 30.6391 4.9782 30.7502C4.71866 30.973 4.41481 31.2953 4.08216 31.8096C2.88679 33.6573 1.31768 37.9849 0.0776946 49.0976C-0.138757 51.0376 -0.306058 52.9258 -0.435198 54.6578C6.54566 64.1565 17.5055 69.9565 28.1118 71.4471C29.3715 67.8311 31.9868 60.1442 33.3939 54.7136C34.4814 50.5167 35.4719 46.1854 35.6385 41.8368Z",
    fill: "#F5F5F8"
  }), React__default.createElement("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M35.6122 41.8764C35.6949 39.7173 35.5795 37.2501 34.3681 35.5081C33.8319 34.7369 33.0818 34.1073 32.0378 33.7103C29.749 32.8399 27.9969 34.0341 25.8948 34.6934C23.6233 35.4058 21.3238 35.2367 19.0717 34.5073C13.3296 32.6475 9.06574 27.5749 5.30847 30.5168C5.1798 30.6175 5.08138 30.6786 4.95184 30.7898C4.6923 31.0126 4.38844 31.3349 4.0558 31.8492C2.86043 33.6969 1.29131 38.0244 0.0513274 49.1372C-0.165124 51.0771 -0.332425 52.9654 -0.461565 54.6973C6.51929 64.1961 17.4791 69.9961 28.0854 71.4866C29.3451 67.8706 31.9605 60.1837 33.3676 54.7532C34.455 50.5562 35.4455 46.225 35.6122 41.8764Z",
    fill: "white"
  }), React__default.createElement("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M32.0823 21.135C31.617 26.4535 26.9283 30.3877 21.6099 29.9224C16.2915 29.4571 12.3572 24.7685 12.8225 19.45C13.2878 14.1316 17.9765 10.1973 23.2949 10.6626C28.6133 11.1279 32.5476 15.8166 32.0823 21.135Z",
    fill: "#F5F5F8"
  }), React__default.createElement("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M32.0628 21.1862C31.5943 26.5413 26.903 30.5053 21.5847 30.04C16.2662 29.5747 12.3345 24.8563 12.8031 19.5012C13.2716 14.1462 17.9628 10.1821 23.2813 10.6474C28.5997 11.1127 32.5313 15.8312 32.0628 21.1862Z",
    fill: "white"
  }), React__default.createElement("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M32.0823 21.135C31.617 26.4534 26.9283 30.3877 21.61 29.9224C16.2915 29.4571 12.3572 24.7683 12.8225 19.45C13.2878 14.1316 17.9765 10.1973 23.295 10.6626C28.6133 11.1279 32.5476 15.8166 32.0823 21.135Z",
    fill: "white"
  }), React__default.createElement("path", {
    "fill-rule": "evenodd",
    "clip-rule": "evenodd",
    d: "M27.2298 22.9905C26.9654 24.9327 24.8264 26.9662 22.2885 26.8951C19.7362 26.8237 17.8098 24.6736 17.5142 22.4784C17.289 20.8053 17.8682 18.7578 18.7968 18.5904C19.8842 18.3944 20.3072 20.981 22.4243 21.4618C24.321 21.8925 25.7475 20.2181 26.7341 20.9715C27.432 21.5045 27.2615 22.7575 27.2298 22.9905Z",
    fill: "#E4E4EB"
  })));
};
var IconQuote = function IconQuote(_ref45) {
  var _ref45$color = _ref45.color,
      color = _ref45$color === void 0 ? 'currentColor' : _ref45$color,
      _ref45$size = _ref45.size,
      size = _ref45$size === void 0 ? 32 : _ref45$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 100 100",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("circle", {
    stroke: color,
    strokeWidth: "8",
    fill: "transparent",
    cx: "50",
    cy: "50",
    r: "46"
  }), React__default.createElement("path", {
    d: "M45.05 67.15v-15.6h-7.2c.5-4.3 2.7-8.55 6.6-12.75l-5.7-4.5c-3.3 3.3-5.65 6.725-7.05 10.275-1.4 3.55-2.1 7.425-2.1 11.625v10.95h15.45zm25.2 0v-15.6h-7.2c.5-4.3 2.7-8.55 6.6-12.75l-5.7-4.5c-3.3 3.3-5.65 6.725-7.05 10.275-1.4 3.55-2.1 7.425-2.1 11.625v10.95h15.45z",
    fill: color,
    fillRule: "nonzero"
  })));
};
var IconRocket = function IconRocket(_ref46) {
  var _ref46$color = _ref46.color,
      color = _ref46$color === void 0 ? 'currentColor' : _ref46$color,
      _ref46$size = _ref46.size,
      size = _ref46$size === void 0 ? 32 : _ref46$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24"
  }, React__default.createElement("path", {
    d: "M20.735.077c2.495-.392 3.584.719 3.184 3.222-.827 5.178-3.389 9.387-7.245 12.85-.835.75-1.266 1.496-1.095 2.584.196 1.25-.198 2.2-1.28 2.95-.78.539-1.392 1.313-2.15 1.893-1.169.894-2.474.312-2.607-1.157-.077-.843-.014-1.699-.014-2.534l-7.511 1.97 1.966-7.46c-.717 0-1.568.115-2.365-.04-.571-.11-1.32-.47-1.531-.928-.208-.448-.02-1.303.316-1.73.834-1.062 1.777-2.072 2.83-2.914.466-.372 1.31-.416 1.969-.377 1.046.06 1.765-.261 2.477-1.049C11.194 3.475 15.471.904 20.735.077zM17.685 8c1.075.008 1.612-.686 1.744-1.82-.193-.948-.717-1.65-1.804-1.607-.991.04-1.611.666-1.625 1.673-.014 1.08.619 1.745 1.684 1.754zm-5.583 5.712c.991-.048 1.61-.68 1.612-1.69.002-1.08-.64-1.74-1.708-1.736-1.079.003-1.61.703-1.72 1.764.171 1.046.737 1.713 1.816 1.662zm-6.388 4.574l3.429-1.218-1.904-2.21-1.525 3.428z",
    fill: color
  }));
};
var IconSearchClose = function IconSearchClose(_ref47) {
  var _ref47$size = _ref47.size,
      size = _ref47$size === void 0 ? 20 : _ref47$size,
      _ref47$color = _ref47.color,
      color = _ref47$color === void 0 ? 'currentColor' : _ref47$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 20 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm1.414-10l2.122-2.121a1 1 0 10-1.415-1.415L10 8.586 7.879 6.464A1 1 0 006.464 7.88L8.586 10l-2.122 2.121a1 1 0 001.415 1.415L10 11.414l2.121 2.122a1 1 0 001.415-1.415L11.414 10z",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconSearch = function IconSearch(_ref48) {
  var _ref48$size = _ref48.size,
      size = _ref48$size === void 0 ? 24 : _ref48$size,
      _ref48$color = _ref48.color,
      color = _ref48$color === void 0 ? 'currentColor' : _ref48$color;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M23.454 20.535c.952.952.524 1.88-.256 2.661-.78.782-1.71 1.21-2.662.258l-4.48-4.48a10.273 10.273 0 01-5.705 1.727C4.643 20.701 0 16.059 0 10.351 0 4.642 4.643 0 10.351 0c5.709 0 10.35 4.642 10.35 10.351 0 2.061-.612 4.03-1.726 5.705l4.479 4.479zm-7.48-5.297a7.41 7.41 0 001.84-4.887c0-4.115-3.349-7.464-7.463-7.464s-7.464 3.35-7.464 7.464c0 4.113 3.35 7.463 7.464 7.463 1.817 0 3.532-.66 4.888-1.84l.389-.338.347-.398z",
    fill: color,
    fillRule: "nonzero"
  }));
};
var IconStar = function IconStar(_ref49) {
  var _ref49$color = _ref49.color,
      color = _ref49$color === void 0 ? 'currentColor' : _ref49$color,
      _ref49$size = _ref49.size,
      size = _ref49$size === void 0 ? 20 : _ref49$size;
  return React__default.createElement("svg", {
    width: size * 1.05,
    height: size,
    viewBox: "0 0 21 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    d: "M11.893.86l2.162 4.354c.15.304.442.514.779.563l4.834.698c1.274.184 1.783 1.74.861 2.633l-3.498 3.388a1.023 1.023 0 0 0-.297.91l.825 4.786c.218 1.261-1.114 2.223-2.254 1.627l-4.323-2.259a1.038 1.038 0 0 0-.963 0l-4.324 2.26c-1.14.595-2.472-.367-2.254-1.628l.825-4.785a1.024 1.024 0 0 0-.297-.91L.47 9.106c-.922-.892-.413-2.448.86-2.632l4.835-.698c.337-.05.628-.26.78-.563L9.105.86c.57-1.148 2.217-1.148 2.787 0",
    fill: color,
    fillRule: "evenodd"
  }));
};
var IconTwitter = function IconTwitter(_ref50) {
  var _ref50$color = _ref50.color,
      color = _ref50$color === void 0 ? 'currentColor' : _ref50$color,
      _ref50$size = _ref50.size,
      size = _ref50$size === void 0 ? 32 : _ref50$size;
  return React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    preserveAspectRatio: "none"
  }, React__default.createElement("path", {
    fill: color,
    d: "M32 7.075a12.941 12.941 0 0 1-3.769 1.031 6.601 6.601 0 0 0 2.887-3.631 13.21 13.21 0 0 1-4.169 1.594A6.565 6.565 0 0 0 22.155 4a6.563 6.563 0 0 0-6.563 6.563c0 .512.056 1.012.169 1.494A18.635 18.635 0 0 1 2.23 5.195a6.56 6.56 0 0 0-.887 3.3 6.557 6.557 0 0 0 2.919 5.463 6.565 6.565 0 0 1-2.975-.819v.081a6.565 6.565 0 0 0 5.269 6.437 6.574 6.574 0 0 1-2.968.112 6.588 6.588 0 0 0 6.131 4.563 13.17 13.17 0 0 1-9.725 2.719 18.568 18.568 0 0 0 10.069 2.95c12.075 0 18.681-10.006 18.681-18.681 0-.287-.006-.569-.019-.85A13.216 13.216 0 0 0 32 7.076z"
  }));
};
var IconUserFamily = function IconUserFamily(_ref51) {
  var _ref51$size = _ref51.size,
      size = _ref51$size === void 0 ? 16 : _ref51$size,
      _ref51$color = _ref51.color,
      color = _ref51$color === void 0 ? 'currentColor' : _ref51$color;
  return React__default.createElement("svg", {
    height: size,
    width: size * 1.375,
    viewBox: "0 0 42 50",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: color,
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M20.5 19A9.5 9.5 0 0 0 30 9.5C30 4.254 25.747 0 20.5 0 15.254 0 11 4.254 11 9.5c0 5.247 4.254 9.5 9.5 9.5M20.693 40.953c-3.172 0-5.742-2.615-5.742-5.842 0-3.227 2.57-5.843 5.742-5.843 3.17 0 5.742 2.616 5.742 5.843s-2.571 5.842-5.742 5.842m21.158.485c-1.325-6.752-5.455-16.235-10.098-18.148-1.224-.503-2.617-.336-3.733.38a12.96 12.96 0 0 1-7.025 2.063c-2.583 0-4.99-.758-7.024-2.064-1.118-.715-2.51-.882-3.734-.38C5.593 25.204 1.29 35.214.139 41.44c-.437 2.367.117 4.788 2.232 5.869 2.227 1.138 5.014 1.995 8.08 2.584 1.059-2.76 2.696-5.535 4.421-6.245.663-.273 1.415-.183 2.02.205a7.015 7.015 0 0 0 3.8 1.116 7.02 7.02 0 0 0 3.801-1.116 2.194 2.194 0 0 1 2.02-.205c1.772.729 3.404 3.501 4.465 6.353 3.29-.59 6.278-1.485 8.64-2.692 2.116-1.08 2.696-3.507 2.233-5.87"
  })));
};
var IconUsers = function IconUsers(_ref52) {
  var _ref52$size = _ref52.size,
      size = _ref52$size === void 0 ? 19 : _ref52$size,
      _ref52$color = _ref52.color,
      color = _ref52$color === void 0 ? 'currentColor' : _ref52$color;
  return React__default.createElement("svg", {
    width: size * 1.157,
    height: size,
    viewBox: "0 0 60 50",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("g", {
    fill: color,
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M43.42 19.618a9.23 9.23 0 0 0 9.231-9.23 9.23 9.23 0 1 0-18.462 0 9.23 9.23 0 0 0 9.232 9.23M59.065 35.09c-1.292-3.517-3.947-9.589-7.308-11.055a2.957 2.957 0 0 0-2.776.242 9.378 9.378 0 0 1-10.269.01c-.815-.53-1.83-.652-2.724-.28-.518.216-1.02.544-1.5.955a1.724 1.724 0 0 0-.193 2.426c2.966 3.448 5.281 8.887 6.588 12.443.057.154.103.306.143.46.26 1.03 1.211 1.711 2.244 1.724 5.31.07 10.687-.797 14.186-2.617 1.532-.794 2.217-2.655 1.61-4.308M18.37 24.37c6.34 0 11.48-5.295 11.48-11.825C29.85 6.013 24.71.719 18.37.719 12.03.72 6.89 6.013 6.89 12.545c0 6.53 5.14 11.825 11.48 11.825M27.71 27.902c-1.063-.443-2.271-.297-3.24.334a11.157 11.157 0 0 1-6.1 1.812c-2.243 0-4.332-.665-6.099-1.812-.97-.631-2.178-.777-3.241-.334-4.032 1.68-7.218 8.956-8.768 13.169-.727 1.98.102 4.208 1.939 5.157.478.248.99.478 1.523.697 3.384 1.382 7.803 2.173 12.374 2.368 6.8.292 13.929-.73 18.442-3.065 1.836-.949 2.665-3.177 1.938-5.157-1.55-4.213-4.736-11.49-8.768-13.17"
  })));
};
var IconWarning = function IconWarning(_ref53) {
  var _ref53$color = _ref53.color,
      color = _ref53$color === void 0 ? 'currentColor' : _ref53$color,
      _ref53$size = _ref53.size,
      size = _ref53$size === void 0 ? 20 : _ref53$size;
  return React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 32 32"
  }, React__default.createElement("path", {
    fill: color,
    fillRule: "evenodd",
    d: "M28.359 23.597L17.575 6.147c-.869-1.403-2.281-1.406-3.15 0L3.641 23.597C2.475 25.481 3.322 27 5.529 27h20.944c2.203 0 3.05-1.522 1.887-3.403zM16 24a1 1 0 1 1 0-2 1 1 0 1 1 0 2zm1-4.997c0 .544-.447.997-1 .997-.556 0-1-.447-1-.997v-6.006c0-.544.447-.997 1-.997.556 0 1 .447 1 .997v6.006z"
  }));
};
var IconWalk = function IconWalk(_ref54) {
  var _ref54$color = _ref54.color,
      color = _ref54$color === void 0 ? 'currentColor' : _ref54$color,
      _ref54$size = _ref54.size,
      size = _ref54$size === void 0 ? 20 : _ref54$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 16 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    d: "M9.541 5.137c.417.347.655.796.884 1.256.573 1.155 1.128 2.318 1.698 3.475a.305.305 0 00.194.138c.943.174 1.887.343 2.831.507.554.097.905.605.804 1.179a.984.984 0 01-1.156.78c-1.158-.228-2.314-.46-3.472-.687-.355-.07-.591-.284-.75-.593-.256-.496-.505-.996-.758-1.493-.033-.066-.07-.13-.133-.244-.1.472-.188.892-.275 1.313-.104.506-.198 1.013-.313 1.516-.03.136.005.217.092.31l1.83 1.954c.307.325.615.648.926.968.312.32.432.69.434 1.101.01 2.027.008 4.055.003 6.082-.002.646-.543 1.16-1.191 1.155-.667-.005-1.183-.526-1.184-1.208a700.36 700.36 0 01.003-4.903.617.617 0 00-.205-.485c-.885-.872-1.761-1.752-2.64-2.63-.053-.052-.108-.101-.186-.175-.19 1.148-.36 2.257-.569 3.359a1.71 1.71 0 01-.352.733c-.783.954-1.592 1.888-2.392 2.83-.518.61-1.039 1.217-1.553 1.83-.457.545-1.187.613-1.726.153-.49-.418-.51-1.146-.072-1.667 1.07-1.272 2.13-2.553 3.193-3.832a.405.405 0 00.089-.18c.55-3.218 1.115-6.434 1.848-9.617.003-.013 0-.027 0-.078-.232.13-.44.273-.668.366-.262.108-.38.32-.498.546L3.06 11.197a5.11 5.11 0 00-.199.43c-.222.54-.856.791-1.378.543a1.026 1.026 0 01-.47-1.402C1.548 9.78 2.08 8.79 2.614 7.802c.07-.127.14-.253.206-.382.138-.274.35-.468.618-.618a578.516 578.516 0 003.404-1.929c.961-.547 1.85-.443 2.699.264zM8.896 0c1.1.001 1.994.9 1.982 1.991a1.99 1.99 0 01-1.987 1.97 1.98 1.98 0 01-1.979-1.995A1.974 1.974 0 018.896 0z",
    id: "wa"
  })), React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("mask", {
    id: "wb",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#wa"
  })), React__default.createElement("use", {
    fill: "#000",
    xlinkHref: "#wa"
  }), React__default.createElement("g", {
    mask: "url(#wb)",
    fill: color
  }, React__default.createElement("path", {
    d: "M-4 0h24v24H-4z"
  }))));
};
var IconEYFSCL = function IconEYFSCL(_ref55) {
  var _ref55$color = _ref55.color,
      color = _ref55$color === void 0 ? 'currentColor' : _ref55$color,
      _ref55$size = _ref55.size,
      size = _ref55$size === void 0 ? 20 : _ref55$size;
  return React__default.createElement("svg", {
    width: size,
    height: size / 1.2,
    viewBox: "0 0 48 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M16.6444 6C18.0135 6 19.4011 6.16273 20.775 6.50174C21.576 6.69913 22.3483 6.94575 23.0898 7.23709C22.5879 8.48893 22.2525 9.80827 22.0979 11.1592C21.3772 10.8419 20.6153 10.582 19.8168 10.3853C18.7751 10.1282 17.7101 10 16.6444 10C11.807 10 7.3702 12.6243 5.30022 16.7257C2.88901 21.5063 3.92619 27.1466 7.71968 30.9708L8.09538 31.3351L9.98534 33.0983L9.176 35.478L9.11629 35.7197L9.052 35.918L9.47103 35.9049C10.0283 35.8838 10.5759 35.8591 11.1123 35.8306L12.6864 35.7341L14.1836 35.6147C16.1244 35.44 17.8347 35.2027 19.212 34.8963L19.9193 34.7322C24.2579 33.8866 27.7975 30.9742 29.2756 26.9305C29.4295 26.5093 29.5581 26.085 29.6622 25.6592C30.4967 26.0807 31.3745 26.4146 32.2808 26.6542L33.4635 26.9388C33.3414 27.3958 33.1979 27.8512 33.0325 28.3037C31.0128 33.8292 26.218 37.5799 20.6845 38.6583C17.9234 39.3499 14.1021 39.6939 10.5554 39.8589L8.65615 39.9321C8.34682 39.9417 8.04176 39.9501 7.74198 39.9573L5.88843 39.9902L4.24194 40C3.04504 40.0008 2.38971 38.6272 3.17322 37.7649C4.04422 36.8065 4.90873 35.6879 5.23002 34.7434L5.31627 34.4354C5.32989 34.3756 5.34751 34.3166 5.36674 34.2599C0.167351 29.4093 -1.67847 21.6799 1.72877 14.9244C4.52875 9.37655 10.4099 6 16.6444 6ZM36.9961 0C41.1178 0 45.006 2.37869 46.8571 6.28698C49.0346 10.8874 47.9669 16.129 44.7882 19.56L44.4519 19.9084L44.47 19.9694L44.4853 20.032C44.61 20.6496 45.1114 21.4078 45.6634 22.0903L45.9021 22.3775C46.3831 22.9416 46.0439 23.8163 45.3594 23.9379L45.1956 23.9521L43.8546 23.9418C41.201 23.9023 37.593 23.7337 35.0043 23.1705L34.3251 23.0069C30.8192 22.2788 27.7619 19.8219 26.338 16.1924L26.1616 15.7124C24.0114 9.44424 27.4611 2.41804 33.8267 0.477799L34.2652 0.35346C34.9919 0.1624 35.7243 0.0508218 36.4517 0.0138327L36.9961 0Z",
    fill: color
  }));
};
var IconEYFSPD = function IconEYFSPD(_ref56) {
  var _ref56$color = _ref56.color,
      color = _ref56$color === void 0 ? 'currentColor' : _ref56$color,
      _ref56$size = _ref56.size,
      size = _ref56$size === void 0 ? 20 : _ref56$size;
  return React__default.createElement("svg", {
    width: size / 1.41,
    height: size,
    viewBox: "0 0 34 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M13.994 14C9.94284 14 6.80715 13.4847 4.24397 11.3725C1.81853 9.37379 0.423232 6.54584 0.0580675 2.88866L0.00426308 2.23467C-0.0650435 1.16804 0.71854 0.235556 1.78338 0.117478C5.82987 -0.33123 9.14068 0.505443 11.7158 2.6275C14.2134 4.68567 15.6186 7.62316 15.9315 11.4399L15.9789 12.2219L15.9849 12.5846L15.9789 13.6588C15.9773 13.7677 15.9756 13.8814 15.9736 14H17.0011C17.5547 14 18.0035 14.4477 18.0035 15V22.0304C18.2703 17.8733 19.726 14.7375 22.2865 12.6275C24.8616 10.5054 28.1724 9.66877 32.2189 10.1175C33.2077 10.2271 33.9539 11.039 34 12.0084L33.998 12.2347L33.9442 12.8887C33.579 16.5458 32.1837 19.3738 29.7583 21.3725C27.3782 23.3338 24.3419 24.1091 20.6997 24.0256L19.9873 23.9997L18.0035 24V28H27.1458C29.3602 28 31.1553 29.7909 31.1553 32C31.1553 32.2634 31.1292 32.5262 31.0774 32.7845L28.6717 44.7845C28.2969 46.6542 26.6514 48 24.7401 48H7.25739C5.34616 48 3.70063 46.6542 3.3258 44.7845L0.920137 32.7845C0.485866 30.6182 1.89405 28.5109 4.0654 28.0777C4.32431 28.026 4.58769 28 4.85172 28H13.994V14ZM27.1458 32H4.85172L7.25739 44H24.7401L27.1458 32Z",
    fill: color
  }));
};
var IconEYFSPSE = function IconEYFSPSE(_ref57) {
  var _ref57$color = _ref57.color,
      color = _ref57$color === void 0 ? 'currentColor' : _ref57$color,
      _ref57$size = _ref57.size,
      size = _ref57$size === void 0 ? 20 : _ref57$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M31.2211 22.219C32.9114 21.7829 34.6558 22.0027 36.1321 22.8377C38.1685 23.9895 38.8395 26.0421 39.1659 27.4971C40.7642 27.0265 42.8388 26.6311 44.7534 27.714C46.2306 28.5495 47.2979 29.9201 47.7567 31.5717C48.2075 33.1915 48.0281 34.9101 47.2496 36.4105C46.2671 38.3023 44.8358 39.1921 43.6983 39.7533C42.7877 40.2051 41.2906 40.6387 39.7743 40.9904L39.2058 41.1183L38.0902 41.3481L37.047 41.5381L36.5463 41.6211L35.6275 41.7591L34.8134 41.8625L34.0974 41.9348L33.4728 41.9795L32.933 42L32.4713 41.9998L32.0811 41.9823L31.9108 41.9681L31.6154 41.9312L31.3749 41.8857L31.1026 41.8087L30.9704 41.7568L30.7942 41.6667L30.5756 41.5243L30.4552 41.4234L30.3156 41.2863L30.1563 41.1055L29.8799 40.7363L29.5574 40.2273L29.3163 39.7978L29.0539 39.2877L28.9146 39L28.6197 38.3549L28.2199 37.4081L27.7849 36.2917L27.4274 35.3072C26.7643 33.4138 26.0958 31.1146 26.0181 29.7543C25.9113 27.9057 26.2747 26.4601 27.1975 25.0699C28.1336 23.6608 29.5628 22.648 31.2211 22.219ZM31.5077 8.1667C33.7295 9.85806 35.2433 12.3271 35.7683 15.1167C36.0203 16.4583 36.0671 17.7078 35.907 18.949C34.8642 18.5683 33.7681 18.3768 32.6584 18.3767C32.7573 17.5229 32.7163 16.6725 32.546 15.7678C32.1774 13.8017 31.1105 12.0623 29.5401 10.8663C27.9648 9.66391 26.0526 9.1547 24.1512 9.43401C22.3288 9.70174 20.897 10.7702 19.2791 12.993L19.0071 13.375L17.5915 15.4513L17.3023 15.1879L15.9231 13.9794C15.468 13.5879 15.1308 13.3231 14.718 13.0334C12.9822 11.8147 11.3375 11.3164 9.70435 11.5563C7.80534 11.8353 6.10158 12.8761 4.90241 14.4868C3.70848 16.0886 3.14236 18.068 3.30804 20.0655C3.47185 22.0856 4.20903 23.7524 5.69644 25.4898C6.23435 26.1159 7.14279 26.9273 8.29538 27.829L9.01523 28.3801L9.7876 28.9483L11.0314 29.8226L11.9086 30.4135L13.2334 31.2702L14.1439 31.8359C15.9815 32.9581 17.49 33.7571 18.6481 34.2218L18.9139 34.3251L19.3054 34.4636L19.6391 34.5641L19.8158 34.6051L19.97 34.6693L20.1805 34.6313C20.4802 34.5771 20.9634 34.2698 21.5693 33.7945C22.0051 33.4525 22.4877 33.0297 23.0154 32.5278C23.2675 33.603 23.6119 34.8088 24.0223 36.0745C23.6556 36.3842 23.3104 36.6525 22.9852 36.8827C22.0333 37.5564 21.3841 37.8472 20.6344 37.9574C19.8913 38.0665 19.1938 37.9728 18.1026 37.5978C17.0918 37.2504 15.8626 36.6758 14.3991 35.8618L13.4927 35.3447L12.5294 34.7695L11.5083 34.1358L10.5767 33.5391L9.61061 32.8984C8.77277 32.3329 7.97601 31.7685 7.23917 31.2191L6.38495 30.5678L5.9379 30.2142L5.10876 29.5288C4.32538 28.8599 3.6843 28.2438 3.23021 27.7136C1.26225 25.4184 0.264847 23.1271 0.0387987 20.3665C-0.191868 17.5357 0.611566 14.7255 2.29999 12.4575C4.02801 10.1396 6.50175 8.63608 9.25728 8.23128C11.7705 7.86208 14.0241 8.48498 16.3795 10.1084L16.7963 10.4053L16.9849 10.5467L17.1924 10.2803C18.7234 8.3463 20.2742 7.12514 22.0922 6.49869L22.486 6.37351L22.8885 6.26628L23.3164 6.17427L23.7042 6.10897C26.4616 5.7039 29.2374 6.43654 31.5077 8.1667Z",
    fill: color
  }));
};
var IconEYFSMD = function IconEYFSMD(_ref58) {
  var _ref58$color = _ref58.color,
      color = _ref58$color === void 0 ? 'currentColor' : _ref58$color,
      _ref58$size = _ref58.size,
      size = _ref58$size === void 0 ? 20 : _ref58$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M44 0C46.2091 0 48 1.79086 48 4V44C48 46.2091 46.2091 48 44 48H4C1.79086 48 0 46.2091 0 44V4C0 1.79086 1.79086 0 4 0H44ZM42 4.04186H6C4.97433 4.04186 4.12899 4.81394 4.01346 5.80861L4 6.04186V42C4 43.0257 4.77208 43.871 5.76676 43.9865L6 44H42C43.0257 44 43.871 43.2279 43.9865 42.2332L44 42V6.04186C44 4.93729 43.1046 4.04186 42 4.04186ZM14.5 28C14.9909 28 15.3992 28.3538 15.4839 28.8202L15.5 29V32.5H19L19.1798 32.5161C19.5879 32.5902 19.9098 32.9121 19.9839 33.3202L20 33.5V34.5L19.9839 34.6798C19.9098 35.0879 19.5879 35.4098 19.1798 35.4839L19 35.5H15.5V39L15.4839 39.1798C15.4098 39.5879 15.0879 39.9098 14.6798 39.9839L14.5 40H13.5L13.3202 39.9839C12.9121 39.9098 12.5902 39.5879 12.5161 39.1798L12.5 39V35.5H9L8.82025 35.4839C8.41206 35.4098 8.0902 35.0879 8.01611 34.6798L8 34.5V33.5L8.01611 33.3202C8.0902 32.9121 8.41206 32.5902 8.82025 32.5161L9 32.5H12.5V29L12.5161 28.8202C12.5902 28.4121 12.9121 28.0902 13.3202 28.0161L13.5 28H14.5ZM39 35L39.1798 35.0161C39.5879 35.0902 39.9098 35.4121 39.9839 35.8202L40 36V37L39.9839 37.1798C39.9098 37.5879 39.5879 37.9098 39.1798 37.9839L39 38H29L28.8202 37.9839C28.4121 37.9098 28.0902 37.5879 28.0161 37.1798L28 37V36L28.0161 35.8202C28.0902 35.4121 28.4121 35.0902 28.8202 35.0161L29 35H39ZM39 30L39.1798 30.0161C39.5879 30.0902 39.9098 30.4121 39.9839 30.8202L40 31V32L39.9839 32.1798C39.9098 32.5879 39.5879 32.9098 39.1798 32.9839L39 33H29L28.8202 32.9839C28.4121 32.9098 28.0902 32.5879 28.0161 32.1798L28 32V31L28.0161 30.8202C28.0902 30.4121 28.4121 30.0902 28.8202 30.0161L29 30H39ZM31.3866 9.28809L31.5251 9.40381L34 11.8787L36.4749 9.40381L36.6134 9.28809C36.9544 9.05185 37.4096 9.05185 37.7506 9.28809L37.8891 9.40381L38.5962 10.1109C38.9433 10.458 38.9819 10.9969 38.7119 11.3866L38.5962 11.5251L36.1213 14L38.5962 16.4749L38.7119 16.6134C38.9482 16.9544 38.9482 17.4096 38.7119 17.7506L38.5962 17.8891L37.8891 18.5962L37.7506 18.7119C37.4096 18.9481 36.9544 18.9481 36.6134 18.7119L36.4749 18.5962L34 16.1213L31.5251 18.5962L31.3866 18.7119C31.0456 18.9481 30.5904 18.9481 30.2494 18.7119L30.1109 18.5962L29.4038 17.8891L29.2881 17.7506C29.0519 17.4096 29.0519 16.9544 29.2881 16.6134L29.4038 16.4749L31.8787 14L29.4038 11.5251L29.2881 11.3866C29.0519 11.0456 29.0519 10.5904 29.2881 10.2494L29.4038 10.1109L30.1109 9.40381L30.2494 9.28809C30.5904 9.05185 31.0456 9.05185 31.3866 9.28809ZM19 12.5L19.1798 12.5161C19.5879 12.5902 19.9098 12.9121 19.9839 13.3202L20 13.5V14.5L19.9839 14.6798C19.9098 15.0879 19.5879 15.4098 19.1798 15.4839L19 15.5H9L8.82025 15.4839C8.41206 15.4098 8.0902 15.0879 8.01611 14.6798L8 14.5V13.5L8.01611 13.3202C8.0902 12.9121 8.41206 12.5902 8.82025 12.5161L9 12.5H19Z",
    fill: color
  }));
};
var IconEYFSUW = function IconEYFSUW(_ref59) {
  var _ref59$color = _ref59.color,
      color = _ref59$color === void 0 ? 'currentColor' : _ref59$color,
      _ref59$size = _ref59.size,
      size = _ref59$size === void 0 ? 20 : _ref59$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24C0 10.7452 10.7452 0 24 0ZM24 5C19.9556 5 16.2064 6.26364 13.1256 8.41765C14.0794 8.63043 15.4307 8.61088 17.1795 8.36092L17.7772 8.26907C21.6825 7.62892 22.6055 8.54301 22.5464 9.90088L22.5163 10.2216L22.4567 10.5551L22.3732 10.8982L22.2713 11.248L22.1563 11.6014C21.7761 12.7266 21.1173 13.0069 20.2923 13.2686L19.7188 13.4494C19.1233 13.6456 18.4649 13.9153 17.7772 14.5032C17.0894 15.0911 16.7735 15.5684 16.5513 15.9625L16.3502 16.3298C16.0963 16.7852 15.8445 17.1087 14.9361 17.3649L14.5633 17.458C12.262 17.969 11.9517 19.5419 13.2904 20.9266C13.9204 21.5782 14.7122 21.5027 15.5979 21.3468L16.2787 21.2248C17.091 21.0885 17.9623 21.0144 18.8469 21.4356L19.901 21.9461C21.1209 22.5614 21.6794 22.9984 22.5504 24.2618L22.7753 24.5955C23.5583 25.7813 23.9425 25.8619 24.2896 25.7603L24.6377 25.6322C24.7283 25.6021 24.8242 25.5798 24.9309 25.5798C25.5808 25.5798 26.9739 27.6163 25.1637 29.2562L24.9309 29.4509C22.8779 31.0399 21.1112 35.8014 20.9476 37.817L20.9353 38.098C20.9353 39.9105 19.2083 39.5291 18.8469 38.6377L18.721 38.2976C18.6984 38.2316 18.6754 38.1612 18.6521 38.0868L18.5132 37.5943C18.1964 36.3448 17.9771 34.4387 18.7973 32.8461C19.8676 30.7678 16.8352 28.6553 16.0294 28.4958L15.9438 28.4862C15.3295 28.4862 13.6173 26.8817 14.4357 24.8719L14.5633 24.5955C14.9313 23.8831 14.8269 23.644 14.4144 23.4552L14.1833 23.363L13.5852 23.1568C13.5286 23.1364 13.4704 23.115 13.4107 23.0921L13.0358 22.9368C12.1233 22.5269 10.9592 21.7537 9.80437 19.9454L9.55714 19.5419C8.45335 17.6646 7.44724 16.6505 6.5388 16.4993C5.54785 18.802 5 21.337 5 24C5 34.4934 13.5066 43 24 43C29.9722 43 35.3009 40.2445 38.784 35.9356C37.478 34.5426 36.7461 31.3682 36.9998 28.6716C37.124 27.3509 36.0098 26.9278 34.6513 26.6513L34.0106 26.5314L33.0321 26.3548C31.4175 26.0379 29.9869 25.5319 30.2675 23.6835C30.8849 19.617 35.5543 17.8085 39.3254 19.046C40.8346 19.5412 41.8548 19.5905 42.3859 19.1938C41.8364 17.0826 40.9326 15.1153 39.7423 13.358C39.2904 13.7764 38.8462 13.6062 38.4081 12.8506C37.8428 11.8753 36.611 12.0718 34.7129 13.44C33.2207 14.8014 31.8882 15.1958 30.7153 14.6232C29.71 14.1325 29.7342 13.6238 29.9396 13.2779L30.0534 13.1193L30.1739 12.993L30.3495 12.8506L30.7415 12.7733L31.1343 12.6819C31.8442 12.5032 32.7409 12.1976 32.4746 11.8385C32.0723 11.2958 31.9318 9.41986 33.2186 9.2131L33.4607 9.19455C34.1147 9.19455 34.6057 8.95095 34.9339 8.46377C31.8424 6.28016 28.0707 5 24 5Z",
    fill: color
  }));
};
var IconEYFSEA = function IconEYFSEA(_ref60) {
  var _ref60$color = _ref60.color,
      color = _ref60$color === void 0 ? 'currentColor' : _ref60$color,
      _ref60$size = _ref60.size,
      size = _ref60$size === void 0 ? 20 : _ref60$size;
  return React__default.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M0.0954113 19.8833C0.441294 15.9423 1.7588 11.8374 3.63119 8.39155L4.04076 7.66355L3.98692 7.54549C6.65087 3.04119 10.268 0.403681 13.7776 0.0957603C20.218 -0.469293 24.5882 1.46632 26.8884 5.90259C27.4876 6.7383 27.8829 7.88372 28.2392 9.14909L28.9014 11.6159C29.5983 14.1306 30.5196 16.65 32.7511 17.9234C35.4996 19.4917 38.3637 19.3331 41.3435 17.4476L41.8145 17.1396L42.0837 17.015L42.3619 16.9128L42.7058 16.8112C43.7623 16.5374 45.472 16.438 46.6671 17.9234C48.9785 20.7962 48.1668 26.8452 45.1357 32.6975L44.8452 33.2454L44.9096 33.349L44.4727 34.1179C42.6655 37.2004 40.2686 40.1315 37.4414 42.3834L36.7813 42.8907L36.2746 43.2499L35.5372 43.7343L34.8062 44.181L34.2397 44.5072L33.6134 44.8492L32.7511 45.2896L31.8059 45.7334L30.994 46.0831C27.1053 47.689 21.7126 48.9273 16.178 47.0711L15.5388 46.8431L14.7069 46.5011L13.9896 46.1654L13.3172 45.8172C13.0808 45.6889 12.8333 45.5484 12.5779 45.3951C5.06158 40.883 -0.827248 30.397 0.0954113 19.8833ZM28.0129 29.0337C28.0129 31.7902 30.2525 34.0248 33.0152 34.0248C35.7779 34.0248 38.0175 31.7902 38.0175 29.0337C38.0175 26.2771 35.7779 24.0425 33.0152 24.0425C30.2525 24.0425 28.0129 26.2771 28.0129 29.0337ZM20.0092 37.0195C20.0092 38.6734 21.353 40.0142 23.0106 40.0142C24.6682 40.0142 26.012 38.6734 26.012 37.0195C26.012 35.3656 24.6682 34.0248 23.0106 34.0248C21.353 34.0248 20.0092 35.3656 20.0092 37.0195ZM12.0055 32.0284C12.0055 33.6823 13.3493 35.023 15.0069 35.023C16.6645 35.023 18.0083 33.6823 18.0083 32.0284C18.0083 30.3744 16.6645 29.0337 15.0069 29.0337C13.3493 29.0337 12.0055 30.3744 12.0055 32.0284ZM10.0046 13.062C10.0046 14.7159 11.3484 16.0567 13.006 16.0567C14.6636 16.0567 16.0074 14.7159 16.0074 13.062C16.0074 11.4081 14.6636 10.0673 13.006 10.0673C11.3484 10.0673 10.0046 11.4081 10.0046 13.062ZM8.00369 23.0443C8.00369 24.6982 9.34746 26.039 11.0051 26.039C12.6627 26.039 14.0065 24.6982 14.0065 23.0443C14.0065 21.3904 12.6627 20.0496 11.0051 20.0496C9.34746 20.0496 8.00369 21.3904 8.00369 23.0443Z",
    fill: color
  }));
};

var Icons = /*#__PURE__*/Object.freeze({
  __proto__: null,
  IconArrowLeft: IconArrowLeft,
  IconArrowRight: IconArrowRight,
  IconAssessment: IconAssessment,
  IconBadgeVerified: IconBadgeVerified,
  IconBell: IconBell,
  IconBook: IconBook,
  IconBookLight: IconBookLight,
  IconCalendar: IconCalendar,
  IconCheck: IconCheck,
  IconCheckSolo: IconCheckSolo,
  IconChevronDown: IconChevronDown,
  IconClock: IconClock,
  IconClose: IconClose,
  IconCurrencyPound: IconCurrencyPound,
  IconDoc: IconDoc,
  IconEYFSCL: IconEYFSCL,
  IconEYFSEA: IconEYFSEA,
  IconEYFSLD: IconBookLight,
  IconEYFSMD: IconEYFSMD,
  IconEYFSPD: IconEYFSPD,
  IconEYFSPSE: IconEYFSPSE,
  IconEYFSUW: IconEYFSUW,
  IconEducation: IconEducation,
  IconFacebook: IconFacebook,
  IconGrid: IconGrid,
  IconHamburger: IconHamburger,
  IconHeart: IconHeart,
  IconHide: IconHide,
  IconHome: IconHome,
  IconInstagram: IconInstagram,
  IconLinkedIn: IconLinkedIn,
  IconLocationPin: IconLocationPin,
  IconMail: IconMail,
  IconMeal: IconMeal,
  IconMoney: IconMoney,
  IconNappy: IconNappy,
  IconObservation: IconObservation,
  IconPencil: IconPencil,
  IconPhone: IconPhone,
  IconPlay: IconPlay,
  IconPlus: IconPlus,
  IconProfile: IconProfile,
  IconQuote: IconQuote,
  IconReward: IconReward,
  IconRocket: IconRocket,
  IconSearch: IconSearch,
  IconSearchClose: IconSearchClose,
  IconShow: IconShow,
  IconSleep: IconSleep,
  IconSnapshot: IconSnapshot,
  IconStar: IconStar,
  IconSuccess: IconSuccess,
  IconTiney: IconTiney,
  IconTips: IconTips,
  IconTwitter: IconTwitter,
  IconUser: IconUser,
  IconUserFamily: IconUserFamily,
  IconUsers: IconUsers,
  IconWalk: IconWalk,
  IconWallet: IconWallet,
  IconWarning: IconWarning
});

var TineyLogo = function TineyLogo() {
  return React__default.createElement("svg", {
    width: "115",
    height: "52",
    viewBox: "0 0 81 38",
    xmlns: "http://www.w3.org/2000/svg",
    xlinkHref: "http://www.w3.org/1999/xlink"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    id: "a",
    d: "M.035.089h4.6V4.63h-4.6z"
  }), React__default.createElement("path", {
    id: "c",
    d: "M0 .089h11.965v25.705H0z"
  }), React__default.createElement("path", {
    id: "e",
    d: "M.071.077h16.35v30.351H.07z"
  })), React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    fill: "#474775",
    d: "M15 26h5V7h-5z"
  }), React__default.createElement("g", {
    transform: "translate(15.182)"
  }, React__default.createElement("mask", {
    id: "b",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#a"
  })), React__default.createElement("path", {
    d: "M2.335 4.63c1.27 0 2.3-1.017 2.3-2.27 0-1.255-1.03-2.271-2.3-2.271-1.27 0-2.3 1.016-2.3 2.27s1.03 2.271 2.3 2.271",
    fill: "#FFB7A6",
    mask: "url(#b)"
  })), React__default.createElement("g", null, React__default.createElement("mask", {
    id: "d",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#c"
  })), React__default.createElement("path", {
    d: "M8.281 6.738V.088h-4.6v6.65H0v3.634h3.68v7.46c.005 4.39 3.72 7.962 8.285 7.962v-4.542c-2.031 0-3.684-1.537-3.684-3.427v-7.453h3.684V6.738H8.281z",
    fill: "#474775",
    mask: "url(#d)"
  })), React__default.createElement("path", {
    d: "M31.236 6.738c-1.772 0-3.411.56-4.751 1.51a4.615 4.615 0 0 0-3.424-1.51v19.056h4.6V14.808c0-1.945 1.604-3.528 3.575-3.528 1.97 0 3.574 1.583 3.574 3.528v10.986h4.6V14.808c0-4.45-3.667-8.07-8.174-8.07",
    fill: "#474775"
  }), React__default.createElement("g", {
    transform: "translate(63.95 6.66)"
  }, React__default.createElement("mask", {
    id: "f",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#e"
  })), React__default.createElement("path", {
    d: "M16.397 6.678v-6.6h-4.6v10.834c0 1.938-1.591 3.516-3.551 3.528-1.96-.012-3.55-1.59-3.55-3.528V.077H.093v10.835c0 4.434 3.642 8.045 8.128 8.07h.048a8.208 8.208 0 0 0 3.544-.822l.006 4.199c0 1.945-1.603 3.528-3.574 3.528-1.97 0-3.574-1.583-3.574-3.528H.07c0 4.45 3.667 8.07 8.175 8.07 4.508 0 8.175-3.62 8.175-8.07v-.004l-.024-15.677z",
    fill: "#474775",
    mask: "url(#f)"
  })), React__default.createElement("path", {
    d: "M47.19 14.3a4.906 4.906 0 0 1 4.54-3.02c2.05 0 3.81 1.25 4.538 3.02h-9.077zm13.886 3.632s.153-1.388.153-1.816a9.265 9.265 0 0 0-1.81-5.497 9.529 9.529 0 0 0-7.69-3.88c-4.608 0-8.459 3.256-9.319 7.56a9.265 9.265 0 0 0-.18 1.817c0 .655.153 1.816.153 1.816.017.102.036.206.056.306.86 4.305 4.71 7.561 9.32 7.561a9.576 9.576 0 0 0 6.616-2.657l-2.01-1.983-1.299-1.283a4.636 4.636 0 0 1-3.308 1.382c-2.05 0-3.97-1.238-4.693-3.326h14.01z",
    fill: "#474775"
  })));
};
var TineyLogoLight = function TineyLogoLight() {
  return React__default.createElement("svg", {
    width: "115",
    height: "52",
    viewBox: "0 0 81 38",
    xmlns: "http://www.w3.org/2000/svg",
    xlinkHref: "http://www.w3.org/1999/xlink"
  }, React__default.createElement("defs", null, React__default.createElement("path", {
    id: "a",
    d: "M.035.089h4.6V4.63h-4.6z"
  }), React__default.createElement("path", {
    id: "c",
    d: "M0 .089h11.965v25.705H0z"
  }), React__default.createElement("path", {
    id: "e",
    d: "M.071.077h16.35v30.351H.07z"
  })), React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    fill: "#fff",
    d: "M15 26h5V7h-5z"
  }), React__default.createElement("g", {
    transform: "translate(15.182)"
  }, React__default.createElement("mask", {
    id: "b",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#a"
  })), React__default.createElement("path", {
    d: "M2.335 4.63c1.27 0 2.3-1.017 2.3-2.27 0-1.255-1.03-2.271-2.3-2.271-1.27 0-2.3 1.016-2.3 2.27s1.03 2.271 2.3 2.271",
    fill: "#FFB7A6",
    mask: "url(#b)"
  })), React__default.createElement("g", null, React__default.createElement("mask", {
    id: "d",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#c"
  })), React__default.createElement("path", {
    d: "M8.281 6.738V.088h-4.6v6.65H0v3.634h3.68v7.46c.005 4.39 3.72 7.962 8.285 7.962v-4.542c-2.031 0-3.684-1.537-3.684-3.427v-7.453h3.684V6.738H8.281z",
    fill: "#fff",
    mask: "url(#d)"
  })), React__default.createElement("path", {
    d: "M31.236 6.738c-1.772 0-3.411.56-4.751 1.51a4.615 4.615 0 0 0-3.424-1.51v19.056h4.6V14.808c0-1.945 1.604-3.528 3.575-3.528 1.97 0 3.574 1.583 3.574 3.528v10.986h4.6V14.808c0-4.45-3.667-8.07-8.174-8.07",
    fill: "#fff"
  }), React__default.createElement("g", {
    transform: "translate(63.95 6.66)"
  }, React__default.createElement("mask", {
    id: "f",
    fill: "#fff"
  }, React__default.createElement("use", {
    xlinkHref: "#e"
  })), React__default.createElement("path", {
    d: "M16.397 6.678v-6.6h-4.6v10.834c0 1.938-1.591 3.516-3.551 3.528-1.96-.012-3.55-1.59-3.55-3.528V.077H.093v10.835c0 4.434 3.642 8.045 8.128 8.07h.048a8.208 8.208 0 0 0 3.544-.822l.006 4.199c0 1.945-1.603 3.528-3.574 3.528-1.97 0-3.574-1.583-3.574-3.528H.07c0 4.45 3.667 8.07 8.175 8.07 4.508 0 8.175-3.62 8.175-8.07v-.004l-.024-15.677z",
    fill: "#fff",
    mask: "url(#f)"
  })), React__default.createElement("path", {
    d: "M47.19 14.3a4.906 4.906 0 0 1 4.54-3.02c2.05 0 3.81 1.25 4.538 3.02h-9.077zm13.886 3.632s.153-1.388.153-1.816a9.265 9.265 0 0 0-1.81-5.497 9.529 9.529 0 0 0-7.69-3.88c-4.608 0-8.459 3.256-9.319 7.56a9.265 9.265 0 0 0-.18 1.817c0 .655.153 1.816.153 1.816.017.102.036.206.056.306.86 4.305 4.71 7.561 9.32 7.561a9.576 9.576 0 0 0 6.616-2.657l-2.01-1.983-1.299-1.283a4.636 4.636 0 0 1-3.308 1.382c-2.05 0-3.97-1.238-4.693-3.326h14.01z",
    fill: "#fff"
  })));
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _templateObject$u() {
  var data = _taggedTemplateLiteral(["\n  flex-grow: 1;\n\n  img {\n    max-width: 100%;\n  }\n\n  // Continer\n  ", "\n\n  // Item Container\n  ", "\n\n  // Item\n  ", "\n\n  // lg items\n  ", "\n\n  // md items\n  ", "\n\n  // sm items\n  ", "\n\n  // noPadding\n  ", "\n\n  // Mobile Order\n  ", "\n"]);

  _templateObject$u = function _templateObject() {
    return data;
  };

  return data;
}
var GUTTER_SIZE = 24;

var flexCalculator = function flexCalculator(unit) {
  return "width: ".concat(unit / 12 * 100, "%;");
};

var flexMdCalculator = function flexMdCalculator(unit) {
  return "\n  ".concat(mq.md("\n    width: ".concat(unit / 12 * 100, "%;\n  ")), "\n");
};

var flexSmCalculator = function flexSmCalculator(unit) {
  return "\n  ".concat(mq.sm("\n    width: ".concat(unit / 12 * 100, "%;\n  ")), "\n");
};

var GridUS = function GridUS(_ref) {
  var className = _ref.className,
      style = _ref.style,
      children = _ref.children;
      _ref.container;
      _ref.item;
      _ref.sm;
      _ref.md;
      _ref.lg;
  return React__default.createElement("div", {
    className: className,
    style: style
  }, children);
};

var Grid = styled(GridUS)(_templateObject$u(), function (props) {
  if (props.container) {
    return "\n        display: flex;\n        flex-wrap: wrap;\n        flex-direction: row !important;\n        transform: translateX(-".concat(GUTTER_SIZE / 2, "px);\n        width: calc(100% + ").concat(GUTTER_SIZE, "px);\n\n        ").concat(mq.lg("\n          transform: none;\n          width: 100%;\n        "), "\n      ");
  }
}, function (props) {
  if (props.itemContainer) {
    return "\n        flex-wrap: wrap;\n        flex-direction: row !important;\n      ";
  }
}, function (props) {
  if (props.item) {
    return "\n        justify-content: ".concat(props.flex || 'flex-start', ";\n        display: flex;\n        flex-direction: column;\n        padding: ").concat(GUTTER_SIZE / 2, "px;\n\n        ").concat(mq.lg("\n          padding: ".concat(GUTTER_SIZE / 4, "px;\n        ")), "\n      ");
  }
}, function (props) {
  if (props.item) {
    if (props.lg) {
      switch (props.lg) {
        case 1:
          return flexCalculator(1);

        case 2:
          return flexCalculator(2);

        case 3:
          return flexCalculator(3);

        case 4:
          return flexCalculator(4);

        case 5:
          return flexCalculator(5);

        case 6:
          return flexCalculator(6);

        case 7:
          return flexCalculator(7);

        case 8:
          return flexCalculator(8);

        case 9:
          return flexCalculator(9);

        case 10:
          return flexCalculator(10);

        case 11:
          return flexCalculator(11);

        default:
          return flexCalculator(12);
      }
    }
  }
}, function (props) {
  if (props.item) {
    if (props.md) {
      switch (props.md) {
        case 1:
          return flexMdCalculator(1);

        case 2:
          return flexMdCalculator(2);

        case 3:
          return flexMdCalculator(3);

        case 4:
          return flexMdCalculator(4);

        case 5:
          return flexMdCalculator(5);

        case 6:
          return flexMdCalculator(6);

        case 7:
          return flexMdCalculator(7);

        case 8:
          return flexMdCalculator(8);

        case 9:
          return flexMdCalculator(9);

        case 10:
          return flexMdCalculator(10);

        case 11:
          return flexMdCalculator(11);

        default:
          return flexMdCalculator(12);
      }
    }
  }
}, function (props) {
  if (props.item) {
    if (props.sm) {
      switch (props.sm) {
        case 1:
          return flexSmCalculator(1);

        case 2:
          return flexSmCalculator(2);

        case 3:
          return flexSmCalculator(3);

        case 4:
          return flexSmCalculator(4);

        case 5:
          return flexSmCalculator(5);

        case 6:
          return flexSmCalculator(6);

        case 7:
          return flexSmCalculator(7);

        case 8:
          return flexSmCalculator(8);

        case 9:
          return flexSmCalculator(9);

        case 10:
          return flexSmCalculator(10);

        case 11:
          return flexSmCalculator(11);

        default:
          return flexSmCalculator(12);
      }
    }
  }
}, function (props) {
  if (props.noPadding) {
    return "padding: 0 !important;";
  }
}, function (props) {
  if (props.mobileOrder) {
    return "\n        ".concat(mq.sm("\n          order: ".concat(props.mobileOrder, "\n        ")), "\n      ");
  }
});
/**
 * @component
 * Based heavily on the MUI grid. This is a simplified version with fixed gutters.
 * It will suffice for 99% of our use cases.
 */

var Grid$1 = (function (props) {
  return React__default.createElement(Grid, props);
});

function _templateObject$t() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  padding: ", ";\n  position: relative;\n  width: 100%;\n  z-index: 1;\n  background-opacity: ", ";\n\n  > svg {\n    position: absolute;\n    left: 0;\n    height: ", ";\n    width: 100%;\n    transform: ", ";\n    z-index: ", ";\n  }\n\n  .row {\n    margin: 0 auto;\n    max-width: ", ";\n    padding: 0 2rem;\n    position: relative;\n    width: 100%;\n    z-index: 2;\n  }\n\n  ", ";\n\n  ", ";\n\n  ", "\n\n  ", "\n\n  ", "\n\n  // Full Bleed\n  ", "\n"]);

  _templateObject$t = function _templateObject() {
    return data;
  };

  return data;
}

var Section = function Section(_ref) {
  var className = _ref.className,
      children = _ref.children,
      component = _ref.component,
      bg = _ref.bg,
      bg2 = _ref.bg2;
      _ref.bgLayout;
      _ref.bgLayout2;
      _ref.bgHeight;
      _ref.bgHeightSm;
      _ref.bgOpacity;
      _ref.color;
      _ref.padding;
      _ref.mobilePadding;
      _ref.tabletPadding;
      var style = _ref.style,
      outsideRow = _ref.outsideRow,
      rowWidth = _ref.rowWidth;
      _ref.svgTransform;
      var id = _ref.id;
  var SectionTag = component || 'section';
  return React__default.createElement(SectionTag, {
    className: className,
    style: style,
    id: id
  }, bg && bg(), bg2 && bg2(), React__default.createElement("div", {
    className: "row",
    rowWidth: rowWidth
  }, children), outsideRow && outsideRow);
};

var SectionStyled = styled(Section)(_templateObject$t(), function (props) {
  return props.color || 'transparent';
}, function (props) {
  return props.padding;
}, function (p) {
  return p.bgOpacity || 100;
}, function (props) {
  return "".concat(props.bgHeight, "px") || '100%';
}, function (props) {
  return props.svgTransform || 'none';
}, function (props) {
  return props.svgZIndex || 'none';
}, function (props) {
  return props.rowWidth ? "".concat(props.rowWidth, "px") : "".concat(props.theme.gridWidth, "px");
}, function (props) {
  switch (props.bgLayout) {
    case 'top':
      return "\n          > svg:first-child {\n            top: -1px;\n          }\n        ";

    case 'center':
      return "\n          z-index: 2;\n          > svg:first-child {\n            top: 50%;\n            transform: translateY(-50%);\n          }\n        ";

    case 'right':
      return "\n          > svg:first-child {\n            left: auto;\n            right: -2rem;\n            top: 10rem;\n            width: auto !important;\n          }\n        ";

    default:
      return "\n          > svg:first-child {\n            bottom: -1px;\n          }\n        ";
  }
}, function (props) {
  switch (props.bgLayout2) {
    case 'top':
      return "\n          > svg {\n            top: -1px;\n          }\n        ";

    case 'center':
      return "\n          z-index: 2;\n          > svg {\n            top: 50%;\n            transform: translateY(-50%);\n          }\n        ";

    case 'right':
      return "\n          > svg {\n            left: auto;\n            right: -2rem;\n            top: 10rem;\n            width: auto !important;\n          }\n        ";

    default:
      return "\n          > svg {\n            bottom: -1px;\n          }\n        ";
  }
}, function (props) {
  return mq.lg("\n      > svg {\n        height: ".concat(props.bgLayout === 'center' ? props.bgHeight : (props.bgHeight || 400) / 1.5, "px;\n        width: ").concat(props.bgLayout === 'center' ? '150%' : '100%', ";\n      }\n    "));
}, function (props) {
  return mq.md("\n      padding: ".concat(props.tabletPadding || props.mobilePadding, ";\n      > svg {\n        height: ").concat(props.bgHeightSm || (props.bgLayout === 'center' ? props.bgHeight : (props.bgHeight || 300) / 2), "px;\n        width: ").concat(props.bgLayout === 'center' ? '200%' : '100%', ";\n      }\n    "));
}, function (props) {
  return mq.sm("\n      padding: ".concat(props.mobilePadding, ";\n    "));
}, function (props) {
  if (props.fullBleed) {
    return "\n        .row {\n          max-width: none;\n          padding: 0;\n        }\n      ";
  }
});
/**
 * @component
 */

var Section$1 = (function (props) {
  return React__default.createElement(SectionStyled, props);
});

function _templateObject$s() {
  var data = _taggedTemplateLiteral(["\n  max-width: 85rem;\n\n  > * + * {\n    margin-top: 2rem;\n  }\n\n  h1,\n  h2 {\n    ", ";\n  }\n\n  h3,\n  h4 {\n    ", ";\n    margin-top: 3rem;\n  }\n\n  h5,\n  h6 {\n    ", ";\n  }\n\n  p,\n  li,\n  dt,\n  dd,\n  td {\n    ", ";\n    color: ", ";\n\n    a {\n      color: ", ";\n    }\n\n    strong {\n      font-weight: ", ";\n      color: ", ";\n    }\n\n    strong em {\n      text-decoration: underline;\n    }\n  }\n\n  ol {\n    list-style-type: lower-alpha;\n  }\n\n  ol li {\n    margin-left: 4rem;\n    position: relative;\n  }\n\n  ul li {\n    padding-left: 2rem;\n    position: relative;\n\n    &:before {\n      background-color: ", ";\n      border-radius: 50%;\n      content: '';\n      display: block;\n      height: 0.6rem;\n      position: absolute;\n      top: 0.9rem;\n      left: 0;\n      width: 0.6rem;\n    }\n  }\n\n  blockquote {\n    border-left: 3px solid ", ";\n    padding-left: 3rem;\n    margin: 5rem 0;\n    p {\n      ", ";\n    }\n  }\n\n  img {\n    width: 100%;\n  }\n\n  table {\n    th {\n      border: 1px solid ", ";\n      ", ";\n      text-align: left;\n      color: ", ";\n      padding: 1rem;\n\n      &:first-child {\n        width: 20%;\n      }\n    }\n\n    td {\n      border: 1px solid ", ";\n      ", ";\n      padding: 1rem;\n    }\n  }\n\n  ", "\n\n  // Featured Paragraph\n  ", "\n"]);

  _templateObject$s = function _templateObject() {
    return data;
  };

  return data;
}

var Article = function Article(_ref) {
  var className = _ref.className,
      children = _ref.children,
      dangerouslySetInnerHTML = _ref.dangerouslySetInnerHTML,
      style = _ref.style;

  if (children) {
    return React__default.createElement("article", {
      className: className,
      style: style
    }, children);
  }

  return React__default.createElement("article", {
    className: className,
    dangerouslySetInnerHTML: {
      __html: dangerouslySetInnerHTML.__html
    },
    style: style
  });
};
/**
 * @component
 */


var Article$1 = styled(Article)(_templateObject$s(), function (props) {
  return props.theme.text.titleLarge;
}, function (props) {
  return props.theme.text.title3;
}, function (props) {
  return props.theme.text.title4;
}, function (props) {
  return props.theme.text.body;
}, function (props) {
  return props.theme.colors.purpleDark;
}, function (props) {
  return props.theme.colors.pinkDark;
}, function (props) {
  return props.theme.fontWeight.bold;
}, function (props) {
  return props.theme.colors.purpleDark;
}, function (props) {
  return props.theme.colors.purpleDark;
}, function (props) {
  return props.theme.colors.purpleDark;
}, function (props) {
  return props.theme.text.title2;
}, function (props) {
  return props.theme.colors.greyLight;
}, function (props) {
  return props.theme.text.body;
}, function (props) {
  return props.theme.colors.purpleDark;
}, function (props) {
  return props.theme.colors.greyLight;
}, function (props) {
  return props.theme.text.body;
}, function (props) {
  return "\n    ".concat(mq.sm("\n      th, td {\n        display: block;\n        width: 100% !important;\n      }\n\n      td:first-child {\n        border-top: 0;\n      }\n\n      tr {\n        border: 1px solid ".concat(props.theme.colors.purpleDark, ";\n      }\n    ")), "\n  ");
}, function (props) {
  if (props.feature) {
    return "\n        p:first-child {\n          ".concat(props.theme.text.title1, "\n        }\n\n        ").concat(mq.sm("\n          p:first-child {\n            ".concat(props.theme.text.title3, "\n          }\n        ")), "\n      ");
  }
});

function _templateObject$r() {
  var data = _taggedTemplateLiteral(["\n  ", "\n\n  // Override Color\n  ", "\n\n  // Add space above\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n\n  // Center Text\n  ", "\n\n  span {\n    color: ", ";\n  }\n"]);

  _templateObject$r = function _templateObject() {
    return data;
  };

  return data;
}

var HeadingDumb = function HeadingDumb(_ref) {
  var className = _ref.className,
      style = _ref.style,
      children = _ref.children,
      _ref$priority = _ref.priority,
      priority = _ref$priority === void 0 ? 2 : _ref$priority,
      props = _objectWithoutProperties(_ref, ["className", "style", "children", "priority"]);

  var HeadingTag = "h".concat(priority);
  return React.createElement(HeadingTag, _extends({
    style: style,
    className: className
  }, props), children);
};
/** @component */


var Heading = styled(HeadingDumb)(_templateObject$r(), function (props) {
  switch (props.variant) {
    case 'titleXL':
      return props.theme.text.titleXL;

    case 'titleXLBold':
      return props.theme.text.titleXLBold;

    case 'title1':
      return props.theme.text.title1;

    case 'title1Bold':
      return props.theme.text.title1Bold;

    case 'title2':
      return props.theme.text.title2;

    case 'title3':
      return props.theme.text.title3;

    case 'title4':
      return props.theme.text.title4;

    case 'titleLarge':
    default:
      return props.theme.text.titleLarge;
  }
}, function (props) {
  if (props.color) {
    return "color: ".concat(props.color, " !important;");
  }
}, function (props) {
  if (props.spacerTop) {
    return "margin-top: .5rem !important;";
  }
}, function (props) {
  if (props.spacerTop2) {
    return "margin-top: 2rem !important;";
  }
}, function (props) {
  if (props.spacerBottom) {
    return "margin-bottom: 1rem !important;";
  }
}, function (props) {
  if (props.spacerBottom2) {
    return "margin-bottom: 2rem !important;";
  }
}, function (props) {
  if (props.centered) {
    return "text-align: center !important;";
  }
}, function (p) {
  return p.theme.colors.pinkDark;
});
/**
 * @component
 */

var Heading$1 = (function (props) {
  return React.createElement(Heading, props);
});

function _templateObject$q() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  border-radius: 1.5rem;\n  background-color: ", ";\n  border: 0;\n  color: ", ";\n  cursor: pointer;\n  display: inline-block;\n  padding: 1.8rem 2rem;\n  position: relative;\n  text-align: center;\n  text-decoration: none;\n  /* transition: background 0.25s ease-out; */\n  z-index: 100;\n\n  &:focus {\n    outline: 0;\n  }\n\n  &:hover {\n    background-color: ", ";\n  }\n\n  &:active {\n    transform: translateY(-1px);\n  }\n\n  ", "\n\n  ", "\n\n  // Bordered\n  &&.bordered {\n    margin: 0;\n\n    &.active,\n    &:hover {\n      border: 0;\n    }\n\n    &:active {\n      transform: translateY(0);\n    }\n  }\n\n  \n\n  // Light Version\n  ", "\n\n  // Link version\n  ", "\n\n  ", "\n\n  // Disabled\n  ", "\n\n  // Spacer\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n"]);

  _templateObject$q = function _templateObject() {
    return data;
  };

  return data;
}

var Link = function Link(props) {
  return React.createElement("a", _extends({}, props, {
    href: props.to
  }), props.children);
};

var isExternal = function isExternal(link) {
  return link.slug.indexOf('http') > -1 || link.slug.indexOf('mailto') > -1;
};

var Button = function Button(_ref) {
  var className = _ref.className,
      type = _ref.type,
      children = _ref.children,
      link = _ref.link,
      linkDecorator = _ref.linkDecorator,
      onClick = _ref.onClick,
      icon = _ref.icon;

  switch (type) {
    case 'link':
      return onClick ? React.createElement(Link, {
        className: className,
        onClick: onClick,
        to: "#",
        icon: icon
      }, icon && icon, children) : isExternal(link) ? React.createElement("a", {
        href: link.slug,
        className: className,
        target: "_blank",
        rel: "noopener noreferrer",
        icon: icon
      }, icon && icon, children, linkDecorator && React.createElement(IconArrowRight, null)) : React.createElement(Link, {
        className: "".concat(className),
        to: link.slug,
        target: link.target || '_self',
        rel: link.target === '_blank' ? 'noopener noreferrer' : '',
        icon: icon
      }, icon && icon, children, linkDecorator && React.createElement(IconArrowRight, null));

    case 'submit':
      return React.createElement("button", {
        type: "submit",
        className: className,
        icon: icon
      }, icon && icon, children);

    default:
      return React.createElement("button", {
        type: "button",
        className: className,
        onClick: onClick,
        icon: icon
      }, icon && icon, children);
  }
};

var StyledButton = styled(Button)(_templateObject$q(), function (props) {
  return props.theme.text.button;
}, function (props) {
  return props.theme.colors.pink;
}, function (props) {
  return props.theme.colors.white;
}, function (props) {
  return props.theme.colors.pinkDark;
}, mq.md("\n    margin-right: 0;\n  "), mq.sm("\n    width: 100%;\n  "), function (props) {
  if (props.light) {
    return "\n        background: ".concat(props.theme.colors.white, ";\n        border: 3px solid ").concat(props.theme.colors.pinkDark, ";\n        color: ").concat(props.theme.colors.pinkDark, ";\n\n        &:before {\n          display: none;\n        }\n\n        &:hover {\n          background: ").concat(props.theme.colors.pinkLightest, "; \n        }\n\n        &:active {\n          background: ").concat(props.theme.colors.pinkLight, "; \n        }\n      ");
  }
}, function (props) {
  if (props.type === 'link' && !props.forceButton) {
    return "\n        background: transparent;\n        border: 0;\n        color: ".concat(props.alt ? props.theme.colors.white : props.theme.colors.pinkDark, ";\n        min-width: 0;\n        margin-right: 2rem;\n        padding-left: 0;\n        padding-right: 0;\n        vertical-align: top;\n\n        svg {\n          margin-left: 0.7rem;\n          vertical-align: top;\n          position: relative;\n          top: 0.2rem;\n        }\n\n        &:before {\n          display: none;\n        }\n\n        &:hover {\n          background: transparent;\n          color: ").concat(props.alt ? props.theme.colors.pinkDark : props.theme.colors.purpleDark, ";\n\n          &:after {\n            content: '';\n            border-top: 4px solid ").concat(props.theme.colors.purpleDark, ";\n            display: ").concat(props.alt ? 'none' : 'block', ";\n            position: absolute;\n            bottom: -5px;\n            height: 4px;\n            width: 100%;\n          }\n        }\n\n        &.active {\n          background: transparent;\n          color: ").concat(props.theme.colors.purpleDark, ";\n\n          &:after {\n            content: '';\n            border-top: 4px solid ").concat(props.theme.colors.purpleDark, ";\n            display: block;\n            position: absolute;\n            bottom: -5px;\n            height: 4px;\n            width: 100%;\n          }\n        }\n\n        &:active {\n          background: transparent;\n        }\n      ");
  }
}, function (p) {
  if (p.icon) {
    return "\n        padding-left: 5rem;\n\n        svg {\n          position: absolute;\n          top: 50%;\n          transform: translateY(-50%);\n          left: 1.5rem;\n        }\n      ";
  }
}, function (props) {
  if (props.disabled) {
    return "\n        cursor: not-allowed;\n        pointer-events: none;\n        background: ".concat(props.theme.colors.greyLightest, ";\n        border: 0;\n        color: ").concat(props.theme.colors.greyIcon, ";\n        \n        &:hover,\n        &:active {\n          background-color: ").concat(colors.greyMid, "!important;\n          cursor: not-allowed;\n          pointer-events: none;\n        }\n        \n        &:before {\n          background-color: ").concat(colors.greyMid, "!important;\n          cursor: not-allowed;\n          pointer-events: none;\n        }\n      ");
  }
}, function (props) {
  if (props.spacerTop) {
    return "margin-top: .5rem !important;";
  }
}, function (props) {
  if (props.spacerTop2) {
    return "margin-top: 2rem !important;";
  }
}, function (props) {
  if (props.spacerBottom) {
    return "margin-bottom: 1rem !important;";
  }
}, function (props) {
  if (props.spacerBottom2) {
    return "margin-bottom: 2rem !important;";
  }
});
var Button$1 = (function (props) {
  return React.createElement(StyledButton, props);
});

function _templateObject5$5() {
  var data = _taggedTemplateLiteral(["\n          text-align: initial !important;\n        "]);

  _templateObject5$5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$6() {
  var data = _taggedTemplateLiteral(["\n          h2 {\n            font-size: 3.0rem;\n            line-height: 3.6rem;\n          }\n        "]);

  _templateObject4$6 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$b() {
  var data = _taggedTemplateLiteral(["\n          h2 {\n            font-size: 3.4rem;\n            line-height: 4.0rem;\n          }\n        "]);

  _templateObject3$b = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$f() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  text-align: ", ";\n\n  ", "\n\n  ", "\n\n  .section-intro__mark {\n    margin-bottom: 2rem;\n  }\n\n  .section-intro__subline,\n  .section-intro__link {\n    margin-top: 2rem;\n  }\n\n  .section-intro__list {\n    margin-top: 3rem;\n  }\n\n  .section-intro__link a:after {\n    border-top: none;\n  }\n\n  .section-intro__subline p {\n    font-weight: 700;\n  }\n\n  article {\n    margin-top: 2rem;\n    max-width: 60rem;\n  }\n\n  // Centered\n  ", "\n\n  // Light\n  ", "\n\n  ", "\n"]);

  _templateObject2$f = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$p() {
  var data = _taggedTemplateLiteral(["\n  font-weight: 800;\n"]);

  _templateObject$p = function _templateObject() {
    return data;
  };

  return data;
}

var SectionIntro = function SectionIntro(_ref) {
  var className = _ref.className,
      mark = _ref.mark,
      priority = _ref.priority,
      title = _ref.title,
      titleStyle = _ref.titleStyle,
      content = _ref.content,
      link = _ref.link;
      _ref.centered;
      var _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'titleLarge' : _ref$variant,
      style = _ref.style,
      list = _ref.list,
      light = _ref.light,
      large = _ref.large,
      cta = _ref.cta,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["className", "mark", "priority", "title", "titleStyle", "content", "link", "centered", "variant", "style", "list", "light", "large", "cta", "children"]);

  var headingStyle = large ? {
    fontWeight: 400,
    marginBottom: '2rem'
  } : {};
  return React__default.createElement("div", _extends({
    className: className,
    style: style
  }, rest), mark && React__default.createElement("div", {
    className: "section-intro__mark"
  }, mark), title && React__default.createElement("div", {
    className: "section-intro__title",
    style: titleStyle
  }, React__default.createElement(SectionHeading, {
    priority: priority,
    variant: variant,
    style: headingStyle
  }, title)), children && children, content ? large ? React__default.createElement(SectionHeading, {
    variant: "title1"
  }, content) : React__default.createElement(Article$1, {
    dangerouslySetInnerHTML: {
      __html: content
    }
  }) : null, list && React__default.createElement("div", {
    className: "section-intro__list"
  }, list), link && React__default.createElement("div", {
    className: "section-intro__link"
  }, React__default.createElement(Button$1, {
    type: "link",
    link: link,
    forceButton: true
  }, link.text)), cta && React__default.createElement("div", {
    className: "section-intro__link"
  }, React__default.createElement(Button$1, {
    onClick: cta.action,
    light: light
  }, cta.text)));
};

var SectionHeading = styled(Heading$1)(_templateObject$p());
var SectionIntroStyled = styled(SectionIntro)(_templateObject2$f(), function (p) {
  return p.theme.layoutable;
}, function (props) {
  return props.centered ? 'center' : 'initial';
}, function (props) {
  if (!props.noMax) {
    return "\n        .section-intro__title {\n          max-width: 60rem;\n        }\n      ";
  }
}, function (props) {
  if (props.large) {
    return "\n        .section-intro__title {\n          max-width: 87rem;\n        }\n\n        ".concat(mq.md(_templateObject3$b()), "\n\n        ").concat(mq.sm(_templateObject4$6()), "\n      ");
  }
}, function (p) {
  if (p.centered) {
    return "\n        text-align: center;\n\n        .section-intro__title {\n          margin-left: auto;\n          margin-right: auto;\n        }\n\n        article {\n          margin: 2rem auto 0;\n        }\n      ";
  }
}, function (props) {
  if (props.light) {
    return "\n        .section-intro__title h2, \n        .section-intro__title h3 {\n          color: ".concat(props.theme.colors.white, ";\n        }\n\n        article p,\n        article a {\n          color: ").concat(props.theme.colors.white, ";\n          ").concat(props.theme.fontSmoothing, ";\n        }\n      ");
  }
}, function (p) {
  if (p.mobileLeft) {
    return "\n        ".concat(mq.sm(_templateObject5$5()), "\n      ");
  }
});
/**
 * @component
 */

var SectionIntro$1 = (function (props) {
  return React__default.createElement(SectionIntroStyled, props);
});

function _templateObject3$a() {
  var data = _taggedTemplateLiteral(["\n          margin-bottom: 1rem !important;\n        "]);

  _templateObject3$a = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$e() {
  var data = _taggedTemplateLiteral(["\n          margin-top: 1rem !important;\n        "]);

  _templateObject2$e = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$o() {
  var data = _taggedTemplateLiteral(["\n  text-decoration: none;\n\n  ", "\n\n  // Color override\n  ", "\n\n  // Spacing Top\n  ", "\n\n  // Spacing\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n\n  a {\n    text-decoration: none;\n    color: inherit;\n    cursor: pointer;\n  }\n\n  span,\n  em {\n    color: ", ";\n  }\n\n  // Center Text\n  ", "\n"]);

  _templateObject$o = function _templateObject() {
    return data;
  };

  return data;
}

var TextDumb = function TextDumb(_ref) {
  var className = _ref.className,
      children = _ref.children;
      _ref.variant;
      var _ref$component = _ref.component,
      component = _ref$component === void 0 ? 'p' : _ref$component,
      props = _objectWithoutProperties(_ref, ["className", "children", "variant", "component"]);

  var TextTag = "".concat(component);
  return React.createElement(TextTag, _extends({
    className: className
  }, props), children);
};
/** @component */


var Text$1 = styled(TextDumb)(_templateObject$o(), function (props) {
  switch (props.variant) {
    case 'headline':
      return props.theme.text.headline;

    case 'caption1':
      return props.theme.text.caption1;

    case 'caption1Bold':
      return props.theme.text.caption1Bold;

    case 'caption2':
      return props.theme.text.caption2;

    case 'button':
      return props.theme.text.button;

    case 'footnote':
      return props.theme.text.footnote;

    default:
      return props.theme.text.body;
  }
}, function (props) {
  if (props.color) {
    return "color: ".concat(props.color, " !important;");
  }
}, function (props) {
  if (props.spacerTop) {
    return "\n        margin-top: 2rem !important;\n\n        ".concat(mq.sm(_templateObject2$e()), "\n      ");
  }
}, function (props) {
  if (props.spacerTop) {
    return "margin-top: .5rem !important;";
  }
}, function (props) {
  if (props.spacerTop2) {
    return "margin-top: 2rem !important;";
  }
}, function (props) {
  if (props.spacerBottom) {
    return "margin-bottom: 1rem !important;";
  }
}, function (props) {
  if (props.spacerBottom2) {
    return "\n        margin-bottom: 2rem !important;\n        \n        ".concat(mq.sm(_templateObject3$a()), "\n      ");
  }
}, function (props) {
  return props.theme.colors.pinkDark;
}, function (props) {
  if (props.centered) {
    return "text-align: center !important;";
  }
});
/**
 * @component
 */

var Text$2 = (function (props) {
  return React.createElement(Text$1, props);
});

function _templateObject$n() {
  var data = _taggedTemplateLiteral(["\n  align-items: center;\n  display: flex;\n  position: relative;\n\n  .mark-logo {\n    svg {\n      margin-left: 0.5rem;\n      height: 2.1rem;\n      width: 4.3rem;\n      position: relative;\n      top: 0.27rem;\n    }\n  }\n\n  .mark-badge {\n    svg {\n      color: ", ";\n      margin-left: 0.7rem;\n    }\n  }\n\n  ", ";\n"]);

  _templateObject$n = function _templateObject() {
    return data;
  };

  return data;
}

var TrustedMarkUS = function TrustedMarkUS(_ref) {
  var className = _ref.className,
      _ref$text = _ref.text,
      text = _ref$text === void 0 ? 'Trusted by' : _ref$text,
      large = _ref.large,
      alt = _ref.alt;
  return React__default.createElement("div", {
    className: className
  }, large ? React__default.createElement(Heading$1, {
    color: alt ? colors.white : null,
    variant: "title1"
  }, text) : React__default.createElement(Text$2, {
    color: alt ? colors.white : null,
    variant: "caption1"
  }, text), React__default.createElement("div", {
    className: "mark-logo"
  }, alt ? React__default.createElement(TineyLogoLight, null) : React__default.createElement(TineyLogo, null)), React__default.createElement("div", {
    className: "mark-badge"
  }, React__default.createElement(IconBadgeVerified, null)));
};

var TrustedMark = styled(TrustedMarkUS)(_templateObject$n(), function (props) {
  return props.theme.colors.purpleBright;
}, function (props) {
  if (props.large) {
    return "\n        .mark-logo {\n          svg {\n            margin-left: 0.8rem;\n            height: 5rem;\n            width: 9rem;\n            top: 0.75rem;\n          }\n        }\n\n        .mark-badge svg {\n          height: 4rem;\n          margin-left: 1rem;\n          width: 4rem;\n        }\n\n        ".concat(mq.sm("\n          .mark-logo {\n            display: block;\n          }\n        "), "\n      ");
  }
});
/** @component */

var TrustedMark$1 = (function (props) {
  return React__default.createElement(TrustedMark, props);
});

function _templateObject$m() {
  var data = _taggedTemplateLiteral(["\n  background: ", ";\n  content: '';\n  width: ", ";\n  height: ", ";\n  border-radius: 50%;\n  position: absolute;\n  top: ", ";\n  right: ", ";\n\n  ", "\n\n  ", "\n\n  ", "\n"]);

  _templateObject$m = function _templateObject() {
    return data;
  };

  return data;
}
var FooterBubble = styled.div(_templateObject$m(), function (p) {
  return p.theme.colors.pinkLight;
}, function (props) {
  return props.small ? '2rem' : '5rem';
}, function (props) {
  return props.small ? '2rem' : '5rem';
}, function (props) {
  return props.small ? '-14rem' : '-11.5rem';
}, function (props) {
  return props.small ? '-3rem' : '0rem';
}, function (props) {
  return "\n    ".concat(mq.lg("\n      top: ".concat(props.small ? '-14rem' : '-11.5rem', ";\n      right: ").concat(props.small ? '1rem' : '4rem', ";\n    ")), "\n  ");
}, function (props) {
  return "\n    ".concat(mq.md("\n      top: ".concat(props.small ? '-8rem' : '-5.5rem', ";\n    ")), "\n  ");
}, function (props) {
  return "\n    ".concat(mq.sm("\n      top: ".concat(props.small ? '-7.5rem' : '-5.5rem', ";\n    ")), "\n  ");
});

function _templateObject3$9() {
  var data = _taggedTemplateLiteral(["\n  > svg {\n    width: 2.5rem;\n    height: 2.5rem;\n  }\n"]);

  _templateObject3$9 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$d() {
  var data = _taggedTemplateLiteral(["\n    justify-content: center;\n\n    a {\n      margin: 0 1rem;\n    }\n  "]);

  _templateObject2$d = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$l() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n\n  a {\n    color: ", ";\n    margin-right: 2rem;\n  }\n\n  a {\n    &:hover {\n      color: ", ";\n    }\n  }\n\n  ", "\n"]);

  _templateObject$l = function _templateObject() {
    return data;
  };

  return data;
}
var iconMap = {
  twitter: IconTwitter,
  instagram: IconInstagram,
  facebook: IconFacebook
  /**
   * Fine to hardcode here as the truth
   */

};
var socialLinks = [{
  kind: 'facebook',
  slug: 'https://www.facebook.com/tiney.co/'
}, {
  kind: 'twitter',
  slug: 'https://twitter.com/tineyco'
}, {
  kind: 'instagram',
  slug: 'https://instagram.com/tineyco'
}];
function SocialRow () {
  return React__default.createElement(SocialRow$1, null, socialLinks.map(function (link, i) {
    var IconContent = iconMap[link.kind];
    return React__default.createElement("a", {
      href: link.slug,
      key: i,
      target: "_blank",
      rel: "noopener noreferrer"
    }, React__default.createElement(Icon, null, React__default.createElement(IconContent, null)));
  }));
}
var SocialRow$1 = styled.div(_templateObject$l(), function (p) {
  return p.theme.colors.white;
}, function (p) {
  return p.theme.colors.pinkDark;
}, mq.md(_templateObject2$d()));
var Icon = styled.div(_templateObject3$9());

function _templateObject$k() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  border-radius: 50%;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: relative;\n  height: ", ";\n  width: ", ";\n\n  &:hover {\n    background-color: ", ";\n  }\n"]);

  _templateObject$k = function _templateObject() {
    return data;
  };

  return data;
}

var IconButton = function IconButton(_ref) {
  var className = _ref.className,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'play' : _ref$type,
      _ref$iconColor = _ref.iconColor,
      iconColor = _ref$iconColor === void 0 ? 'white' : _ref$iconColor;
  return React__default.createElement("div", {
    className: className
  }, getIconComponent(type, iconColor));
};

var getIconComponent = function getIconComponent(type, iconColor) {
  switch (type) {
    case 'play':
      return React__default.createElement(IconPlay, {
        color: iconColor
      });

    case 'arrow':
      return React__default.createElement(IconArrowRight, {
        color: iconColor
      });
  }
};
/**
 * @component
 */


var IconButton$1 = styled(IconButton)(_templateObject$k(), function (props) {
  return props.theme.colors[props.color || 'pink'];
}, function (props) {
  return props.small ? '6rem' : '7rem';
}, function (props) {
  return props.small ? '6rem' : '7rem';
}, function (props) {
  return props.theme.colors['pinkDark'];
});

var TWForm =
/*#__PURE__*/
function (_React$Component) {
  _inherits(TWForm, _React$Component);

  function TWForm(props) {
    var _this;

    _classCallCheck(this, TWForm);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TWForm).call(this, props));
    _this.state = {
      hasError: false,
      error: null,
      hasSubmitted: false
    };
    return _this;
  }

  _createClass(TWForm, [{
    key: "componentDidCatch",
    value: function componentDidCatch(error, errInfo) {
      var onError = this.props.onError; // Display fallback UI

      this.setState({
        hasError: true,
        error: error,
        hasSubmitted: true
      });
      onError && onError(error, errInfo);
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props = this.props,
          className = _this$props.className,
          _render = _this$props.render,
          header = _this$props.header,
          onBlur = _this$props.onBlur,
          onChange = _this$props.onChange,
          _onSubmit = _this$props.onSubmit,
          submitCount = _this$props.submitCount,
          rest = _objectWithoutProperties(_this$props, ["className", "render", "header", "onBlur", "onChange", "onSubmit", "submitCount"]);

      var _this$state = this.state,
          hasError = _this$state.hasError,
          error = _this$state.error,
          hasSubmitted = _this$state.hasSubmitted;

      if (hasError) {
        return React.createElement("div", {
          style: {
            backgroundColor: '#f37283',
            color: '#fff',
            display: 'flex',
            padding: '20px',
            fontSize: '2rem',
            fontFamily: '"Muli", sans-serif',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            height: '100%',
            minHeight: '100px'
          }
        }, React.createElement("p", null, error.message));
      } else {
        return React.createElement(Formik, _extends({
          validateOnChange: hasSubmitted || submitCount > 0,
          validateOnBlur: hasSubmitted || submitCount > 0,
          onSubmit: function onSubmit(values, actions) {
            _this2.setState({
              hasSubmitted: true
            });

            _onSubmit(values, actions);
          }
        }, rest, {
          enableReinitialize: true,
          render: function render(formikProps) {
            return React.createElement(Form, {
              className: className,
              noValidate: true,
              onBlur: onBlur,
              onChange: onChange,
              onSubmit: function onSubmit(values, bag) {
                _this2.setState({
                  hasSubmitted: true
                });

                formikProps.handleSubmit(values, bag);
              }
            }, header, _render(formikProps));
          }
        }));
      }
    }
  }]);

  return TWForm;
}(React.Component);

var commonjsGlobal = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var objectPath = createCommonjsModule(function (module) {
(function (root, factory) {
  /*istanbul ignore next:cant test*/

  {
    module.exports = factory();
  }
})(commonjsGlobal, function () {

  var toStr = Object.prototype.toString;

  function hasOwnProperty(obj, prop) {
    if (obj == null) {
      return false;
    } //to handle objects with null prototypes (too edge case?)


    return Object.prototype.hasOwnProperty.call(obj, prop);
  }

  function isEmpty(value) {
    if (!value) {
      return true;
    }

    if (isArray(value) && value.length === 0) {
      return true;
    } else if (typeof value !== 'string') {
      for (var i in value) {
        if (hasOwnProperty(value, i)) {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  function toString(type) {
    return toStr.call(type);
  }

  function isObject(obj) {
    return typeof obj === 'object' && toString(obj) === "[object Object]";
  }

  var isArray = Array.isArray || function (obj) {
    /*istanbul ignore next:cant test*/
    return toStr.call(obj) === '[object Array]';
  };

  function isBoolean(obj) {
    return typeof obj === 'boolean' || toString(obj) === '[object Boolean]';
  }

  function getKey(key) {
    var intKey = parseInt(key);

    if (intKey.toString() === key) {
      return intKey;
    }

    return key;
  }

  function factory(options) {
    options = options || {};

    var objectPath = function (obj) {
      return Object.keys(objectPath).reduce(function (proxy, prop) {
        if (prop === 'create') {
          return proxy;
        }
        /*istanbul ignore else*/


        if (typeof objectPath[prop] === 'function') {
          proxy[prop] = objectPath[prop].bind(objectPath, obj);
        }

        return proxy;
      }, {});
    };

    function hasShallowProperty(obj, prop) {
      return options.includeInheritedProps || typeof prop === 'number' && Array.isArray(obj) || hasOwnProperty(obj, prop);
    }

    function getShallowProperty(obj, prop) {
      if (hasShallowProperty(obj, prop)) {
        return obj[prop];
      }
    }

    function set(obj, path, value, doNotReplace) {
      if (typeof path === 'number') {
        path = [path];
      }

      if (!path || path.length === 0) {
        return obj;
      }

      if (typeof path === 'string') {
        return set(obj, path.split('.').map(getKey), value, doNotReplace);
      }

      var currentPath = path[0];
      var currentValue = getShallowProperty(obj, currentPath);

      if (path.length === 1) {
        if (currentValue === void 0 || !doNotReplace) {
          obj[currentPath] = value;
        }

        return currentValue;
      }

      if (currentValue === void 0) {
        //check if we assume an array
        if (typeof path[1] === 'number') {
          obj[currentPath] = [];
        } else {
          obj[currentPath] = {};
        }
      }

      return set(obj[currentPath], path.slice(1), value, doNotReplace);
    }

    objectPath.has = function (obj, path) {
      if (typeof path === 'number') {
        path = [path];
      } else if (typeof path === 'string') {
        path = path.split('.');
      }

      if (!path || path.length === 0) {
        return !!obj;
      }

      for (var i = 0; i < path.length; i++) {
        var j = getKey(path[i]);

        if (typeof j === 'number' && isArray(obj) && j < obj.length || (options.includeInheritedProps ? j in Object(obj) : hasOwnProperty(obj, j))) {
          obj = obj[j];
        } else {
          return false;
        }
      }

      return true;
    };

    objectPath.ensureExists = function (obj, path, value) {
      return set(obj, path, value, true);
    };

    objectPath.set = function (obj, path, value, doNotReplace) {
      return set(obj, path, value, doNotReplace);
    };

    objectPath.insert = function (obj, path, value, at) {
      var arr = objectPath.get(obj, path);
      at = ~~at;

      if (!isArray(arr)) {
        arr = [];
        objectPath.set(obj, path, arr);
      }

      arr.splice(at, 0, value);
    };

    objectPath.empty = function (obj, path) {
      if (isEmpty(path)) {
        return void 0;
      }

      if (obj == null) {
        return void 0;
      }

      var value, i;

      if (!(value = objectPath.get(obj, path))) {
        return void 0;
      }

      if (typeof value === 'string') {
        return objectPath.set(obj, path, '');
      } else if (isBoolean(value)) {
        return objectPath.set(obj, path, false);
      } else if (typeof value === 'number') {
        return objectPath.set(obj, path, 0);
      } else if (isArray(value)) {
        value.length = 0;
      } else if (isObject(value)) {
        for (i in value) {
          if (hasShallowProperty(value, i)) {
            delete value[i];
          }
        }
      } else {
        return objectPath.set(obj, path, null);
      }
    };

    objectPath.push = function (obj, path
    /*, values */
    ) {
      var arr = objectPath.get(obj, path);

      if (!isArray(arr)) {
        arr = [];
        objectPath.set(obj, path, arr);
      }

      arr.push.apply(arr, Array.prototype.slice.call(arguments, 2));
    };

    objectPath.coalesce = function (obj, paths, defaultValue) {
      var value;

      for (var i = 0, len = paths.length; i < len; i++) {
        if ((value = objectPath.get(obj, paths[i])) !== void 0) {
          return value;
        }
      }

      return defaultValue;
    };

    objectPath.get = function (obj, path, defaultValue) {
      if (typeof path === 'number') {
        path = [path];
      }

      if (!path || path.length === 0) {
        return obj;
      }

      if (obj == null) {
        return defaultValue;
      }

      if (typeof path === 'string') {
        return objectPath.get(obj, path.split('.'), defaultValue);
      }

      var currentPath = getKey(path[0]);
      var nextObj = getShallowProperty(obj, currentPath);

      if (nextObj === void 0) {
        return defaultValue;
      }

      if (path.length === 1) {
        return nextObj;
      }

      return objectPath.get(obj[currentPath], path.slice(1), defaultValue);
    };

    objectPath.del = function del(obj, path) {
      if (typeof path === 'number') {
        path = [path];
      }

      if (obj == null) {
        return obj;
      }

      if (isEmpty(path)) {
        return obj;
      }

      if (typeof path === 'string') {
        return objectPath.del(obj, path.split('.'));
      }

      var currentPath = getKey(path[0]);

      if (!hasShallowProperty(obj, currentPath)) {
        return obj;
      }

      if (path.length === 1) {
        if (isArray(obj)) {
          obj.splice(currentPath, 1);
        } else {
          delete obj[currentPath];
        }
      } else {
        return objectPath.del(obj[currentPath], path.slice(1));
      }

      return obj;
    };

    return objectPath;
  }

  var mod = factory();
  mod.create = factory;
  mod.withInheritedProps = factory({
    includeInheritedProps: true
  });
  return mod;
});
});

/**
 * Deep Resolve values, potentially an array
 * @param {object} values Form Values
 * @param {string or array} depends Could be a string or array of strings
 */

var resolveDepends = function resolveDepends(values, depends) {
  var conditionalvalue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  if (!values) return true;

  if (Array.isArray(depends)) {
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = depends[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var depend = _step.value;

        if (conditionalvalue !== null) {
          return objectPath.get(values, depend) === conditionalvalue;
        } else {
          return objectPath.has(values, depend) && objectPath.get(values, depend);
        }
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return != null) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }
  } else {
    if (conditionalvalue !== null) {
      return objectPath.get(values, depends) === conditionalvalue;
    } else {
      return objectPath.has(values, depends) && objectPath.get(values, depends);
    }
  }

  return false;
};
/**
 * Gets inline error by name checking within form error object
 * for all 'touched' fields
 */

var getInlineError = function getInlineError(errors, touched, submitCount, name) {
  if (Object.keys(errors).length === 0) {
    return false;
  }

  if (objectPath.has(touched, name) || submitCount > 0) {
    return objectPath.has(errors, name) ? objectPath.get(errors, name) : false;
  }

  return false;
};
/**
 * Check if a date is valid
 * @link https://stackoverflow.com/a/1433119/1293256
 * @param  {[type]}  d The day
 * @param  {[type]}  m The month
 * @param  {[type]}  y The year
 * @return {Boolean}   Returns true if valid
 */

var isValidDate = function isValidDate(d, m, y) {
  m = parseInt(m, 10) - 1;
  return m >= 0 && m < 12 && d > 0 && d <= daysInMonth(m, y);
};
/**
 * Get the number of days in any particular month
 * @link https://stackoverflow.com/a/1433119/1293256
 * @param  {integer} m The month (valid: 0-11)
 * @param  {integer} y The year
 * @return {integer}   The number of days in the month
 */

var daysInMonth = function daysInMonth(m, y) {
  switch (m) {
    case 1:
      return y % 4 === 0 && y % 100 || y % 400 === 0 ? 29 : 28;

    case 8:
    case 3:
    case 5:
    case 10:
      return 30;

    default:
      return 31;
  }
};
/**
 * Pads a day or month with zero if needed
 */

var padZero = function padZero(a) {
  return a < 10 ? "0".concat(a) : a;
};
/**
 * Default month strings
 */

var monthsArray = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/**
 * Creates an array of numbers
 */

var createRange = function createRange(start, end) {
  if (start === end) {
    return [start];
  }

  return [start].concat(_toConsumableArray(createRange(start + 1, end)));
};
/**
 * Changes internal parts to JS Date
 * (month is NOT zero offset)
 */

var partsToJSDate = function partsToJSDate(d, m, y) {
  return new Date("".concat(y, "-").concat(padZero(m), "-").concat(padZero(d)));
};
/**
 * Changes JS Date to parts
 * (date returns month with zero offset)
 */

var jsDateToParts = function jsDateToParts(date) {
  if (!date) return [null, null, null];
  var parsedDate = new Date(date);
  var d = parsedDate.getDate();
  var m = parsedDate.getMonth() + 1;
  var y = parsedDate.getFullYear();
  return [d, m, y];
};
/**
 * CSS Focus transition
 */

var transitionFocus = "\n  transition: all .2s;\n";

function _templateObject5$4() {
  var data = _taggedTemplateLiteral(["\n  margin-top: ", ";\n  padding: ", ";\n  color: ", ";\n  font-size: 1.4rem;\n  display: flex;\n  justify-content: space-between;\n  width: ", ";\n"]);

  _templateObject5$4 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$5() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  display: block;\n  text-align: ", ";\n  padding: ", ";\n"]);

  _templateObject4$5 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$8() {
  var data = _taggedTemplateLiteral(["\n  a {\n    color: ", ";\n  }\n"]);

  _templateObject3$8 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$c() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  margin-bottom: 1rem;\n"]);

  _templateObject2$c = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$j() {
  var data = _taggedTemplateLiteral(["\n  display: ", ";\n  flex-direction: column;\n"]);

  _templateObject$j = function _templateObject() {
    return data;
  };

  return data;
}
var TWInputLabel = function TWInputLabel(_ref) {
  var children = _ref.children,
      label = _ref.label,
      labelHTML = _ref.labelHTML,
      validate = _ref.validate,
      compact = _ref.compact,
      noAnnotation = _ref.noAnnotation,
      props = _objectWithoutProperties(_ref, ["children", "label", "labelHTML", "validate", "compact", "noAnnotation"]);

  return React__default.createElement(StyledLabel, _extends({
    component: "label"
  }, props, {
    center: props.center ? 1 : 0,
    compact: compact
  }), labelHTML ? React__default.createElement(TWInlineHTML, {
    style: {
      display: 'inline'
    },
    dangerouslySetInnerHTML: {
      __html: labelHTML
    }
  }) : null, children, label, !(validate && isRequired(validate)) && !noAnnotation && ' (optional)');
};

var isRequired = function isRequired(validate) {
  return validate.some(function (r) {
    return r.split(':::')[0] === 'required';
  });
};
/**
 * Used to group
 * - label
 * - field
 * - error
 */


var TWFieldGroup = styled.div(_templateObject$j(), function (p) {
  return p.type === 'hidden' ? 'none' : 'flex';
});
var TWInlineFieldGroup = styled.div(_templateObject2$c());
var TWInlineHTML = styled.div(_templateObject3$8(), function (props) {
  return props.theme.colors.pinkDark;
});
/**
 * Main input label
 */

var StyledLabel = styled(Text$2)(_templateObject4$5(), function (props) {
  return props.theme.colors.greyMid;
}, function (props) {
  return props.center ? 'center' : 'initial';
}, function (props) {
  return props.compact ? '0 0 1rem' : '0 1rem 1rem';
});
/**
 * Main input error
 */

var StyledError = styled(Text$2)(_templateObject5$4(), function (props) {
  return props.compact ? '0' : '1rem';
}, function (props) {
  return props.compact ? '0 0 1rem' : '0 1rem 1rem';
}, function (props) {
  return props.error ? props.theme.colors.pink : props.theme.colors.grey;
}, function (p) {
  return p.fullWidth ? '100%' : 'initial';
});
var TWInputError = function TWInputError(_ref2) {
  var error = _ref2.error,
      help = _ref2.help,
      compact = _ref2.compact,
      children = _ref2.children;
  return help || typeof error === 'string' ? React__default.createElement(StyledError, {
    error: error,
    component: "label",
    compact: compact,
    fullWidth: !!children
  }, error || help, children) : null;
};

/**
 * Basic Required validation
 */

var validateRequired = function validateRequired(value, msg) {
  var error;

  if (!value || value.length < 1) {
    error = msg || 'This is a required field';
  }

  return error;
};
/**
 * Basic Email validation
 */

var validateEmail = function validateEmail(value, msg) {
  var error;

  if (value.length && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
    error = msg || 'Please enter a valid email';
  }

  return error;
};
/**
 * Basic Min Length
 */

var validateMinLength = function validateMinLength(value, msg, length) {
  var error;

  if (!value || value.length < length) {
    error = msg || "Must be at least ".concat(length, " characters");
  }

  return error;
};
/**
 * Basic Max Length
 */

var validateMaxLength = function validateMaxLength(value, msg, length) {
  var error;

  if (!value || value.length > length) {
    error = msg || "Can't be more than ".concat(length, " characters");
  }

  return error;
};
var validatePostcode = function validatePostcode(value, msg) {
  var error;
  var re = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/i;

  if (value.length && !re.test(value)) {
    error = msg || 'Please enter a valid postcode';
  }

  return error;
};
var validatePhone = function validatePhone(value, msg) {
  var error;
  var phoneNumber = parsePhoneNumber(value);

  if (value && value.length && !phoneNumber.isValid()) {
    error = msg || 'Please enter a valid contact number';
  }

  return error;
};
/**
 * Custom Regex
 */

var validateRegex = function validateRegex(value, msg, regex) {
  var error;

  if (!value) {
    return false;
  }

  var customPattern = new RegExp(regex);

  if (!customPattern.test(value)) {
    error = msg || 'Invalid field';
  }

  return error;
};
/**
 * Check date is in past
 */

var validateDate = function validateDate(value, msg) {
  var error;
  if (!value) return error;

  if (!isValid(value)) {
    error = msg || 'Please enter a valid date';
  }

  return error;
};
var validatePast = function validatePast(value, msg) {
  var error;

  if (isFuture(value)) {
    error = msg || 'Date cannot be in the future';
  }

  return error;
};
var validateFuture = function validateFuture(value, msg) {
  var error;

  if (isPast(value)) {
    error = msg || 'Date must be in the future';
  }

  return error;
};
var validateDateAgeMax = function validateDateAgeMax(value, msg, age) {
  var error;
  if (!value) return error;
  var today = new Date();
  var fullYears = differenceInYears(today, value);

  if (fullYears > age - 1) {
    error = msg || "Must be under ".concat(age, " years of age");
  }

  return error;
};
/**
 * Export for Field Level validation
 */

var validationMappings = {
  required: validateRequired,
  minLength: validateMinLength,
  maxLength: validateMaxLength,
  email: validateEmail,
  postcode: validatePostcode,
  phone: validatePhone,
  regex: validateRegex,
  dateAgeMax: validateDateAgeMax,
  dateValid: validateDate,
  dateFuture: validateFuture,
  dateNotFuture: validatePast
  /**
   * Validator Regexes
   */

};
var PARAM_REGEX_NO_BRACKETS = /\[(.*?)\]$/;
var PARAM_REGEX = /\[.*?\]$/;
/**
 * Gets the parameter from between the [] in the validator
 * eg minLength[3] returns 3
 */

var getParam = function getParam(validator) {
  var param = validator.match(PARAM_REGEX_NO_BRACKETS);
  return param && param.length > 1 ? param[1] : false;
};
/**
 * Removes the parameter (and brackets) in the validator
 * eg minLength[3] returns 3
 */

var removeValidatorParam = function removeValidatorParam(validator) {
  return validator.replace(PARAM_REGEX, '');
};
/**
 * Composes validators and calls them in order
 */


var TWValidate = (function (args) {
  return function (value) {
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = args[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var validatorString = _step.value;
        // Do we have a msg?
        // console.log('---------', validatorString)
        var validateArgs = validatorString.split(':::');
        var validatorDef = void 0;
        var validatorMsg = void 0;

        if (validateArgs.length === 1) {
          validatorDef = validateArgs[0];
        } else if (validateArgs.length === 2) {
          validatorDef = validateArgs[0];
          validatorMsg = validateArgs[1];
        } else {
          validatorMsg = validateArgs.pop();
          validatorDef = validateArgs.join(':::'); // rejoin
        } // Do we have a param?


        var param = getParam(validatorDef); // console.log(validatorDef)
        // console.log(value)
        // console.log(validatorMsg)
        // console.log(param)
        // Error placeholder

        var error = void 0;

        if (param) {
          var basicValidator = removeValidatorParam(validatorDef);
          error = validatorDef && validatorMsg ? validationMappings[basicValidator](value, validatorMsg, param) : validationMappings[basicValidator](value, null, param);
        } else {
          error = validatorDef && validatorMsg ? validationMappings[validatorDef](value, validatorMsg) : validationMappings[validatorDef](value);
        }

        if (error) {
          return error;
        }
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return != null) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    return false;
  };
});

function _templateObject2$b() {
  var data = _taggedTemplateLiteral(["\n  .tw-tel-input.react-tel-input {\n    width: 100%;\n  }\n\n  .react-tel-input .form-control {\n    height: unset;\n    width: 100% !important;\n  }\n\n  .tw-tel-input .flag-dropdown {\n    position: absolute;\n    top: 0;\n    bottom: 0;\n    padding: 0;\n    background-color: #e4e4eb;\n    border: 0;\n    width: 65px;\n    border-radius: 1.5rem 0 0 1.5rem;\n  }\n\n  .react-tel-input {\n    font-family: 'Muli';\n  }\n\n  .react-tel-input .selected-flag {\n    outline: none;\n    position: relative;\n    height: 100%;\n    padding: 0 0 0 8px;\n    border-radius: 1.5rem 0 0 1.5rem;\n    width: 65px;\n    padding-left: 2rem;\n  }\n\n  .tw-tel-input .form-control {\n    position: relative;\n    font-size: 14px;\n    letter-spacing: 0.01rem;\n    margin-top: 0 !important;\n    margin-bottom: 0 !important;\n    padding-left: 48px;\n    margin-left: 0;\n    background: #ffffff;\n    border: 1px solid #cacaca;\n    border-radius: 5px;\n    line-height: 25px;\n    width: 300px;\n    outline: none;\n    color: #474676;\n    font-family: 'Muli', sans-serif;\n    font-weight: 400;\n    -webkit-font-smoothing: antialiased;\n    -moz-osx-font-smoothing: grayscale;\n    color: #777997;\n    font-size: 1.7rem;\n    line-height: 2.3rem;\n    -webkit-transition: all 0.2s;\n    transition: all 0.2s;\n    -webkit-appearance: none;\n    -moz-appearance: none;\n    appearance: none;\n    background-color: #fff;\n    outline: none;\n    border-radius: 1.5rem;\n    border: 4px solid #e4e4eb;\n    padding: 1rem;\n    padding-left: 75px;\n    -webkit-appearance: none;\n  }\n\n  .react-tel-input .selected-flag:hover,\n  .react-tel-input .selected-flag:focus {\n    background-color: #e4e4eb;\n  }\n\n  .react-tel-input .flag-dropdown.open .selected-flag {\n    background: #e4e4eb;\n    border-radius: 1.5rem 0 0 1.5rem;\n  }\n\n  .react-tel-input .flag-dropdown.open {\n    z-index: 101;\n  }\n\n"]);

  _templateObject2$b = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$i() {
  var data = _taggedTemplateLiteral(["\n  .react-tel-input{font-family:'Roboto',sans-serif;font-size:15px;position:relative;width:100%}.react-tel-input :disabled{cursor:not-allowed}.react-tel-input .flag{width:16px;height:11px;background-image:url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAACmCAMAAAACnqETAAADAFBMVEUAAAD30gQCKn0GJJ4MP4kMlD43WGf9/f329vcBAQHhAADx8vHvAwL8AQL7UlL4RUUzqDP2MjLp6un2Jyj0Ghn2PTr9fHvi5OJYuln7Xl75+UPpNzXUAQH29jH6cXC+AAIAJwBNtE/23Ff5aGdDr0TJAQHsZV3qR0IAOQB3x3fdRD/Z2NvuWFLkcG7fVlH4kI4AAlXO0M8BATsdS6MCagIBfQEASgPoKSc4VKL442q4xeQAigD46eetAABYd9jvf3nZMiwAAoD30zz55X5ng9tPbKZnwGXz8x77+lY7OTjzzikABGsenh72pKNPldEAWgHgGBgAACH88/Gqt95JR0OWAwP3uLd/qdr53kMBBJJ3d3XMPTpWer8NnAwABKPH1O1VVFIuLSz13NtZnlf2kEh9keLn7vfZ4vNkZGHzvwJIXZRfZLuDwfv4y8tvk79LlUblzsxorGcCBusFKuYCCcdmfq5jqvlxt/tzktEABLb8/HL2tlTAw8SLlMFpj/ZlpNhBZ81BYbQcGxuToN9SYdjXY2Lz7lD0dCQ6S9Dm0EUCYPdDlvWWvd2AnviXqc11eMZTqPc3cPMCRev16ZrRUE0Hf/tNT7HIJyTptDVTffSsTkvhtgQ0T4jigoFUx/g+hsX9/QUHzQY1dbJ7sHV02Pduvd0leiK1XmaTrfpCQPgELrrdsrY1NamgyPrh03iPxosvX92ysbCgoZzk5kP1YD7t6AILnu+45LykNS40qvXDdHnR6tBennz6u3TSxU1Or9Swz6wqzCsPZKzglJbIqEY8hDhyAgFzbJxuOC+Li4d9sJLFsnhwbvH2d1A3kzAqPZQITsN76nq2dzaZdKJf4F6RJkb078YFiM+tnWZGh2F+dDibykYoMcsnekdI1UhCAwWb25qVkEq43km9yBrclQMGwfyZ3/zZ2QK9gJxsJWCBUk32QwqOSYKRxh6Xdm3B4oMW22EPZzawnR72kgZltCqPxrdH1dkBkqDdWwwMwMO9O2sqKXHvipPGJkzlRVLhJjVIs9KrAAAAB3RSTlMA/v3+/Pn9Fk05qAAAUU9JREFUeNp0nAlYVNcVxzHazoroGBkXhAgCCjMsroDoKIgKdFABBwQUnSAoCqLRFBfcCBIM4kbqShO1hlSrCJqQQmNssVFqjBarsdjFJWlMTOLXJDZt8/X7+j/n3pk3vNq/bb8+3nbP79137/+dd954qTVt8uTJL73OMhqNer03ady4cWOhWbNmjV+0FfKGjMb36Y9/1fXUst9cb2y8/lpb797z5k2dOjXVD9Ljn59fcHBwQEDAgGch3l9on6feeeedn0r9kvT222+/sErRgvcDArwV8f5tN/rcvPnMZ22pqVFRSVGjR38k9Rsp9fLql/MXLj20VGjt2rVeak2Og/auI/kHBQ3We/tCo0ZNhwYNGj58/NaWlpbOyMhIX1//2/jTrICvckhXruQsWbJw4cL3tzhPORynSk5lZWVtglL9IkmdDQ05NqvVGhLwbKSUL+Tvb9yH/2sj+eN0IZZ3fvq3Hnp71ZtCOyofdnTYSzq9xX7UtsF9+/Y1FpeZT54sc2aUlq6Jy89YM/qj2oZaoeOkMR8dV/Tee++NWb04rrA5MRYKDAyc/NKCpwDIyKhE9LEzZ/r4DLQAAE6EyEeM6AcNH7m1pTMnB+fHX7tG9Bs0Xt+GwM/frqm5tz950aKDk6rsiA0xbUrbRAii/BDeV9bGhQsPRlyOCAuZ9GykZwT++n2RHPnVYQU+oaFDPQD8jEQAPiDdaLPaHGVXbn/O7YHQuIH9B/gYgzts1iqrtSopKWlNRkzS6I8arFaOFvTfew8AfiYil/rN6sWTKwtbArOzExISUl7+vwCuQNt8Bg71AQCcTwNpWeFbW3IIQEmJr08XgIzX2xDcvZrs7Jru5EWXwwKSwh2RkQ77w7Q0bXp6YRoDaKO+kZl8MCwsYpJ3pEf8liAAoPhDhqUMQ/wAkF+oqKiosJYA7HxotdnTtVe6Pr/S0h+AI90QffU3T9obGuwdD5PqkmJiMtbM+ajWI/60TX0COhoarAAE1dfXV80FgMmLi1oSKP7/B6ASAGyBV4YM7D/Bx8/bF7g5fgmgEwCCSiJtJQRgxEi9zZqVdYUu9pW0tLCIgOvxdR0dpxx5aWl7EzV7CYDV+tXnCzMzkzMvE4AFlTuhZaSf/OQny1L32RC+JcHikzJ06NAJoe+YNKRbsbG3xPlWZTxssNmdOP/J27ffudLJ60V7DAaT1lxRVvfwYe3Jlrq4uJiKjAwAcIWP+BkAhV/i7HA0uAG8BAIUf8qfzvwvgJcQf+XMK4GWi8OGTpgQ6uftzwC0LIM2WgcASwaXOBwlA7v6/YgAhFRt2pRGeu0/UyImbal77eHDo2kVAJAeKwE0fl6P63/5nSlTAKBCiR8AovbZEL9lf8I5AMD5booAE7OzY8X5fhGJi0/nTzTcMh+80iIBaF0APqvIu3EjqfRGcV3S4aSKYk8AaW4ADU4gOFlfn8sAXnoJBDpTCMDL87zU2kwATl+x1Nw+P2HChKHBBMDHFT8DwGjX11FSYu/f/aMf9XtOjwAacf2hmxRg7ywXDrr30kb7NVhDquo/z0y+nJs7ZUoYA5DxM4BFmcnJyV93PzjbvQhK3urqAYF7xflWVT5ssDaU4Ox7T9+6Ei4BaN0AUkvXJEExMTGHD9cdFgA2yfgZQAP1f0dJw0lrfS4BmIb4z5yZBgL/H8DibbehGROenQ0AQRhvZPwQAGDQ8wlqsFkmdP9ofr/n/OgK2ml1xxQECAAy/tdee++91wCA1mfWJy/KXUTr536T+O67764X2r9//T+3JkPdDx50f7qItDXfff+zeAxY1lYV0VCmPV1Ts5fGAGUYDbHpo0qT6vKTignAtWvXiuf0StwGZZPQybMPAYC8/xF/bj0AUPwvvzytKCdl6dMAvJxRuXjxkCHnL86YMXs2A8B4m4yWQTrdIp0uByMajcATJrwzXwCIiIjAFSrbJwGI+FlH00YH8/rQy5enQPsYgBK/BLCI1c0Afonhn/XjH8MNLP9o1Y4Pfg795N9hYQ23bt1q4fb07z+A/ITR2J8AFJnqOP7iuj7Fc35TK+9/bkPaM+NGiSnsB6wRIwGA4n/5T5Pzc5aeeAqAP1VCM4niWRqVgr1p1sEYlskNJQC4BQZbLJi0MAgCgBUKqYo3VEVEhIWFTZqXtYmVxiIAtB4QeDUAvMuSFBgAJCkwAKHlLAKw4wMIFG5URVgdLdwedEq6BuCgj1qzpi4uiVScYa6I0fWKJQVC2aRDY0eNWrlyECwMMIDDc2vZ6UF0F7z8tB5w4kTvtZ+ygklGkk4lvZ6sne45SDg8aJIQ2z+4Mmg0qcfauXPnfvPNN9XV/1S0VSWyf1Ls4FZ5aIHu/blGKb2UOM0ckq4PmsZ2b8yYMb2l4FbhX8ePHwmhuSPXkhaQ5q0tXzBvntdUUq9eSyFu9njXxpA74Leg198yktRWVI4OkAkymw2Q3WO90+nnN3u2H0QkHI6JpHHj2GvTYdsupd68GfVZ4yTJqJeUaNKhQ+rzCUvOMXEr//4vD3333XdLe+rRJx4iqumDnT2O5zW1HII1hPLy8pJGjz9GWgk9D61Al4fWkWay9VRbUa1GEVCYDRoonu0dr++n0ZQ0dMCNdDRYHVrtuImjWHQ80lvfl4WfhJetw1CFm6h+rkazd28iJHvyIe/IHt7ZOBY7o4GPH4smPqf7nRwz/sH6bmmi2HtvYiBUYPxEcZakt701PdsPAIhb3DBbYmIIAOK+F9HXJ6z7t799AwDI48+cOQRi66m2ogoAYVwIQEkQb8DrJza1azRWq9NpjUjXtg+aNXHU9EEQHW/YsGFD3toHMFZbgzUsDNPkPgAgpScG1vA4TgB8PZATAAoc6IasWPHhhwCQkyNCdwMIJCVqDabA8+cAAJFLYVD92dvpjvQe7ZcA7p0/350dEzNmy+iRAHBPrO9+AwB41Of4h2HoFdZYhsfL7ej7QmbSBdED/GkDXv+ju9Pv4i9mM+g09Rs1duKoQSQR/4whb7msbFhufHy8M2xup6AZ3sHzWOChaveIWQCtn00A7s/84MDuD4bd+fBDcYEukrVna5fwMQPAsqnQZOqqLtBzezysvHd6z/YLANndUELMGAmgXqzPfeON3+IE8PHbuL2YegYCAO+/fz/io2VMM+5HpR/BGXIPGCzix3oAaBo13aApK9Mahg8fNAo9ANsPGi7iB4BLZRUPH9advJGb6zx+3Jk7FwFtCNekNzQUabW3cAv0Ek9uUA0U+PGsY4NmzrxQVBS3e82wGQDA7bvI8SsAsgNP7y26HV4GALyeJzGaY5J18fZ4GT+3DwBK8/K2ZF/s7v46ZYwEsMJHrJ/gApBJ8QPAs9gh2BYBnT077OwUnvcBwB0/nCEAQPFBdADefv5dPEu3p2u18e39Bg2aPou2h9wNmP3wi7bGL9qsuVOcizoBgM/X0BBtamggK2wGABn+WSLw8awm9P4Du3ecys+aMWPGt6J9medF/EsBIBbxJxSFm4vM5moJAOGL+AHAO90jfglgy5bshO7uFAIQM2fkyhUr6sX6fW+MJQDYX1wvWI/+uOIc79mziJec4ESxDPGy6AF9RfzYHgBw02s7yswNhf1GDJ8+lvcfPgKrxfoAa0S9uP9HTV95LHdur8TzuF7W5OSqDdEGAFiaiIjk9U8hAMdw+1Ts3r37VPOMGR/K9l3k+CUA9P9b4c6y8LKC6upqAiDj3wpxD1Dix/m9Uku3KAD6xMx5DgC6xfrLYwnAEuw/jOJnAMHjpnvECwA8aK5YseK3EA2aogf0pQNIAIOaXI8S0/sBAPaHaLUEIOJHPmjUsWACACN7/qLVmoz2Zjabv3x8X+oBdP/DWeih94d9sHv3BzO+fOOND6l9C93xL00BgOy97dHo/ZHm6EcAwM8OHlZ+YLpFtF9eQAGA9+81pg8DQCzdU3D9Ef/YN3AC8OP4Z5D1DBg7XYmfAKitqYl7AA8AvDxxVLtGW1VVVhYRZjC0jhg/Tuzv3j6gCuEjfghGYd/cXrFk5BNqai4K633k938h/Zp15C8Tx68E7X7Dtm2b8QZEAH743j8gYQQwC8TGlp08Z7ZWC+k/4eFf6pc//Sje3+TZ/pFeqXkQ7hoIhhoAnve8ogRgCQZBMQsgTgBgXykpAoDKmpoIuJP/wMvzwaOKHkisVfUnDYZZ2J/k3n4ST/94UiHt2/d+Lx7yttFAXnP+60W6+X9ggQFzGDdeOJT791fQNAgAv/qHFFMAAJou7AWQBCAkKXzknW71bD96APnWQ4c+hthRsv1Ty2WNA4InwYYpzhJSW1MT+lmkxx9awyfNhQVmvf9+c9M4kVt1by8tsmuLub3I/in6er7URGkh1SZ1znfk/xR9o2oP7F8Pax1vbO8RgJcwhYp8BvpMcD1t+0GffPJ7xUo+CA54Yc+DPXv2vGA0vkBavfqIW+xeH3kr8iJ9QxJegQNpu/TMzZupnzXOkQ7+OkumeCCOU+Si2Sr7kR6RkQZ/iA0y62PWVKlUiLy8fsz1MSd6s+YhLz1vu0t7ILS4T1Rqn2cU9fF6YQdpMZIAG6dNmzZ5bX+7PZKGsXi0CM9xwZ+0DmuVnejxsHMDJu3Zu24vkrT+QTtYq4/8nvWHPzyeCa2HUySRbzMKAO9CGhZ15Pku67uGlaS7frzoeFat26uY2CpzijiIrbKfLdH2buy7eKLkR8oAaXWhQNLH8+qEKirKy0tLS6O8bXVZQpvg8dPmbV/O+jH0IvRClLY06hkPAcBGqLa19ckBzC0HVg+0R9rQFpqFtWER1oBPhr3+eutPocevPzIaBwTseTORAu/rQ7sd2AgA4g69T1PlfmGVsX9fn8ESALk4ER5Gsb/Mny2tbzGkPQwASH1s2iTDBwC2yhYeVdgq+yXODAwpCCzAozT7Dml12fqR8VGcOMtk9A0pkUvsI7YvR+DQrl2vQLtWpdbFPAVAq8lgMrcygKEEoKQsJKTMYQgLDQn4ZN3r60T43ngSrH5g1rBcWaINAoCMX1plXq8GoBUAXNYX4RcfPqzVXa8tqk3bpATAVtnCVpytsp8tsCBifcJVil8BoFhfu7OE5RCyGn0HWxweQLYvf/HF2tp1T568IgD0Gf2MJilKBSCrPf5Cc3h76e4zuwmAv8ZqQ5cLMwwNA4DWn+IfwoeqX3/8kQvAQC2rGQCU+NkqywuiAqAVACa6rO/hYsR/uBi3wKZd7wGA1gPAcEvfhAQAmEEA4DwLEgo4/tmzwyYdYqurWF+9zWKxhCKlTjnV2WEBxkhHX5/G8jSZEZoKALWJWbuyYgWBVRgA6vqk9hgDNh54YtI2t2jbn5wBgAl2m1XTYAmxhFoNU5DG/uRnHuG/d/yjEa0X7kID+99tgu6OxTytxK8A0KoAaCGexz+rWHPpUtKaG4e1hwnAhhNZlLtMhwyG+HhDGVvl0PXZ2fv7w3oMe8vPijuf4of2AQCyutDmzWdI1zcv0Psr8SOFF2As0Th8Qr84CiEzcjSKni09b4l5C+al4r9uAcCBA1nthuYKc3spA4i0hWgNdFazgbK8n3iEjzct380S1rd/f+mkAECJH87O21/2v76eALQM4MiRX0+MKqXsFXSYAei8/d3WXLHaoQNTUga4AYSGiesPTSEASvwEwCrin4D4GYAv4m9MS5M5yalGX1uixccntCDwKqf5n5FSboGNBw4caG03m1tbz5zZs3v1bAAAKvtJDAuzAeD1c0r4DEBY4f4DKH4C8AclfgYQxFl0etRWAAj+RwjA6DUyfuoC3xt02F6JnwDQ8UNpeQAB+DTY6op/HxJLU+au3jj5JYRPwvR5ZoFN3v12oVxjkE+oXbG+4o71WH5dJa9VALD7wBPMArvP7AEAfaTVgm3NZkzcszHoBCvhM4BvhTcfMOCB8OZH/sDxp0hrCwA8PvKjNqkaAPaL80sAyvU3fF+sU1tptspDaRkA3gKAEIoforwaAPhZ3f2de4RWeUvAARqDKH65ZDKE7/nxriexm17ZtO0JxvhXX1n1Q5UAYCMQTCsvn7ybEuYL9JE2q9jfZJoSBgADEP5xt757MJM0xMcHUUOfzr9Pywlua+vtThhJAOvdPYDc/LjRayC+CxiDTm2l2SpbeJmPHywzyhLDXH1ICI96wEAcAlIr4ABKSThuXt4c75ByyJ2Zj9qDWbD2SSJmAdaqBSp5CdPoB5frx9LDdEVDG6C5cKnB/xz1kdB3rAcP2Bb7+X0q9GtOXirWU7HGEgBSwI/CoehosrIT2f7pFKmtNFvlYF4W/jvAI6kMoX2y1kBIZKBHu1PDwfNI7A1ZbP+UIgPMAn08hFnAIOROal3P6pnlzSQlK8pHf4F2s+AwjSRNvDsCadl76bQif9tbqDBdNvzPfxcy8+nCw1OULDDrOukEi7PXnngo+IDLY8UZZMmGOmsMn09yPTI8VwjhWEUkXIY4mYVu2/7qq9tJXuqsLoxJj+XMZqEWUmdnskabf8olWOI9Rl9Ik07vqeh1id/EpqZRUGKOhksqxveuZGm0Idx3g//+BPrd734n793wXnuFEoUOXc+ClJcrC4wiI8rv0On4GNUbbh8TBRtwDOPVWerxv2P9SuiPukKcBwd0xRPusuLSH+/xUmd1r9dm5XsuZzZ35kBLxCt+ANBoihA5CY6YAODEmnS8KRpIr7cBgJp2uyDkahcmi+EAUE7SpvPQFRrw9yfcvk5nPHUyApDokQWPBQCOXN7DafPo+ABH1RN8fL0t6OrVq1X3eC7C8dVZ6vHu2P/4xz//WQDAQ44rnmhXFlrYYxeAW+mJ6bcSEyUAEFCyqJdPfkX6HLp8+fJXBEBTyAR2uAD0tWjSfbh9BGAUxX/1zi8HVXcpAHZq03m9BNBptXY4ET8DUOKXANJk/AxAFETYbO/ayJ3aACAwcH3gep/Qru4PUZ8w/nW8X9gWOMSdZR7bRG81jkOU1XjeDUArFOey4i++WFW1vr4NAMTLaFjLvekuAJvylYKIXIcvFcQItzLB9o5G44CzylcA+Pe1+GjS+fojwGDO4hbcOfuXX35bnZ0deIgB7Nyp1QqrygB+1Wb9lbOBAUQTAOV1XuwhdRZXI7Q3UVplfSKS45aEc0MH9p/yTveKkQCw7WrIXneWmYDMrD3++Mnx47x8Iqt8GiTs4+bJ8y6V3Xj4sOLkjV27qjA9AYCBvGJsQkLgXraKBAAEOsCdZPfLdbjjRwQAUOJvxy7t/BK+NKuPhqVYTX6PEHJ101+qq8MWLcrUqdf/ne5Pa+OvMLPRPB3dBw+ychaDSkers7gaFiAliv31sSHr14euv0o8n322XoeAHXhwOyuydsMYwJDax0+ePD5OywCA8NM4fAIwdWfdtIqKvKyMXbuKDPWFRS8wAG3r3lvtF0RBAveANuqv7K2Dc+3K9Z/g7gGtlKRja9sjPjSQF6/eqc7+9ttztKz3Z6uarl22BcqL+jvdo1URvyqzGbSUpOTX6XlkW0mvpaqzuBLA6dOxOD4DKMA7koRzaMyUf3+xczUCvlVgic+m+CWAIUNqjz95vEkBwJdfAniVhj6+/xuRjGyTAO42XRjVxJMfACjxE4CuveRlC2SO7d13NJD59yJFSQD0QRj+tPHu7flhpqv6y+pv/9lF7wn0QexZ4g1bBIBZBCAnIsJaEm+QAJT4f/Naqrmndd2wCFMPhuHTp3OWQDk6vS1hfcL+6v6I/iU8vgPAkAs1+5vPIn62zt6+56AsdNChjx49OqcvwsEQPx2OjwcAIv5d+YW5hfkSgNZ814wNGADHP0HEo58Q8PXe2Fjx/JkCxd7T8uXn+CUA3P4AILcPFu8NuqrDziF+lND4hfCjigAQsywKozQN0Esc8eJ89LTHLk8+7ZmV+LnBnJX2KNAA8KvVQ//9xWTYkDNnJq9VW2m5XF8vl2lSx/X3AMDhU35kee7yXS94mfh8St78RNZDOetAEwBAmaRjoS6t4a7M0TKFcWxNtfE+cvvgsWKCjs3U8jwFAGxd0w150DIAkHO0QSjaSPM3Pa6BI+RnVtojAPAErBRo6AeHtN1YDP8uRra1aiutXgYALTZ1H287pn+SxAAA0pFB0aQT7wuzKbOQwV93kfC/Qt13j/TI0k5kg2Yqox1YY0VBwlKdWXgx6VvLzKlRrPEjRU53Q7QQdpenE/bW7G7JBpZOpUmfLVi9arXQWkhtpdXLZP8WzFsQFx3Hh2vm/CjrBZaX9UbvmzenotZWWmpZ3AOJUgvCtkq/2u2Vy0lmbiOfZhxLqSWuyC/FpS5qbCyiW/6LUm/om2rv6mrvR9VGyCRkNErs6uOprS2bcpaZ91Bbd0CTmsTiPd/i8gtuzxGVPpoIebTY61qJ+aT9pJOytEnQ6NfiSBlxcbWsMTRG7LBtdFvJ8nxI9FAyKEhgkJRa4jqHpigjQxMZqamry/fV1Hk3eWRx198zmjTpmEZovSbe7tRGq4+ntraGnlY9nJfT47Wu5YAGVIKSZIEF7y8KOrg9R5C++r2iI6/W9myvF2p3/YNwyqQYcl/Fc14TkcNAk+r60AkPhBzg0wkA4GNi2fyDCMAg5VURKkfz4uwOzWJN0GBNuR0Qrnk3jTrrqlh68O1wvDlyNCBp6R+k0Tqq7ACgOp7K2koA6b7xSgFGeuTgvkElWBYAEDgidxVY8P5c0DGMrbLTgx908tVTPdo73uumw+4baW94WByTlp+fFuMCkJGhBqD1ACCeFP2pTg/WVzkgTpiXUV6GtCCeD4Li82N29vYGoDs1/Lrvy379ngcADaWtg0JwMAe8ufp46gIM+brdYnEKL4/lSF5fItqjFE6ms6/g/UVBB18Qb1xgeno4x7qqf/XUKdr81i2ZIfJaU1LR0YEsbUxMWmnFUQEgP5/sYFxceXlWn1XIGR6w0JzDWosGZ2SIBgeFwJvDeBBvtxWVz5Ior2Xle486i4KIO1fP3aEXkiv0QQ47pa9CQoTTnP304227d08ejwMsszRaylwAZIGDvwCw/RQ8ObRRaBUXcIiCDpwPAN6NvQoN5vgHngOA5XT7NDVJa+31WUXSjRsxa27EXEuLawGAo3HU/+OysnBjlpdmPeNnExkYV16+HO3NEKMQJjgrGizjl1a0MTLI4xL2vek9KrBg+IiuhBRUFhMAfrojiae74Kcf715m8j0+ngDgj/vBR9QOAyArUmj2njc5cJmkOLCKa5u5PTO4YMM7cR0REPELAMtxxA0bpDX3SsXYFwNdu5bWmZN0bc7RjNraOMSPHpBRCgCrKWcYKq//njNrp4kGmyCQCQlGg5X40WDZA3z6u3vAnUEjRtw5d+5LAJi/Qm9xcOstFht9JxHp9/TjDeteKJyd7AFhuVPKhFX39vcXXd4hssjbuQO4IGxkAD6iPZy1Rg9Yj/g5/IGPAGD58kJ42Q0bwnE8AUDG39mZl5eToyMAiL62Fok2AkD34O7QM26jlIcG14oui6sYEjymrpxeyuUJlaZuqViWnz5Y0x8AQpt7J6V6Hxs+4k4N2chD386f/6EeRseB9lso89oBY6I+3lhVAQYDSHfud5qEkUEWGftj574ii2xWUqJyPTqfKOjg/WlQ5P7v4wJwSguhoJEV7hW1huOHKO1xDQD45aJWWyoAUAPOhBEAgwtAbZ2YhC2haDA/bbkfNvKmxmRobJF5mgEDNL/Q2EPKU72nD7rPPhq5rwf9CIDdageAUK2hod4GAKrj/U8BRiQ/ju8/R/7UJ4Ssbl9HutbpL63uUws2RH/k5bKe1vrKq8td1nsflDsXAES5OXQY9da639SS6uQswAC0ByyTlR6QAQkbEgIBQNbicggY8qCpdRpb3M6dNAguS4rTWC4ZjwVCXIABCitgdZ2RGNBDMAs4bSUAoDre/xRgsCFYvx5hkbkVVjfIv6/L6j61YIMLOs7ysuvttdSRV+vcnqEecycAiFpbFtUbiEpbzpiy6NKsDlhL/pS1ZQuq6TZwkjCYJOtuSVNJpZ8nIQeaf/NmPlKyz9R+b4T++cj46JF+9iM9JK2un5+0uurjkX2T5Qsso5Df/7O6smCj5/a93oI+5eUjKu0JVpLMJK/r18PDZRaWq4i3k0ykcHbLKmcqaoVlCvcQtGjEjyZ6emF1Fre3CpDa6vKZhbHn8wdLueytnqU8n7CTFSllugeMik0WaJd6CrUZDTfmwep/cY3S5M/hmqjP73V9Mj0uKjnA7ZQtFebiRWiVt8x/yrHW6GE1SYf8Hraa2psUa2m0QWRlQ0QWd8FiUrkrL5XK+ytm13iiUog3mzZtQbANsrpL7CfpySCz+G8BXEChYRVAxj1vSsmCDVUBxTfFTq3zpDO+Li5/Q9OFlrg6tdX2MovZCn6MtXM7PS8LAPQ+HQA48IcPeardqFesJtf6HvL2bby97tat9unCCQIAz/ORkWKeBwB3PgafKWxOFVYXCYvjwuqe4NAlnpcIgIhcFkQAAAfOfwwNIwAALR4IkKEpMJp6ZrWj1QUUgx2Yde32G/hIB+VVx6LUVlsCcF2Dyt4MQBzvFQgAKP62pvA2CUBaTZmF/RjLEV+dn7nuVvuo4fQRFQBYoHRH31DKAgdX5EMSb0ZGXIy0uiU+JcLqEoBprvgZgBK/BKDEHxYBAIMEAG16NQDoJYAdO7QCQAKnL043N5+mbpB4qNEZ77CXlFRk5FMJfFOd/OyOxJ/deZ1A99+8Weue5gjALphFLL+yezcB2AhZmy5Y2Wnh9feSCGE1ET8DAM2D3WeHDKFuMGi80R/hl+CjqvgSBsBlc5V0vMpCqigRF4viN7AVXV252B3+S8jaKtdTZoH5q7IIaUUjJnEBhYHWxysA3ty4482Nb2r5+KyMuvw64fQqnBknT2aU7aQe0PX8MqoXaKUsaCvivWvQmiQA7qHQ5t7bkSt5RctWYzcD2MEAwsNDJICvFi7sewf6knRnIltPn8vdxGNYvGkcAPj42OPt9hJfTqpyAws1GRnaImRBXQAQf4mBG7i2snwnaxlp51R1FjnEYRfqgBo69nHO0YD1ngAKNxbiP7S9BFAXV1EhnN7D8KLw5riiirq4lXUHK47VIf6mC63tTU3trU3T78IJilJSpQcAwK5XeLlQAXCg6oMbVYife8DCep8RSqkpACD+e0hL70UPGD5S70/pLXQ6pyhY4BzfYi20uNDgBoD4Bxi4gQyQZnVZPK3OMquXOecIdgQA0vMGuPwbD+yg9RIA4o8T20+tAFvxlV59Te6y0Vh5wWQytLYaTOgBAFCp3KNiEPzxrldUADD8VV06/wUWfw4AZDUVqzoSy2GXHwyZiTGgHwGhLHGoj7Mk0jmUAVS4D54BxcVcr90E5fUfkJTGb36ox4gSDwg9hkthP4RQCDtu3Ic6dYEDF1CYPAHweowBwgqPbVoJyXJXfFCxrCgjDv8Jr4urO51bk1GBLDOUQ+IssxesKKlSqveeH7+iBnAAqo/YTTogsq49rOfB7m23brUOp2UGQNH4DJ1gEVnledP47pKvfLdEqd/9occo8TMAJX4CoFXilwBg+lQA5HoFAIcvviiZWsHXH4q5nVDzk9HqLLNXUaFLJlORqahuz4uQOCDPAkblUYvkx1bTw3oGt3Xi4ivLsoDBnVWeygNc3mYSsoQA4PnyFwDIMCglD8EjXc3/kAQAPbPE4Wx9PW6BF6RDkW1ci2+K+JsngQE9AB2QOwEudGNdRoU6y+zl/ohMmjWyf6uiyfduWEVSnJ0wZLw4UvkMTaebCCuqLOtVFQxKGasQdwSYZdcZPWweSykFFuKwlZxoOBdQXIiGmvUkVxJ5g5TaSivnHs3SqeQ1UZUl7Q1p9Bp3kQWvFicXNvvQfGX7cR8fmqs6oPozOp1KAqgClSyw1AKSnqVA/PbTXj3E7RWnn/81jrcb4loHme7+n/Pz5krWuu3GM5+hVnmOfAICAFVWtzdVE9g05VApHvNTPawnW8fLiYmPeXvofmCNztv2lRxRuG/p1AUXOl6rrDd6WFGyyqsXQ4oXnKe3sRIT2f5YAsY2PV4nNJPUS2nv/a9wQJ3yewPiW2OcP3wDN8LQvIHP3zO+7/kXJ8IvrYGuJBUDgEhqyruaAJSXa0I0eaSjRwGA1otw2DrqOs8HBt6hzb+tSbi4RAdn17jE/UI7UwJw+Po6xLOFjmsroj//fEMmr+eCCovl6lUfeqHu47d2scsG0WA5eSqMj1AovM/QiAB8JXZnnRvBul6u9k4/v9Ccmbzwn8ZIgROwwDPET6sxdeaEa5xOTfiSnHA+//OeWetce0cDVAzl5BwGgNb29lb570L73fZ+AFCqsWg4fgCIYuspLidbVxzwNgggzZOQ0o2AyNpG2JWHKQZgJ6sdycvR3CGdDbYyE6kFABD/+uyEgoFcUBHQEAHVV1XxZyNhcwUAy/r1FP+UiIBZo0zmY+2etcQc//3uzE5T54P1evSokvj4SB/w7I/jAUB4Z3N6ZF8f3/TmJRsYwMILraQLUOvwz8ocHR2ODlSo5V65sg8ANKx0B7IsJGGtLaraXXF+Nir0/r77fPb58wkXM1HAAACUpbZjvQJAfJY00EnLRt8gdPXPIyIuiwoRLqi4mlBQkFI9gQFQUWpDhNNZbwWAXADg+AMD9w8dOmVKaMAsg2FQ+3BYFs/2TL+/EIN4Z8qjgXqjf4kdpoP7kwCgMWkdMGNDI03hOD+11+xhrWWt8uHiwyfbGk+6AdjtjkhhPV3Fx2F0/tnyszixP9cCy8/UshP2y8/Q7Brg9sHeImvLX42JlLADy+E4HrxxZlhY8gSuEGGrjOrnagAg4wMA9RH4lCu+w5lLADpQ+mlxxm8LvFUytKTEcnCWofV5fOVzzAmVlDk7yAneP4/4M79GcSoBcJb4l8SHIH4+Hj8oNoeGLtv8kNojASjWGlnwS5eK16BMM6eidMlhFwBtpK/Bw3qGqqyn2J+SkASAPtM6fz7l62QG4O8RvwQQL95qOGnZDeCyLGaGVeYesL8ayxKANl6Lt125+/DV2CVTZZGzcrHZPDmvbPLm8O/RA4a39+uux+WQF2T6/ZZMxJ/yDbcHPcBGPYDjFwBM2lPL8jafyTCF4/zUXrOHlY7iStXDEDlUAPCNdzgdeHqz8z9Hwzx8SQoAR4/S6/yYo1FsPbUKADipewnZeMvxZcrS7q2LuNY3TMYPAQAUSfHbeDma/1xmtdIYYMYYQE5yYEFKyjdoLwMIC4sHAPzHSQAqKovi8L5w2uT8yrz8uPLiWStN7Su60COnkADg8fkWU2dmZkr/ZwWAoCCMAUEU/7M4np9BE57TrM3avLm8sHnhBkM0ffbX4S4mdoSNXiPiv3b7ypIlt2/rvNjaYnwXFQb99QRAO5QB4Fvio6PZeor4OAury7mYXfMtWeFvD/X6OpNqfbtkXpYLIkTBhX1w30gDA6D9Mfp2d/cTn6kZg7gQoLpaFlQsKH/J9Sj6p1/8Yktq76LFIDAtP39yXn5dXv4zs5DFqFB06Us8jYZn7v/GVRCBW4qrC4aKMQA9wJyzJFqbn2+IXrgkmgHkDqRV8nwE4DDU53DO7dt0C6gLCqZi+tdatHlyGhjN1lPL4vVbAwPvu2aVOyn7dd4h92ReVhREqAsuxk6XqyFplT0LMILXyklQUpiaVJlfWRkXt7g8P6M8I2Na1KyVpTt2vPjiRgjO/MAq3RKopsDd3lNFbuVDWTj/hmYTj3ctzQYCEIFRVzkfirUheRdcAwB1lpXsnyHAFOVyj2w9hdPk9UsPjVM+Oxv/9cdzx49VliF1wcVY1S84eBg9JavMLlyqeOrhw6mpl4qjooqfiSruM+sErLmHYP7++sijvduVYgfa7gX1+XV6Y48TzoF6WOFPDilfxZHUWWB1VlY+Fe12qTe0wCOIQKkE+SaAQcp6E1JvlZRSYaH+AyCPn1sTnxMqmq2SOsurXl5L6vUWnYFb4KXWJ3v39viFBXXWVFpT/EFY0wOiSjg//03Wmd5ZdRcSL9SJdyN4MRK4cuX69bHvtjWyLn4claHNqFCssfN/ACSSlF+MGKC8+fSFjHPbWOJ4Bw/+1VsldXvVy2sXQ+ug2Fgy108DwIHXPr4gfmHhs4fQDegL0g2dPhI20/2ISwA4B52fv5EeQncAwGk0/HReHj/u5qUGrny+oCBWNPhg48GuKK3GcMkKcR2DddI8IfQYIffvA8hfjEDBBklG4A8AHDj0DnTwr656mAApdZZXvcxWe+bM27e3bQujn/J6CoDH/FFkQs1dBnCiklL4izERbebSUmEMTE3HzOIzOQaw42+dnX/bCBGAFjS/heNXADQ27u+6eLHrIABkGOouKVmdsgyhiooMoU/58/ga1vnzNV/j9beUqB94v02JnwDopFxPzOqCCvUyAZi8rQa/d5f9fwAkcg/APXteApgGFWq0hZM9ANx9fkWTJ4CizOQiAWDBYnR8cf1BYHNq4PMAEAgACfsPgkBXVMWlS+gBso6lapJGqKVFI6T+BQpTz6ywuSzeKVVG6tCxtrZsdQPgeLu65C9W8LLyCxEAgFlm2+2IiHsAMOWpAKgHXKAe8AQE3j5BxMrp/NO4tJQBtFOKpp2sJAPYsTwuOTnuRQbwfcWNG5eEMLdc0kkABxMu7t+f0nWzK75nlrdMxpe8SAGgxA8fYVJlhf+nFpkVvUSn6RQAOCtd39WVi3gJQKS4f0R9bxAATAaAewUFADDlqQD+W9y1hkVRRmGyy+6ygrYleMVCM4sQoRvQKiFSBlG56CZiYYigEIgFlcJWhIJ0YUuUCLMbT1mhS4ClaRJPEQRElhbhpRD1qSyhInvq6f6e832zMzta/arebm4zOzvnnW9n3j3fOe9H8f/gev6HH57vpPZyMAbK0pESpAfz/YKA5YuWvb9skdnMBGCq6PO2lpbMz6l19pWhUZdg8h1ljvLHSOCiZUxASxyw/eM9F7Cbn1LHNGWugYHyv3pJgIcDhSRAla5B/zQCZNvdnj2y7U73/lAiYFVJ3/33980jJXkqAsDA84e+aaorq5MEYCaLlBjiVwgw73z//eadZgAEIAV3O6YB9qN4CASQ1t/KMkP82BEE4Mu/5+ieoyDA6pnVzd3G6Ni3r0P8aVqwNA94nJDcetfnWyRuB7Z80rqDvv8MPA+36y1M9W13escIEACVNW9eX9+8vyIghr0Fnq/r/IEdFnq/xP1fwbHjprFqZyYCvHDaYzRXGBkHJAoCArby5qtJa4KAGctAwIzqTR9/vP3j7Xu20whQ69gwAs7UgbPIfGyRRUYxs1LMCzy6tnWTGj8R8CkDnUfyDyc5WOiyxCtmQmTOGxcXd20cm7mdTIALI4DwvHBYGOopjceO9czaggDcA0TBA+4BIGCSsp1mr8YIAgKrqqs/BrbvOWr1lMa5egJ0WWQQAIhqXgAEqE9BQu+3OuilvL7W+FZKOAmHvYuBkwl4rV81WCB4CmNtgncag+XfKyr0bWyiq7kK2MDQdb2dPALUtzPWywznWolWoFcD/fv1Ul6pE1DKjVmkiloGPgMvPTh/qpGOWjsGoPeZUlF9+ypv//pVTspyLe5S3n/paR5YynvfweDt+qzzEAn5CWhkdySGR2NKMD4+1oH/c5WAsv9lO9qSqJZ5k5LbNgukKuerrxUmKrSXzyTQ2moSuJEgiiouIKBfAPBTpWO0IzJS9rAsWNAWPLR0ZQw9VyIisH1UQcnXnJVdSYjg/U/Twcdvl5/fewzejv0ZSlZ2SDmhsLs7t5w+I2yIozwjwwGxjFcZkflh+iz1L7VBtW+jzc3pzM8CwoyGUM7hBcjz5YIKqTSBaWrWWbTxcVZ6IHhgYNMAZ6Vv7ADEk4J9jgUBE1TpiConQzls5WJji2IHStN+8vErCEzzpSqlEVtnVG0dylnZEioQmMf7y7jnzXMTEDjBF/aHAG/n/YHD54us8xDE7WjurLVXuPDDlAjIiUzPyTcY8ImRKSBAZH0PHJAFF4+/jfDwd2wl5c5jw8xB9cSAzVeeL0tleZ8gpYik6yRlQp0KMSkrXb3uq2EXvpv8LmWluWNFEIAqBDcBqnSMTiQCEH7R/D2lu1ItkJZdBWm+aWkj0qq2YjtnZbkKawbvf4TQ39/d3d/Pf/TZFVjg+xID22l/jv6aiyYOP4DECBNQX9HgKMx3VRAB0Q5k9nNiiYCUICaA4p84ejTCp/25zQ21zCCgvHxmJUZAoYEJkOcLLzQMDE5fsRcaLDQ+BA5to8IwImCA4qcn7cePX6cSAG8zI0nj8WJ6fJQqHeMdiZH5dPk3IXyjOf/rkC5fhF9QUFp69jkoNOSsLBdIzOD9ScGcf+gio/GiQ+dfjxcYMV2SAN6O/YGJzcaJQuoSARXfFDkiwztiYjPzw8opNZcSaTBGRpYnwhwT+59/WEijfux/heI4URk+8+aamZWzzTKNPUyebxKZwRURwskLbSqatCj+nTsPCQJ8/Dyn35kAY27nV7VaAiZdDAjT03gUfdLl79rVbcxw5M+mvjykMEePSyutikPpKkvXEtkxzwQA2wzANv6jT0RBYJcggLfT/ofroKK2NSOi4ZOHOEBAaE650VEUkwkC+LGNf5SkJRFwzWiaGm08QbW+xxxZe/dWOvdmhs901EzP1BAgpO9UR74U4sBZbSYm4KNtOz8iIAlLSlGVSgoB/vUDQWb+bSAIGMnnTlL0ivgcXP62Tbu6zZE54bDW+toPI6CrNC6utPQcGgEsXRE/CGDlxe1Tt8Ay8NAtz9KffWBmtpXCv/NO1RFip9G80+hfh+MTAfmFFbGO0AUdMZnhsbPLUzLSMQjQ05kY5J8YGUv7L2scfaB/XOMLtH+8MysWU9tAT0tfX7gkwGgdIaWvvlZZEPAhj4DPQIDOoYIJ2GdsQFkiDDLcBJyvFjzE5+Dmtys7qDwW1ZIgAFJza0HaCIRf+v3XisMD1+IKAoRIsaRmp2/nP/pEzPAkgM3TcAecOFwc35Gf73C5CuubY9rDQQCMkVPgCms04kVkfvhs3v/9/nHj+hE/E1CE+LmYt69vtyQAOWSY1UkCZPyybQ7KkupCP9yG+ImAG2vUyXYyiLyCCfBvaPDXEGA8Xy14iM9v67Tj4u++dPduJiCgYF7p2WdXVZ177tenfT9CODzw58Wx9OQMlq/9ppvsvufSn/EVmAECKEGnOkIMP7TN/9A1fHwiIL+jor4+ph7FuUxAeUo+EwBvcBDA+7//Pp8PEyDiZ4AAPl8iQErfE4cPc8GSBNr4hDK/Wrb9ieOp8YGAffvEF078NmDpeI1a4DC1vjYxJ5YQDuArMCuwC4MItjaY7Kq6lmtz5VOApScr2DE3QcvjP4APPZ9fYpyyljdetMkWFnJ2lghIsVgc+UYjnoL+QeGz9ftP5cd/bCxYIJhk1tn6F7XC+qzzeP32K94ABAEXAyCApOONkwGRtT1rSLxaPQzAP4qwdKk34wvOEn/xKnDUmzBGB9477w4gj7frfX01hg8MvMbfYRZLmHAX4/35DfyOydjbo5pZJn1zvSXUUmEBVb4L6D+f/yMKQKYRvPKSBgeTUKp7gdT0c3XSNSlaZqzjo4upse0DAVFcDHytgmt3rwDqLNQXbekwAaLAwky1x3w8ofRVua/P4iImwwcGNQ198OBBLy2mMlQSnQGLF/vOnD5scyCjTPEpVnZhFjRtdkrbHX8U4JVUUVFfUeF4z2wjWHN9NtZ5SNFop8PBZXzF6dmjID0/ePjh4vLyYsXn4davd0mI/uKh8CWm2Wwz5uN2ki8xS1tRsMDHQy2ytnfzTn3tMLLQhocNAcETpOPEwaHeBz0IQLM5Q5ixzX4iIzVjZUZ2yr0ls8gQvEw6RNCdZm8+vmLjbXZjsGfbnTGdunBEgYa31/6KehdKS9dMkVlfH79JfdousCSnK7ANPviRlgBIz4TmDx7+xlUyq6T+vpkzUeM0EwSkKSil2l2y2AQBNTWoxiSLTZa2ggA+HipRAf65DxABOBN3HpMImGS42cClc+w4sXmoNfVlDwI4cDm7Ezt7UmpMQkRIRMLqEkYZHCJYOmeGH99xfDcISDWkTvHwPU7npplhskADBDhcaE5fY7EycimrmqvxCU5yBoIAZ0YqbEKH5W678VgFcsz7R4/u3MsIy7ZZFaQCtZMFAYsWGY3bXmACRgoCjGaWtg8h06Ma3N3+4Dlau/xRAd6CAJmCIQJsqanW0zUE5GjihxvdsOyYkEC/iLensB98SZl0iNiLG+bx3cczZ4832g1TZPxyBKRsYTM04XiBr0CM0+VyrrmYSwKmjB+6o2CS77qFC5WSl2hnW1tloiUE99yQoIuoDW3WrP19eAYMGwY16uuN2IDsXbtkSQwREGrYtuydDiLgHZNa22tmKawYQsRUiIIFs2cWOMgA3Ky+tuy2W63eY4d4jgCKX5qxPZFhD5oVaX9xeiPiBwGKQ0T4pszdxzcdnz0+WG2rpPoD5fMofiYgz4HLDygjYKhrfqDvsGTFwQEEVGbh8o84e5h950RuQ5vVtx8MjEP8RIA4YEJX6S7hQEG+xKGGmnfeWW5sJgLU2l4LZX0VApo3SkcIszZ+aeCw+D5gJq8Qcesv3t6bdyN9oBCwocKloKmpyTW4KmHx4mGLnVOyED9QdmxvZlvbk20gYNPu3cfDmQAZPxOwfosYfTTbRZ4kXhdQ/z6AEUfCYLz3QGDwsGS+/A8IAootCfh2+gUdIqlMI2B0H+KfQfFTZ6c6AjgLS77Eoc3L33lnUUcz+RKrtb0Wer86AmKE9jfrsrj06j5NQcMvYzdu5OsvQStKuGd3z8g0Bc7CzY/RyASobYAQckPCTdK3mJukqP6A70G4Aymf52W1EZRvsTWXtHM20hUSndEZVrQt4vKPFFJ58jdNfXPm9I07wZnJfaZt8maxU6D5PCKgbhkufkcz+RKTtJUE8PvlPeD55/kxcPfa0++RM/EA2d9ByRnuY8cV4RU2NSo1dcpULQHlhoxYEf4ZggAZ/jyE31g1NV+N/9iQ3aZp5Fs8nCDOn9sBRDl0SBSyxl5jgy/RZnWnQfunwdWcgPRG3NEgKviZkNs8XErJyW8coJo4jh+pWZNH29pVw88jX2I00eBGENRMvsQsRQUB/H4qxmasB2BuFp0jg+dmrefCxk4iAjhLTO5x08JgTD9pWpibAHiRWSIRvyDgSRDA8SN8ip8IcMdfXX0MBJBvscZHGN5iiJ8IyL5wTDYISLUB6n28FtpftrkxC0d98JCy+9e5peR57FEk8SkI0ElN8iVGaVxNjdFcCF9isV0QwNvXqklvgAjIkUOAAQImGW82KlVaIOACOKmOBwMqATnKUwA8yBEgKWACshQdn3kcbYDsW6w5v7UYeQSaqU6lEUBunLUCbxOGfr90A5qtjiqAYuqsu0yVkqjj9YBeatLmGmRlC4NCF7m3hwbR/zmPtq8FtPZm0bpaXsg/88sWNcuJ/81QGFCW01DA8k+iCsD+HrtwOhonqIh9pZgCYpghfIXF1RcNegLu1rVeb0+p2pDkmTcmWenO4QI2BXJIXRYVdUWS5h1508aqWXZAX2sszNDUz1uvgvXzKZf40MwX6R0puCXvVeC009T0uSZGL5aimlrgsbq2NdPARqFSAgp4++juYqdmsawwesRrpbPNs1Y4NcpiycbuLqcLv7OzKqfe8d6XG0UWF4Djg77WGFIaULPU6kQJpm0efXTtqZf4GFD8vkx6RwquRdYsEeI9aRSyppw2JYwHATiQphZ4rK5tDVnV6kt8gbQZcVuxHQEmInBgMyAIuIZqd6Ujg00bPhPgb8/KaiqrbGrLbNkNApAvp/dI5OprjSGllx9oKiiQWV8QgMB/+OabH14ngIBTLfGB0IXXGQjQOVLk0WSvcJTg/b1HjRmT3NWVfDWDCcDxNLXAcqkrV0y3UGKUVv4KS06k4a5IvsFGg82W4pTxny4IQPzI+E1sngil5yZABvhCtr2msrKsrL2sJbNpSWwYCHjpvQx1u77WGAQ0lXVtLaiSWV8i4BCmYcYJBtby8ckugn1ozf5iBHD8TIDekSKPJns1S4SMRU3pxStXagkAnZpaYNGuHjElLcIqCVhY2DCnetjWrajuRUbI2L1ypc3s3Mzxn75ZElDnP3L4yJ3NUHoKAcoVDsKZVFa2tcMvP65lScvUOx5JwdpRe1ezozwmS30CRslaY5WArtTcLrmEBxMw7hmgkVYgen2tCDg1JCRVU5w9wPEzAXpHCnah1SwRMgQP3ITkZDseusBz8V6cNVVrgQUBFYGrdwRWSHO0woVz6ue8m3z2OaVLUZxs6541q9uwsuH4McJxk5l+506sI9P+kcNJKofILyjPWI7CXB0IaI/tmUEE7G8JuyPSkIFs0XEpTVuJAG2tsSAgI7iKs54gAN/9ZwjjBAHpQnnWObOF9BZKEvFLAvSOFAoBSOLheIIAFDFnX6olQK4mp86vm8v37i2HYwET0DBnznx8P7efc24ptmMEVNhsIe4sKxFw/sSLzIdkgYM+CxtKBLS0NM3vw11uMBNfgUhaNkuugLYaI0CNX0rpAy1dUWVx4v0g4NFHrxUj4DUQcKcgIDUqCgSYFQIGZPyt75r0jhRUIHF/ibpECBEA45mNl3KPPAgQq8npCDBmwARItKlRre2cBvpl0Ps4B2zrtmVPkPFJApBTbTbX1TWPBAH6goWhWI+wMhMFUC0tRwaXbAYBuP4Z6nS5rtaYf0scaKqqKsX7FQLoHnBtx2uCAGVPbvNKZwKMRhl+77smvSPFipmo9OD4BQFGIDk7N5mPgQssaoU1tcB6H18QUN9O8QNzh3LACcPUggQmgB4AdTv9rxl+1clLbnh3pq3bvHl+S8sgsGTzbBCwyuJu6zHX6muNJ9MSH+/jAPx+IgC3vh8OH0b8TADf1QFaLg1marcyAQNMQG8rCNA7UqygUieO/1U+Ht+YduzINQv4i1phtRYYBEzx8PFFbW77EqXN7N2rva/tDtEvqWH+uyU3QMDqrErG5vDNRMBe7ZoarfpaY7HEh/r+9fT4B15nEAGA6LYGmACcungMAia9IwXXInMWex4fz6wWTwgChhJyGd6EC7QqDTB5ojVNV5BAVN+od3AANJP0c8NUeTo7r3U8jqsuqaGrNZZaW33/ep37WR5B02amb03TO1LQXis2cIGEPF8mxw0vo4TSO6lRngycm8f6c3mL895Tz2D7IGRuUvQR8i6Tvr46qXoGgAINLomYCgz19qw/GeMMv2l8uPNxxQhZ3/ZmtCkwQ1pbLM+6cQvDKODuHLuccBrjlFL6KkDbR6f3Fc5YzwVaAi7X3WshTRmyE9NUbFxsSHwPwJewweXaHw2dW78SSBPS9Ko6T6l6BrLHqATOEXg6zDvbZseyvAEy6zu2MiElISTFnuh0kt1g1lSeKFXPx6Jvw4MpitYW5Rb9+bO5GytfIX3VeISPsFqwIXyJ9b7C/kgZKVnrzrIyFwhwNyPj7rTMlFecQrGvATrLmpYhY5SV5YLUTGNpSgURNVqpCgJycvCDTVr0gQCbPcAOF6ULpZMUChsnTAAdYoa/CATgt4Z6PhabgWtm+bUgQLPuDlas0J0/CEBgmtXx1HiEj7BnBsq80+slt0cwrW35yB14g7L/fU1N5SBgUd225prmZvzT8QIIWJyBq4/w9zaVHXiBCWgX8Z+tFEQs12QYckHADcgv5CN+SUDqJVi2WcQPAi5IwHjxi9pRVNQCFE2FoUIGtxKuIkxPeiUxalSq36jixYziFZ9tOwQoo+DDZyUBLpdRIQAXViN9RTx3bdnyKKUh7lrrE8J1pAUFUqh54bHEEBO6L92xXsaP3ekNdxIBzc11zXUdy5mANcZVxmJx+V9A3osIcLnjv8SeS1ng5WrbSOhS/ZIYdlsCHtDSIv/C8UUJiVEbEzc6isKZgLAVM+1m+xrCQWBNdN4jAci8+zqJEJTu3qp+PTRSuK4C+dHl/BoE0Fp2Bw4I6QsCEM2WlIwMUPDoQyCACyZm4IRYamsJoCzFS3dgvh1QZpxLvkCWt3lnc0dH3aLlNcsQcF7kquJVuPxNB16QBLTL+M+eYIew4CzwIqVSDwREqPETAUNxBTTl9xfMjSzescNZviM8fMCR4ggHAZhtUOJ/GQQsDh6VGuI7cxURsMZNgHL8IL5gD3f+8ENPA7JMd93Jnz8aNSaHxep44oLiB3IK4gcBomAibdy4UsSvJ+AOEKAvOJisLqbGAa/A+HfSt5/iv4wIcHH8IwKy3W12y/3l+TEBFL+6GpzNMwucixHEX38QMLBsERGAG4wHAaHOmc7a6Rw/E6B9vyRgeWddTc+yh4gAWcDR3y+lr/ARvj09/faHeLuQ3jNQyS1Xm5u28WfCbwI/t+oLDkiaNjMKmwUBaxo6cfk5fiKggeIfRj/OcEtpvhxZ4EWaR23hkJynn0b80qP0uTAmQOMHEO1E/JVU4VS0bFlReNjcL38W+Jjwc+/4jW/nTg/FuuF8fuvmHpSOQwC7zrBP8H03d7bcdwNPtbEZm0b6Ch9h3Ai2KFNxbqXGaX0vvXRFAB7L0REBYt21ukV0xfPqcfkXyfiR9Y12pQ3zTbCiBubQRcOx/+XXLJqjdWgAAc/h+iN+JmC2TY2fgBGgVHjtxlK54WGn8AkOsEepr1es4tEB5AEHo0Wef0ts7O0iQM5Sq6vjgQB1KpK2mw3ysy2M0JPa5k7K8roNKd4hmOZ0lnVqV6ML2+Vn99/ZXDdyotj/suWeDg1UEIG7AB4CjNlmXe1wvJPL3ABRkPFPPsG3riIo3xEQIGcZRZhEgPoUoP312y93t/HJ1eZOMifTFRwAJi2ODr7g8frdd9+/6jLs7y5AMHmC5B+yzO4SB5Jz0gwil0ACkHPCEv/kE6zvslOFsgCXVyAHitU5dFJabscO2iy211kmT4zXFUioApyxoiF4UrCKKVfrs7TwRvFwJt7Rdvqxj4cc26Skvrm0gl0hNrAWlu+9SpGm+uONB7T11nkEFvj4B2jV7T958uPT5k4+7zvluumPZxZQzdSefEVncRHlKRXvhLXMI8WPKHeeFfWpU66+2I2bxuuztDeopjkPA2+dIWt9xSIwsWFsniYW1SA5PFYWSLg/T18wofcN5l+D5JPlqidtkGTq3OXx+ZM7MLkB++7QDp7BMZ3sU5zqB6td5TUIeH29RyelT9QkjfEuCPDw+gIBWEYZi2lLPL5dn6X9vkK7uvqun0St78bg2KL89vZYIgB5e9EoCCFABCRkB4waFSgelWVy9ThVCut9gykfkJ7TiQVPmnqK1tyfZJrfE9ilfj4I2LFxdce+jn3+b/ASG3x+2Zj/svtJn+JRtByesj8IwK+kyFSLgoU+fl1pJcDoRrqTNvanpKutuUBxvXVXdwgYUAjQL2xMxcvrqhcutNqruc3tmFzSIraoKbCqpWg2ETBTNEqyEPLB9Ugd5et2f6tkSyMH4AQc0eK5H1NREWHj43OOL316J9DUfpAIWNJXUqDWOk/uwFjZV7gv1PLGp5IAX7vdzzfAHjJB+BRnj4Kxsbrw8hkPbXvo0ewQBe9CKnaljR5dMoj4B68dfcTgqbUt9fVL2g3Z5yhfKzYsMDaT+dghiyQgrQWPgVBrbkvuu9W9+bLWt6ioottNADu9BUIOEwF2q93X94QEapI4feLOOhs5/u6KCmuMQkBDw/T0+9e0d7b3HLw/2tQQtHB/ybw0WTsMAlZvWr3vDf+gjn1MAElfu1+C1c8vdQJtlxdMXXj5jIefKXxw/c8+Er1QSl1bYex73eC4/bcNjpMEpNTUpIiChvr65x21BssxBXRArK6N+M+/iKRv647OzoUNDXMKl7TX7tmDEeBYwKvLhYe3NLWAAG7MdHG36BgmIISywr7utrloJ8evpt0pfuSpkaN2kfSFUnQ1dC5Ys6aop70FvxVMFqyEg4qVNFkLfB4TsG/fGxQ/pu9J+dl9rX7D7NZRtF1XOwwCHq149MEv8UoABPAIaBwcd+2rg9cyAXyNm2XBQkPnlztiUqBZBIbwCGCLjzp/MxPgKK+GCij0r9/elrO9N56qLlnptBw4MBg+m5e8cFH8IECt5j7BGH7iininev1PT9osa4PxiypGSGsQ0NlQ1g4CsEY6pDKPgMZ5aUoW+rw3Vg+sw7y1nL4XBASEWBP8Un1puz5r7XXWaw8+mNJtVbDQZ8LWNEUJv/pqY3+k+v0X94DumApHtLpiob5NjdvcPr7utsJaavOSBIQTAZktLWeFzz6dZmpcFH8ZF0EtjaCeYVmQgIWTk4o1M4+VWVPNuuODgPbOpibcAfct20cEzJ+zv0TMoigEVK/m+CUByDonJEwYAWfJS2i7LmsNAh5c/60GV/gEY4EkjVsc33SgvbDEHdTXqlvxFFgQPUSF3pzse9z+GVWEgp9AgIj/0ieBcNPp90xfsMDF/cJXEgEbIsoA8l0mxA3qzdN4Ieh3VOmNLG9WT1N7T0/PvmUvEwFL+maUqtIZBLy9eqMIXxKAeO2pVmvCKN6ul9pev6z/9lktAd471BwtcF6e6vIEHkBAyu54TfzxenMyOFMzygWGTOXHP0HU+t56j3ITdF0IoJbX8/N88MiWE0sEb/1C0LfiPJwNrsCypvY3yHHC1FMwSiOVQQAeg7J8AzD9g7TGCPiOcYWCabqCB9XxVqAt3mPR1l9MOkD+aZ2Jz9CW+tL205OAQV43mBPQemmql776haClFI6Pjxbo1e1vMs31qDn4J2ntpZeKVgzkB6y+7tetEr2M7b0vM2B6JrerWdbLTxzBB+qzynqCshT4BfAMvX7JjPjElKypUxMdiZI3xV3CIrPEdDlOkyDmXj1yhMsfFOxou/XYx0mQ3sBUQH98fbxeeql4jq1h/vwGm1153bpDwaZO16ae3pdp4QG4aSvb3W1uFzWW9KHAAQUNgFrQYFINHAmmLMMW+sv4ovimN5htFVjj62HCzcDp8UYkiOm2K+6Cs3k1OpRVKlnhvPe43oHTvlSQ8X7UykPyNWFpkpDexe4CjgqrrbvCUIG/u7u7K1z6eEWBREKC6sBgt7UvXDjfliBf66XpyzcXw4UX5dlyu2JudrgR1lq37R+k6WwOXRY0cIpN9SF+NWuLdCDBrDD8xqZYUHpbwfe8dEJkfEa6IyMyIzIofDM1SIAAIRttstY3773pq5TjkTna+4unf6M5/lLZZrfaXcBRERGD6CNKbLaIwLLGTindu7oUKcxS0Wq1qw4MCWBgznxriHgNy1as2vQmgMLNuI4hgoDp0y9Us8Bk7tXYuB/3wMHGfhCgncpae5pYKFlK3XlHs7YYHzM+Zn5sPY3LWeZCEFCyEi1jW7bwyh5vtX6ptAF+DFSblMXYbObuzs5uKwhYtQrF2qNJqpOP8WlEsOpzvEFI7417Kzcvwn0QBEBDlJQsdux9zzXuSFl3EMULFMxQpDCEiJ/Nb1jACOswxYEhwTZ/DjHAr/F+Q4qM/+mON0EA1ieFR+aFQkoyAbj8TXPQlHek8dAHTMBTMn5MZgqhk91gtIv9s7Y8Rlj/li8oP8dvndkaE2M1SpdReIzqsr6FICCCYMzo6Ww6UiEIOHzg8OETh6+l2uM8nqVIxwDiLHJSFknv4tq9mzfvq2letjnMaQx1BZY4sVNZo6sisZDPZ96M0aPj4s5mKQxlZLdhPCOppUhFMICCgCXWEHptaG7GIBDxPx3XEX36zewRugBnL9vi6PL34RnY19j45utrP3n4ecKbEpdCGAHGhiVGaoDfjnsALr/lQf8P+L6UXm+hiSCcvkShrna4cKkwWcFPIXNPj9koCDgwsbFxeP+1JJ3xGvEXrzlYnIEs2ZqkY85KVHdnEQF1ze+AgIxIgyHCFpy7uqy5OAMEsI0vjZcROH8mAPEGQCj5ZZ/rlooh1iW33bbEGoXXMRUx3Rkcf08cLWV98kLJB+jyX4fLX0fT16d5ZpVp/UASxsaL68XqcTwCHnzrg5eZQb/qG1J4+Ct4K10bv4YAY4WrtrY+NHSFGAEnTvQfuZZylnjN8R8EA5QjjHZL6X3LQMDs4sgUw7JAIqAx0uEPAvj8S5EWl1KYpKEd9Xw0Ia9KRTDwwAMLU6PO9jZ0d3P4lOmJewME6KTkVa6SPmigvsbDb74mCFDjJwIGXU3AEQX70Umi+qQGpba/fLNqsksE97KUdsO0IUa47GCuqbbWbAmlgFHwcWI4jk6lt71uvwdRshOfpfyU6Ozra9rMXWaNByqaWppccUGQ0uL8x20dgaSxJIDiDaH4tVIxxLrwgQfmpIZ466WpXkp+4VooLj8qWCQBavyvjtvwjOfrL/yy/ahVW3yDfAKqM/j+z4Crr6VQ5yvMBAQCZloMGgFQVrgEXYX9OBoRoD8fECB/SvUAggBzs6UszlVcaGYCeK0KavbD/kzAqaUixsB1ty1J9e5Vbsp7qvYgw3GStCQp3NdY8vzrDBCgPvUIG3y6BLYKeAepbFrS/f27XlZshm9gRF/h6SsMAuRTgN7DBOArII7feKqCjHihH+QwYAL487qRpmMC9FL4r6Virgmo7WVAYP7Ue0ppif+1/4sTH7izrm5jsA0C+v2nELhEpJrhr1teTilEUCCOcvRortxpxYqkJOXopyrI0LflWdxrTwicJIUf2GCaq5WGSTC4nzZtndvyIgzgo2G7B2SNw1VXjQw9R/N+/epzQZM1OWZgnhszGJfq8MckTbGtbdIfXv82TD0xAzs00jDJiaxncIIsY1s3Nyy/PMgRCTsouR0ODVF+qpPt2P66ukOWBPX9l9cp6CkoaEk7z2io+YaADlfCVaNHqEBKqErGHa4QkD3l92xeZZWqAX+fku31b8M0vy8QpbCKFGYCVq97e906tvYhAiLb2spRmy+2gwBEfoni4njJ2MGYi5ZftDNhgnw/CLhIunuPXJ6WVjMZN9FOrRSeN8LdIgkwAUVFOQtynAvuKSrCC4Ph1z9+tRm6ugw2/MFg8Pq3QVnVsq+q3VlSImAdCEhel2tMTU5uRYNCZnkbehPk9pBsuwLy6LzQ1BlxzfKROy3yfweDAMR/jSwrWT7ZuDLBMCBvgj/9tHU8CKDoq6q8CRczAU6MAAyBBQvwgi/879lRUfRvw39BgCuwqa9MWeh4jkkSkJycm1yLv0BAZmI59WZI6asvUKC8PFWLi6zGyCtAgDR3H3PObQ+keUfFzAqJql5XnZzMbnCt80Yg/LRzq6puSsPEEAgQGOjJFH8wEH4dExx8MS7/f0JA55KyOftlv8WGsj3JYi2L5GRj7eNvm0FAW2Ybxf+LlL46qUq+vX2B15xPFilw9Zl43uV1irm9IMAeMmuW3Sj5hRIUBFS99VZV2lg3AZkopJQMSJ/jm25KMPxHBPS0NO0vk+eHE5wWLK29UpPffhwjQC999W1uuIeU1cD1REwlnT8ZBMjhf+W5D4AAc8isAnM1H5L79ogA79KqHxdV/aQSgPjBQLgkgG8D+Ps/ImAJrv+c990LKU9bLU82udZci2puvfRtL9Sux19/namzERUFO/3FdGBklljiYqRKAHyWv8Is4k8//cQNGCDAG6iqajmGphVJQHgPCBhQRkAqf/v/s3vAEjV+QQDHT0DG7vFWvdTEkFduGDxiBiOoXWLxGqVgQV3i4qZzHzCVggBzzziNFJ43huMvrfqpCk07IICR2TMwHwNAfQoA/9VToM+15HzNQspz8fgHkiUNraeQvu48MGDqp6fgYnfFQrS6xMWFY667rdTbaK45wBBGF5fNGKN1uU0GAYz5bh1wCS484T/TAUdNk7ULKSuFvK0SJ0lfHS677MzyFZrV1NQlLi6Aj9dYb3+T55IXM9CxogAcV/3vSvC/Bj1utPD6n/EnnaQbrf6BCX0AAAAASUVORK5CYII=)}.react-tel-input .ad{background-position:-16px 0}.react-tel-input .ae{background-position:-32px 0}.react-tel-input .af{background-position:-48px 0}.react-tel-input .ag{background-position:-64px 0}.react-tel-input .ai{background-position:-80px 0}.react-tel-input .al{background-position:-96px 0}.react-tel-input .am{background-position:-112px 0}.react-tel-input .ao{background-position:-128px 0}.react-tel-input .ar{background-position:-144px 0}.react-tel-input .as{background-position:-160px 0}.react-tel-input .at{background-position:-176px 0}.react-tel-input .au{background-position:-192px 0}.react-tel-input .aw{background-position:-208px 0}.react-tel-input .az{background-position:-224px 0}.react-tel-input .ba{background-position:-240px 0}.react-tel-input .bb{background-position:0 -11px}.react-tel-input .bd{background-position:-16px -11px}.react-tel-input .be{background-position:-32px -11px}.react-tel-input .bf{background-position:-48px -11px}.react-tel-input .bg{background-position:-64px -11px}.react-tel-input .bh{background-position:-80px -11px}.react-tel-input .bi{background-position:-96px -11px}.react-tel-input .bj{background-position:-112px -11px}.react-tel-input .bm{background-position:-128px -11px}.react-tel-input .bn{background-position:-144px -11px}.react-tel-input .bo{background-position:-160px -11px}.react-tel-input .br{background-position:-176px -11px}.react-tel-input .bs{background-position:-192px -11px}.react-tel-input .bt{background-position:-208px -11px}.react-tel-input .bw{background-position:-224px -11px}.react-tel-input .by{background-position:-240px -11px}.react-tel-input .bz{background-position:0 -22px}.react-tel-input .ca{background-position:-16px -22px}.react-tel-input .cd{background-position:-32px -22px}.react-tel-input .cf{background-position:-48px -22px}.react-tel-input .cg{background-position:-64px -22px}.react-tel-input .ch{background-position:-80px -22px}.react-tel-input .ci{background-position:-96px -22px}.react-tel-input .ck{background-position:-112px -22px}.react-tel-input .cl{background-position:-128px -22px}.react-tel-input .cm{background-position:-144px -22px}.react-tel-input .cn{background-position:-160px -22px}.react-tel-input .co{background-position:-176px -22px}.react-tel-input .cr{background-position:-192px -22px}.react-tel-input .cu{background-position:-208px -22px}.react-tel-input .cv{background-position:-224px -22px}.react-tel-input .cw{background-position:-240px -22px}.react-tel-input .cy{background-position:0 -33px}.react-tel-input .cz{background-position:-16px -33px}.react-tel-input .de{background-position:-32px -33px}.react-tel-input .dj{background-position:-48px -33px}.react-tel-input .dk{background-position:-64px -33px}.react-tel-input .dm{background-position:-80px -33px}.react-tel-input .do{background-position:-96px -33px}.react-tel-input .dz{background-position:-112px -33px}.react-tel-input .ec{background-position:-128px -33px}.react-tel-input .ee{background-position:-144px -33px}.react-tel-input .eg{background-position:-160px -33px}.react-tel-input .er{background-position:-176px -33px}.react-tel-input .es{background-position:-192px -33px}.react-tel-input .et{background-position:-208px -33px}.react-tel-input .fi{background-position:-224px -33px}.react-tel-input .fj{background-position:-240px -33px}.react-tel-input .fk{background-position:0 -44px}.react-tel-input .fm{background-position:-16px -44px}.react-tel-input .fo{background-position:-32px -44px}.react-tel-input .fr,.react-tel-input .bl,.react-tel-input .mf{background-position:-48px -44px}.react-tel-input .ga{background-position:-64px -44px}.react-tel-input .gb{background-position:-80px -44px}.react-tel-input .gd{background-position:-96px -44px}.react-tel-input .ge{background-position:-112px -44px}.react-tel-input .gf{background-position:-128px -44px}.react-tel-input .gh{background-position:-144px -44px}.react-tel-input .gi{background-position:-160px -44px}.react-tel-input .gl{background-position:-176px -44px}.react-tel-input .gm{background-position:-192px -44px}.react-tel-input .gn{background-position:-208px -44px}.react-tel-input .gp{background-position:-224px -44px}.react-tel-input .gq{background-position:-240px -44px}.react-tel-input .gr{background-position:0 -55px}.react-tel-input .gt{background-position:-16px -55px}.react-tel-input .gu{background-position:-32px -55px}.react-tel-input .gw{background-position:-48px -55px}.react-tel-input .gy{background-position:-64px -55px}.react-tel-input .hk{background-position:-80px -55px}.react-tel-input .hn{background-position:-96px -55px}.react-tel-input .hr{background-position:-112px -55px}.react-tel-input .ht{background-position:-128px -55px}.react-tel-input .hu{background-position:-144px -55px}.react-tel-input .id{background-position:-160px -55px}.react-tel-input .ie{background-position:-176px -55px}.react-tel-input .il{background-position:-192px -55px}.react-tel-input .in{background-position:-208px -55px}.react-tel-input .io{background-position:-224px -55px}.react-tel-input .iq{background-position:-240px -55px}.react-tel-input .ir{background-position:0 -66px}.react-tel-input .is{background-position:-16px -66px}.react-tel-input .it{background-position:-32px -66px}.react-tel-input .je{background-position:-144px -154px}.react-tel-input .jm{background-position:-48px -66px}.react-tel-input .jo{background-position:-64px -66px}.react-tel-input .jp{background-position:-80px -66px}.react-tel-input .ke{background-position:-96px -66px}.react-tel-input .kg{background-position:-112px -66px}.react-tel-input .kh{background-position:-128px -66px}.react-tel-input .ki{background-position:-144px -66px}.react-tel-input .xk{background-position:-128px -154px}.react-tel-input .km{background-position:-160px -66px}.react-tel-input .kn{background-position:-176px -66px}.react-tel-input .kp{background-position:-192px -66px}.react-tel-input .kr{background-position:-208px -66px}.react-tel-input .kw{background-position:-224px -66px}.react-tel-input .ky{background-position:-240px -66px}.react-tel-input .kz{background-position:0 -77px}.react-tel-input .la{background-position:-16px -77px}.react-tel-input .lb{background-position:-32px -77px}.react-tel-input .lc{background-position:-48px -77px}.react-tel-input .li{background-position:-64px -77px}.react-tel-input .lk{background-position:-80px -77px}.react-tel-input .lr{background-position:-96px -77px}.react-tel-input .ls{background-position:-112px -77px}.react-tel-input .lt{background-position:-128px -77px}.react-tel-input .lu{background-position:-144px -77px}.react-tel-input .lv{background-position:-160px -77px}.react-tel-input .ly{background-position:-176px -77px}.react-tel-input .ma{background-position:-192px -77px}.react-tel-input .mc{background-position:-208px -77px}.react-tel-input .md{background-position:-224px -77px}.react-tel-input .me{background-position:-112px -154px;height:12px}.react-tel-input .mg{background-position:0 -88px}.react-tel-input .mh{background-position:-16px -88px}.react-tel-input .mk{background-position:-32px -88px}.react-tel-input .ml{background-position:-48px -88px}.react-tel-input .mm{background-position:-64px -88px}.react-tel-input .mn{background-position:-80px -88px}.react-tel-input .mo{background-position:-96px -88px}.react-tel-input .mp{background-position:-112px -88px}.react-tel-input .mq{background-position:-128px -88px}.react-tel-input .mr{background-position:-144px -88px}.react-tel-input .ms{background-position:-160px -88px}.react-tel-input .mt{background-position:-176px -88px}.react-tel-input .mu{background-position:-192px -88px}.react-tel-input .mv{background-position:-208px -88px}.react-tel-input .mw{background-position:-224px -88px}.react-tel-input .mx{background-position:-240px -88px}.react-tel-input .my{background-position:0 -99px}.react-tel-input .mz{background-position:-16px -99px}.react-tel-input .na{background-position:-32px -99px}.react-tel-input .nc{background-position:-48px -99px}.react-tel-input .ne{background-position:-64px -99px}.react-tel-input .nf{background-position:-80px -99px}.react-tel-input .ng{background-position:-96px -99px}.react-tel-input .ni{background-position:-112px -99px}.react-tel-input .nl,.react-tel-input .bq{background-position:-128px -99px}.react-tel-input .no{background-position:-144px -99px}.react-tel-input .np{background-position:-160px -99px}.react-tel-input .nr{background-position:-176px -99px}.react-tel-input .nu{background-position:-192px -99px}.react-tel-input .nz{background-position:-208px -99px}.react-tel-input .om{background-position:-224px -99px}.react-tel-input .pa{background-position:-240px -99px}.react-tel-input .pe{background-position:0 -110px}.react-tel-input .pf{background-position:-16px -110px}.react-tel-input .pg{background-position:-32px -110px}.react-tel-input .ph{background-position:-48px -110px}.react-tel-input .pk{background-position:-64px -110px}.react-tel-input .pl{background-position:-80px -110px}.react-tel-input .pm{background-position:-96px -110px}.react-tel-input .pr{background-position:-112px -110px}.react-tel-input .ps{background-position:-128px -110px}.react-tel-input .pt{background-position:-144px -110px}.react-tel-input .pw{background-position:-160px -110px}.react-tel-input .py{background-position:-176px -110px}.react-tel-input .qa{background-position:-192px -110px}.react-tel-input .re{background-position:-208px -110px}.react-tel-input .ro{background-position:-224px -110px}.react-tel-input .rs{background-position:-240px -110px}.react-tel-input .ru{background-position:0 -121px}.react-tel-input .rw{background-position:-16px -121px}.react-tel-input .sa{background-position:-32px -121px}.react-tel-input .sb{background-position:-48px -121px}.react-tel-input .sc{background-position:-64px -121px}.react-tel-input .sd{background-position:-80px -121px}.react-tel-input .se{background-position:-96px -121px}.react-tel-input .sg{background-position:-112px -121px}.react-tel-input .sh{background-position:-128px -121px}.react-tel-input .si{background-position:-144px -121px}.react-tel-input .sk{background-position:-160px -121px}.react-tel-input .sl{background-position:-176px -121px}.react-tel-input .sm{background-position:-192px -121px}.react-tel-input .sn{background-position:-208px -121px}.react-tel-input .so{background-position:-224px -121px}.react-tel-input .sr{background-position:-240px -121px}.react-tel-input .ss{background-position:0 -132px}.react-tel-input .st{background-position:-16px -132px}.react-tel-input .sv{background-position:-32px -132px}.react-tel-input .sx{background-position:-48px -132px}.react-tel-input .sy{background-position:-64px -132px}.react-tel-input .sz{background-position:-80px -132px}.react-tel-input .tc{background-position:-96px -132px}.react-tel-input .td{background-position:-112px -132px}.react-tel-input .tg{background-position:-128px -132px}.react-tel-input .th{background-position:-144px -132px}.react-tel-input .tj{background-position:-160px -132px}.react-tel-input .tk{background-position:-176px -132px}.react-tel-input .tl{background-position:-192px -132px}.react-tel-input .tm{background-position:-208px -132px}.react-tel-input .tn{background-position:-224px -132px}.react-tel-input .to{background-position:-240px -132px}.react-tel-input .tr{background-position:0 -143px}.react-tel-input .tt{background-position:-16px -143px}.react-tel-input .tv{background-position:-32px -143px}.react-tel-input .tw{background-position:-48px -143px}.react-tel-input .tz{background-position:-64px -143px}.react-tel-input .ua{background-position:-80px -143px}.react-tel-input .ug{background-position:-96px -143px}.react-tel-input .us{background-position:-112px -143px}.react-tel-input .uy{background-position:-128px -143px}.react-tel-input .uz{background-position:-144px -143px}.react-tel-input .va{background-position:-160px -143px}.react-tel-input .vc{background-position:-176px -143px}.react-tel-input .ve{background-position:-192px -143px}.react-tel-input .vg{background-position:-208px -143px}.react-tel-input .vi{background-position:-224px -143px}.react-tel-input .vn{background-position:-240px -143px}.react-tel-input .vu{background-position:0 -154px}.react-tel-input .wf{background-position:-16px -154px}.react-tel-input .ws{background-position:-32px -154px}.react-tel-input .ye{background-position:-48px -154px}.react-tel-input .za{background-position:-64px -154px}.react-tel-input .zm{background-position:-80px -154px}.react-tel-input .zw{background-position:-96px -154px}.react-tel-input *{box-sizing:border-box;-moz-box-sizing:border-box}.react-tel-input .hide{display:none}.react-tel-input .v-hide{visibility:hidden}.react-tel-input .form-control{position:relative;font-size:14px;letter-spacing:.01rem;margin-top:0 !important;margin-bottom:0 !important;padding-left:48px;margin-left:0;background:#FFFFFF;border:1px solid #CACACA;border-radius:5px;line-height:25px;height:35px;width:300px;outline:none}.react-tel-input .form-control.invalid-number{border:1px solid #d79f9f;background-color:#FAF0F0;border-left-color:#cacaca}.react-tel-input .form-control.invalid-number:focus{border:1px solid #d79f9f;border-left-color:#cacaca;background-color:#FAF0F0}.react-tel-input .flag-dropdown{position:absolute;top:0;bottom:0;padding:0;background-color:#f5f5f5;border:1px solid #cacaca;border-radius:3px 0 0 3px}.react-tel-input .flag-dropdown:hover,.react-tel-input .flag-dropdown:focus{cursor:pointer}.react-tel-input .flag-dropdown.invalid-number{border-color:#d79f9f}.react-tel-input .flag-dropdown.open{z-index:2;background:#fff;border-radius:3px 0 0 0}.react-tel-input .flag-dropdown.open .selected-flag{background:#fff;border-radius:3px 0 0 0}.react-tel-input input[disabled]+.flag-dropdown:hover{cursor:default}.react-tel-input input[disabled]+.flag-dropdown:hover .selected-flag{background-color:transparent}.react-tel-input .selected-flag{outline:none;position:relative;width:38px;height:100%;padding:0 0 0 8px;border-radius:3px 0 0 3px}.react-tel-input .selected-flag:hover,.react-tel-input .selected-flag:focus{background-color:#fff}.react-tel-input .selected-flag .flag{position:absolute;top:50%;margin-top:-5px}.react-tel-input .selected-flag .arrow{position:relative;top:50%;margin-top:-2px;left:20px;width:0;height:0;border-left:3px solid transparent;border-right:3px solid transparent;border-top:4px solid #555}.react-tel-input .selected-flag .arrow.up{border-top:none;border-bottom:4px solid #555}.react-tel-input .country-list{outline:none;z-index:1;list-style:none;position:absolute;padding:0;margin:10px 0 10px -1px;box-shadow:1px 2px 10px rgba(0,0,0,0.35);background-color:white;width:300px;max-height:200px;overflow-y:scroll;border-radius:0 0 3px 3px}.react-tel-input .country-list .flag{display:inline-block}.react-tel-input .country-list .divider{padding-bottom:5px;margin-bottom:5px;border-bottom:1px solid #ccc}.react-tel-input .country-list .country{padding:7px 9px}.react-tel-input .country-list .country .dial-code{color:#6b6b6b}.react-tel-input .country-list .country:hover{background-color:#f1f1f1}.react-tel-input .country-list .country.highlight{background-color:#f1f1f1}.react-tel-input .country-list .flag{margin-right:7px;margin-top:2px}.react-tel-input .country-list .country-name{margin-right:6px}.react-tel-input .country-list .search{position:sticky;top:0;background-color:#fff;padding:10px 0 6px 10px}.react-tel-input .country-list .search-emoji{font-size:15px}.react-tel-input .country-list .search-box{border:1px solid #cacaca;border-radius:3px;font-size:15px;line-height:15px;margin-left:6px;padding:3px 8px 5px;outline:none}.react-tel-input .country-list .no-entries-message{padding:7px 10px 11px;opacity:.7}.react-tel-input .invalid-number-message{position:absolute;z-index:1;font-size:13px;left:46px;top:-8px;background:#fff;padding:0 2px;color:#de0000}.react-tel-input .special-label{display:none;position:absolute;z-index:1;font-size:13px;left:46px;top:-8px;background:#fff;padding:0 2px;white-space:nowrap}\n"]);

  _templateObject$i = function _templateObject() {
    return data;
  };

  return data;
}
var ReactPhoneInputStyle = createGlobalStyle(_templateObject$i());
var TelStyle = createGlobalStyle(_templateObject2$b());

function _templateObject7$2() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  ", ";\n  ", ";\n  appearance: none;\n  background-color: ", ";\n  outline: none;\n  border-radius: 1.5rem;\n  border: 4px solid\n    ", ";\n  padding: 1rem;\n  resize: none;\n\n  &:focus {\n    border: 4px solid ", ";\n  }\n\n  ::placeholder {\n    /* Chrome, Firefox, Opera, Safari 10.1+ */\n    color: ", " !important;\n    opacity: 1; /* Firefox */\n  }\n\n  :-ms-input-placeholder {\n    /* Internet Explorer 10-11 */\n    color: ", " !important;\n  }\n\n  ::-ms-input-placeholder {\n    /* Microsoft Edge */\n    color: ", " !important;\n  }\n"]);

  _templateObject7$2 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$2() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  ", ";\n  ", ";\n  appearance: none;\n  background-color: ", ";\n  outline: none;\n  border-radius: 1.5rem;\n  border: 4px solid\n    ", ";\n  padding: 1rem;\n  -webkit-appearance: none;\n\n  &:focus {\n    border: 4px solid ", ";\n  }\n\n  ::placeholder {\n    /* Chrome, Firefox, Opera, Safari 10.1+ */\n    color: ", " !important;\n    opacity: 1; /* Firefox */\n  }\n\n  :-ms-input-placeholder {\n    /* Internet Explorer 10-11 */\n    color: ", " !important;\n  }\n\n  ::-ms-input-placeholder {\n    /* Microsoft Edge */\n    color: ", " !important;\n  }\n"]);

  _templateObject6$2 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$3() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n\n  span.icon {\n    position: absolute;\n    right: 0;\n    top: 50%;\n    transform: translateY(-50%);\n    cursor: pointer;\n    padding: 1rem;\n    padding-right: 18px;\n  }\n"]);

  _templateObject5$3 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$4() {
  var data = _taggedTemplateLiteral([""]);

  _templateObject4$4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$7() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: ", ";\n  padding-right: ", ";\n"]);

  _templateObject3$7 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$a() {
  var data = _taggedTemplateLiteral(["\n  font-size: inherit;\n  color: ", ";\n  font-weight: ", ";\n"]);

  _templateObject2$a = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$h() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  margin: 0 0 0 1rem;\n"]);

  _templateObject$h = function _templateObject() {
    return data;
  };

  return data;
}
var TWTextInput = (function (_ref) {
  var name = _ref.name,
      defaultValue = _ref.defaultValue,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'text' : _ref$type,
      label = _ref.label,
      help = _ref.help;
      _ref.helpAlign;
      var _ref$validate = _ref.validate,
      validate = _ref$validate === void 0 ? [] : _ref$validate,
      style = _ref.style,
      depends = _ref.depends,
      conditional = _ref.conditional,
      currentValues = _ref.currentValues,
      multiline = _ref.multiline,
      rows = _ref.rows,
      placeholder = _ref.placeholder,
      rest = _objectWithoutProperties(_ref, ["name", "defaultValue", "type", "label", "help", "helpAlign", "validate", "style", "depends", "conditional", "currentValues", "multiline", "rows", "placeholder"]);

  var FieldTag = depends || multiline || type === 'password' ? Field : FastField;
  var InputTag = multiline ? TWTextArea : TWTextInput$1;
  var additionalProps = {};

  if (multiline) {
    additionalProps.rows = rows || 5;

    if (validate.find(function (v) {
      return v.startsWith('minLength');
    })) {
      additionalProps.min = getParam(validate.find(function (v) {
        return v.startsWith('minLength');
      }));
    }

    if (validate.find(function (v) {
      return v.startsWith('maxLength');
    })) {
      additionalProps.max = getParam(validate.find(function (v) {
        return v.startsWith('maxLength');
      }));
    }

    additionalProps.onKeyUp = function (e) {
      return console.log(e.target.value, validate);
    };
  }

  if (rest.readOnly) {
    additionalProps.disabled = true;
  }

  var _useState = useState(type),
      _useState2 = _slicedToArray(_useState, 2),
      overrideType = _useState2[0],
      setOverrideType = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isPasswordVisible = _useState4[0],
      setIsPasswordVisible = _useState4[1];

  var toggleVisibility = function toggleVisibility() {
    setOverrideType(overrideType === 'password' ? 'text' : 'password');
    setIsPasswordVisible(!isPasswordVisible);
  };

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      length = _useState6[0],
      setLength = _useState6[1];

  var isMultilineValid = useMemo(function () {
    if (!additionalProps.min && !additionalProps.max) return true;
    if (additionalProps.min && !additionalProps.max && length > additionalProps.min) return true;
    if (additionalProps.max && !additionalProps.min && length < additionalProps.max) return true;
    if (additionalProps.min && additionalProps.max && additionalProps.min < length && length < additionalProps.max) return true;
    return false;
  }, [length, additionalProps]);
  return resolveDepends(currentValues, depends, conditional) ? React__default.createElement(FieldTag, {
    name: name,
    validate: TWValidate(validate),
    setOverrideType: setOverrideType
  }, function (_ref2) {
    var field = _ref2.field,
        _ref2$form = _ref2.form,
        errors = _ref2$form.errors,
        touched = _ref2$form.touched,
        submitCount = _ref2$form.submitCount;
        _ref2$form.isSubmitting;
        var setFieldTouched = _ref2$form.setFieldTouched,
        setFieldValue = _ref2$form.setFieldValue;
    var resolvedError = getInlineError(errors, touched, submitCount, name);
    return React__default.createElement(TWFieldGroup, {
      style: style,
      type: overrideType
    }, label && React__default.createElement(TWInputLabel, {
      label: label,
      htmlFor: name,
      validate: validate,
      noAnnotation: rest.noAnnotation
    }), overrideType === 'tel' ? React__default.createElement(React__default.Fragment, null, React__default.createElement(PhoneInput, _extends({
      masks: {
        gb: '..... ......'
      },
      enableLongNumbers: true,
      countryCodeEditable: false,
      containerClass: "tw-tel-input"
    }, field, {
      fieldId: name,
      fieldName: name,
      country: 'gb',
      value: field.value && field.value !== null ? field.value : defaultValue || '',
      onBlur: function onBlur(isValid) {
        setFieldTouched(name, true);
      },
      onChange: function onChange(phone, country, e, formattedValue) {
        setFieldValue(name, "+".concat(phone));
      }
    })), React__default.createElement(TelStyle, null), React__default.createElement(ReactPhoneInputStyle, null)) : React__default.createElement(InputTagContainer, null, React__default.createElement(InputTag, _extends({
      type: overrideType
    }, field, {
      placeholder: placeholder || label,
      error: typeof resolvedError === 'string' ? resolvedError : '',
      value: field.value && field.value !== null ? field.value : defaultValue || ''
    }, additionalProps, {
      onKeyUp: function onKeyUp(e) {
        return setLength(e.target.value.length);
      }
    })), type === 'password' && React__default.createElement("span", {
      className: "icon",
      onClick: toggleVisibility
    }, isPasswordVisible ? React__default.createElement(IconHide, {
      color: colors.purpleDark
    }) : React__default.createElement(IconShow, {
      color: colors.purpleDark
    }))), typeof resolvedError === 'string' ? React__default.createElement(TWInputErrorContainer, null, React__default.createElement(TWInputError, {
      error: typeof resolvedError === 'string' ? resolvedError : '',
      help: help
    }, multiline && additionalProps.max ? React__default.createElement(TWMultilineHelper, null, React__default.createElement(TWMultilineHelperCurrent, {
      isMultilineValid: isMultilineValid
    }, length || '0'), "\xA0/\xA0", React__default.createElement(Text$2, {
      class: "max"
    }, additionalProps.max)) : null)) : null);
  }) : null;
});
var TWMultilineHelper = styled.div(_templateObject$h());
var TWMultilineHelperCurrent = styled(Text$2)(_templateObject2$a(), function (props) {
  return props.isMultilineValid === true ? props.theme.colors.purpleDark : props.theme.colors.red;
}, function (props) {
  return props.theme.fontWeight.bold;
});
var TWInputErrorContainer = styled.div(_templateObject3$7(), function (p) {
  return p.helpAlign === 'left' ? 'flex-start' : 'flex-end';
}, function (p) {
  return p.helpAlign === 'left' ? 'inherit' : 0;
});
styled(IconShow)(_templateObject4$4());
var InputTagContainer = styled.div(_templateObject5$3());
var TWTextInput$1 = styled.input(_templateObject6$2(), function (props) {
  return props.theme.text.body;
}, transitionFocus, function (props) {
  return props.theme.colors.white;
}, function (props) {
  return props.error ? props.theme.colors.pink : props.theme.colors.greyLight;
}, function (props) {
  return props.theme.colors.grey;
}, function (props) {
  return props.theme.colors.greyIcon;
}, function (props) {
  return props.theme.colors.greyIcon;
}, function (props) {
  return props.theme.colors.greyIcon;
});
var TWTextArea = styled.textarea(_templateObject7$2(), function (props) {
  return props.theme.text.body;
}, transitionFocus, function (props) {
  return props.theme.colors.white;
}, function (props) {
  return props.error ? props.theme.colors.pink : props.theme.colors.greyLight;
}, function (props) {
  return props.theme.colors.grey;
}, function (props) {
  return props.theme.colors.greyIcon;
}, function (props) {
  return props.theme.colors.greyIcon;
}, function (props) {
  return props.theme.colors.greyIcon;
});

function _templateObject2$9() {
  var data = _taggedTemplateLiteral(["\n  background-color: #fff;\n  border-radius: 1.5rem;\n  position: relative;\n  z-index: 1;\n  max-width: ", ";\n  min-width: 10rem;\n\n  &:after {\n    content: '';\n    background-image: url(\"data:image/svg+xml,%3Csvg width='13' height='7' viewBox='0 0 13 7' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M.667 1.388l5.339 5.32a.7.7 0 0 0 .988 0l5.34-5.32a.5.5 0 0 0 0-.709l-.53-.527a.5.5 0 0 0-.706 0L6.5 4.733 1.902.152a.5.5 0 0 0-.706 0l-.53.527a.5.5 0 0 0 0 .709z' fill='%23A0A1B9' fill-rule='evenodd'/%3E%3C/svg%3E\");\n    background-position: center center;\n    background-repeat: no-repeat;\n    display: block;\n    height: 2rem;\n    position: absolute;\n    top: 50%;\n    transform: translateY(-50%);\n    right: 1.5rem;\n    width: 2rem;\n    z-index: 0;\n  }\n"]);

  _templateObject2$9 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$g() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  ", ";\n  appearance: none;\n  background-color: transparent;\n  cursor: pointer;\n  outline: none;\n  border-radius: 1.5rem;\n  border: 4px solid\n    ", ";\n  height: 5.4rem;\n  padding: 1rem;\n  padding-right: 3rem;\n  position: relative;\n  width: 100%;\n  z-index: 1;\n  -webkit-appearance: none;\n\n  &:focus {\n    border: 4px solid\n      ", ";\n  }\n"]);

  _templateObject$g = function _templateObject() {
    return data;
  };

  return data;
}
var TWSelect = (function (_ref) {
  var name = _ref.name,
      label = _ref.label,
      options = _ref.options,
      help = _ref.help,
      _ref$validate = _ref.validate,
      validate = _ref$validate === void 0 ? [] : _ref$validate,
      children = _ref.children,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? 'Please Choose' : _ref$placeholder,
      rest = _objectWithoutProperties(_ref, ["name", "label", "options", "help", "validate", "children", "placeholder"]);

  return React__default.createElement(FastField, {
    name: name,
    validate: TWValidate(validate)
  }, function (_ref2) {
    var field = _ref2.field,
        _ref2$form = _ref2.form,
        errors = _ref2$form.errors,
        touched = _ref2$form.touched,
        submitCount = _ref2$form.submitCount;
    var resolvedError = getInlineError(errors, touched, submitCount, name);
    return React__default.createElement(TWFieldGroup, null, React__default.createElement(TWInputLabel, {
      label: label,
      htmlFor: name,
      validate: validate,
      noAnnotation: rest.noAnnotation
    }), React__default.createElement(SelectWrap, rest, React__default.createElement(TWSelectDumb, _extends({}, field, rest, {
      value: field.value || '',
      error: resolvedError,
      id: name
    }), placeholder && React__default.createElement("option", {
      value: "",
      disabled: true
    }, placeholder), children && children, options && Object.keys(options).map(function (key, i) {
      return React__default.createElement("option", {
        key: "key-".concat(i),
        value: key
      }, options[key]);
    }))), React__default.createElement(TWInputError, {
      error: resolvedError,
      help: help
    }));
  });
});
/**
 * Styled Components
 */

var TWSelectDumb = styled.select(_templateObject$g(), function (props) {
  return props.theme.text.body;
}, transitionFocus, function (props) {
  return props.error ? props.theme.colors.pink : props.theme.colors.greyLight;
}, function (props) {
  return props.error ? props.theme.colors.pink : props.theme.colors.grey;
});
var SelectWrap = styled.div(_templateObject2$9(), function (props) {
  return props.small ? '15rem' : 'none';
});

function _templateObject2$8() {
  var data = _taggedTemplateLiteral(["\n    flex-direction: column;\n\n    > * {\n      margin-right: 0;\n    }\n\n    > * + * {\n      margin-top: .5rem;\n  "]);

  _templateObject2$8 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$f() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  max-width: 45rem;\n  width: 100%;\n\n  > * {\n    margin-right: 1.5rem;\n  }\n\n  ", "\n"]);

  _templateObject$f = function _templateObject() {
    return data;
  };

  return data;
}

var TWDate =
/*#__PURE__*/
function (_React$Component) {
  _inherits(TWDate, _React$Component);

  function TWDate(props) {
    var _this;

    _classCallCheck(this, TWDate);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TWDate).call(this, props));

    _defineProperty(_assertThisInitialized(_assertThisInitialized(_this)), "onChange", function (prop, value) {
      _this.setState(_defineProperty({}, prop, value), function () {
        _this.propagateDate();
      });
    });

    var _this$props = _this.props,
        field = _this$props.field,
        futureYears = _this$props.futureYears; // Today

    var today = new Date();
    var thisYear = today.getFullYear();

    var _jsDateToParts = jsDateToParts(field.value),
        _jsDateToParts2 = _slicedToArray(_jsDateToParts, 3),
        d = _jsDateToParts2[0],
        m = _jsDateToParts2[1],
        y = _jsDateToParts2[2];

    var yearsArray = futureYears ? createRange(thisYear, thisYear + futureYears - 1).reverse() : createRange(thisYear - 100, thisYear);
    _this.state = {
      d: d,
      m: m,
      y: y,
      monthsArray: monthsArray,
      yearsArray: yearsArray
    };
    return _this;
  }

  _createClass(TWDate, [{
    key: "propagateDate",
    value: function propagateDate() {
      var _this$props2 = this.props,
          setFieldValue = _this$props2.setFieldValue,
          setFieldTouched = _this$props2.setFieldTouched,
          name = _this$props2.name;
      var _this$state = this.state,
          d = _this$state.d,
          m = _this$state.m,
          y = _this$state.y;

      if (!d) {
        d = 1;
      }

      if (d && m && y) {
        if (isValidDate(d, m, y)) {
          setFieldValue(name, partsToJSDate(d, m, y));
        } else {
          setFieldValue(name, 'something_invalid_here');
        }

        setFieldTouched(name, true);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props3 = this.props,
          defaultPlaceholderDay = _this$props3.defaultPlaceholderDay,
          defaultPlaceholderMonth = _this$props3.defaultPlaceholderMonth,
          defaultPlaceholderYear = _this$props3.defaultPlaceholderYear,
          error = _this$props3.error,
          hideDay = _this$props3.hideDay;
      var _this$state2 = this.state,
          d = _this$state2.d,
          m = _this$state2.m,
          y = _this$state2.y,
          monthsArray = _this$state2.monthsArray,
          yearsArray = _this$state2.yearsArray;
      return React__default.createElement(InlineDate, null, !hideDay ? React__default.createElement(SelectWrap, null, React__default.createElement(TWSelectDumb, {
        value: d && d !== null ? d : '',
        onChange: function onChange(e) {
          return _this2.onChange('d', e.target.value);
        },
        placeholder: defaultPlaceholderDay,
        error: error
      }, React__default.createElement("option", {
        value: "",
        disabled: true
      }, defaultPlaceholderDay), Array(31).fill(1).map(function (a, i) {
        return React__default.createElement("option", {
          value: i + 1,
          key: "d".concat(i)
        }, padZero(i + 1));
      }))) : null, React__default.createElement(SelectWrap, null, React__default.createElement(TWSelectDumb, {
        value: m && m !== null ? m : '',
        onChange: function onChange(e) {
          return _this2.onChange('m', e.target.value);
        },
        className: "month",
        error: error
      }, React__default.createElement("option", {
        value: "",
        disabled: true
      }, defaultPlaceholderMonth), monthsArray.map(function (a, i) {
        return React__default.createElement("option", {
          value: i + 1,
          key: "m".concat(a)
        }, a);
      }))), React__default.createElement(SelectWrap, null, React__default.createElement(TWSelectDumb, {
        value: y && y !== null ? y : '',
        onChange: function onChange(e) {
          return _this2.onChange('y', e.target.value);
        },
        placeholder: defaultPlaceholderYear,
        error: error
      }, React__default.createElement("option", {
        value: "",
        disabled: true
      }, defaultPlaceholderYear), yearsArray.slice(0).reverse().map(function (a) {
        return React__default.createElement("option", {
          value: a,
          key: "y".concat(a)
        }, a);
      }))));
    }
  }]);

  return TWDate;
}(React__default.Component);

TWDate.defaultProps = {
  defaultPlaceholderDay: 'Day',
  defaultPlaceholderMonth: 'Month',
  defaultPlaceholderYear: 'Year'
};
var TWDate$1 = (function (_ref) {
  var name = _ref.name,
      label = _ref.label,
      help = _ref.help,
      futureYears = _ref.futureYears,
      _ref$validate = _ref.validate,
      validate = _ref$validate === void 0 ? [] : _ref$validate,
      hideDay = _ref.hideDay;
  var defaultValidations = ['dateValid'];
  return React__default.createElement(FastField, {
    name: name,
    validate: TWValidate([].concat(defaultValidations, _toConsumableArray(validate)))
  }, function (_ref2) {
    var field = _ref2.field,
        _ref2$form = _ref2.form;
        _ref2$form.values;
        var errors = _ref2$form.errors,
        touched = _ref2$form.touched,
        submitCount = _ref2$form.submitCount,
        setFieldValue = _ref2$form.setFieldValue,
        setFieldTouched = _ref2$form.setFieldTouched;
    var resolvedError = getInlineError(errors, touched, submitCount, name);
    return React__default.createElement(TWFieldGroup, null, React__default.createElement(TWInputLabel, {
      label: label,
      htmlFor: name,
      validate: validate
    }), React__default.createElement(TWDate, {
      field: field,
      name: name,
      error: resolvedError.length,
      setFieldValue: setFieldValue,
      setFieldTouched: setFieldTouched,
      futureYears: futureYears,
      hideDay: hideDay
    }), React__default.createElement(TWInputError, {
      error: resolvedError,
      help: help
    }));
  });
});
/**
 * Styled Components
 */

var InlineDate = styled.div(_templateObject$f(), mq.xs(_templateObject2$8()));

function _templateObject$e() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  -webkit-appearance: none;\n  opacity: 0;\n\n  // Unchecked\n  & + label {\n    position: relative;\n    cursor: pointer;\n    padding: 0 0 0 3.5rem;\n  }\n  & + label:before {\n    content: '';\n    width: 1.5rem;\n    height: 1.5rem;\n    background: white;\n    border: 3px solid ", ";\n    position: absolute;\n    left: 0;\n    top: 0.2rem;\n  }\n\n  // Checked\n  &:focus + label:before,\n  &:hover + label:before,\n  &:checked + label:before {\n    background: ", ";\n    border: 3px solid ", ";\n    ", ";\n  }\n\n  &:checked + label:after {\n    content: '';\n    position: absolute;\n    left: 5px;\n    top: 11px;\n    background: white;\n    width: 3px;\n    height: 3px;\n    box-shadow: 2px 0 0 white, 4px 0 0 white, 4px -2px 0 white, 4px -4px 0 white,\n      4px -6px 0 white, 4px -8px 0 white;\n    transform: rotate(45deg);\n  }\n\n  // Disabled\n  &:disabled + label {\n    color: ", ";\n    cursor: auto;\n  }\n  &:disabled + label:before {\n    box-shadow: none;\n    background: ", ";\n  }\n\n  // Error\n  ", "\n"]);

  _templateObject$e = function _templateObject() {
    return data;
  };

  return data;
}

var TWCheckboxUS = function TWCheckboxUS(_ref) {
  var name = _ref.name,
      label = _ref.label,
      labelHTML = _ref.labelHTML,
      help = _ref.help,
      style = _ref.style,
      _ref$validate = _ref.validate,
      validate = _ref$validate === void 0 ? [] : _ref$validate,
      inline = _ref.inline;
  return React__default.createElement(FastField, {
    name: name,
    validate: TWValidate(validate)
  }, function (_ref2) {
    var field = _ref2.field,
        _ref2$form = _ref2.form,
        values = _ref2$form.values,
        errors = _ref2$form.errors,
        touched = _ref2$form.touched,
        submitCount = _ref2$form.submitCount;
    var resolvedError = getInlineError(errors, touched, submitCount, name);
    return React__default.createElement(TWFieldGroup, {
      style: style
    }, React__default.createElement(TWInlineFieldGroup, null, React__default.createElement(TWCheckbox, _extends({}, field, {
      placeholder: label,
      error: resolvedError,
      id: name,
      checked: objectPath.get(values, name)
    })), React__default.createElement(TWInputLabel, {
      label: label,
      labelHTML: labelHTML,
      htmlFor: name,
      validate: validate,
      noAnnotation: inline
    })), React__default.createElement(TWInputError, {
      error: resolvedError,
      help: help
    }));
  });
};

var TWCheckbox = styled.input.attrs({
  type: 'checkbox'
})(_templateObject$e(), function (props) {
  return props.theme.colors.greyLight;
}, function (props) {
  return props.theme.colors.pink;
}, function (props) {
  return props.theme.colors.pink;
}, transitionFocus, function (props) {
  return props.theme.colors.greyLight;
}, function (props) {
  return props.theme.colors.greyLight;
}, function (props) {
  if (props.error) {
    return "\n        + label:before {\n          background: ".concat(props.theme.colors.pink, ";\n          border: 3px solid ").concat(props.theme.colors.pink, ";\n          ").concat(transitionFocus, ";\n        }\n\n        + label {\n          color: ").concat(props.theme.colors.pinkDark, ";\n        }\n      ");
  }
});

function _templateObject3$6() {
  var data = _taggedTemplateLiteral(["\n  &[type='radio']:checked,\n  &[type='radio']:not(:checked) {\n    position: absolute;\n    left: -9999px;\n  }\n  &[type='radio']:checked + label,\n  &[type='radio']:not(:checked) + label {\n    position: relative;\n    padding-left: 36px;\n    cursor: pointer;\n    line-height: 24px;\n    display: inline-block;\n    color: ", ";\n  }\n  &[type='radio']:checked + label:before,\n  &[type='radio']:not(:checked) + label:before {\n    content: '';\n    position: absolute;\n    left: 0;\n    top: 0;\n    width: 16px;\n    height: 16px;\n    border: 4px solid #ddd;\n    border-radius: 100%;\n    background: #fff;\n  }\n  &[type='radio']:checked + label:after,\n  &[type='radio']:not(:checked) + label:after {\n    content: '';\n    width: 10px;\n    height: 10px;\n    background: ", ";\n    position: absolute;\n    top: 7px;\n    left: 7px;\n    border-radius: 100%;\n    -webkit-transition: all 0.2s ease;\n    transition: all 0.2s ease;\n  }\n  &[type='radio']:not(:checked) + label:after {\n    opacity: 0;\n    -webkit-transform: scale(0);\n    transform: scale(0);\n  }\n  &[type='radio']:checked + label:after {\n    opacity: 1;\n    -webkit-transform: scale(1);\n    transform: scale(1);\n  }\n  -webkit-appearance: none;\n"]);

  _templateObject3$6 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$7() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n"]);

  _templateObject2$7 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$d() {
  var data = _taggedTemplateLiteral(["\n  margin-top: 1rem;\n\n  > *:last-child {\n    margin-bottom: 0 !important;\n  }\n"]);

  _templateObject$d = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Single Radio Button
 */

var TWRadioButtonUS = function TWRadioButtonUS(_ref) {
  var id = _ref.id,
      value = _ref.value,
      checked = _ref.checked,
      label = _ref.label,
      rest = _objectWithoutProperties(_ref, ["id", "value", "checked", "label"]);

  return React__default.createElement(TWInlineFieldGroup, null, React__default.createElement(TWRadioButton, _extends({
    value: value,
    checked: checked,
    id: id
  }, rest)), React__default.createElement(TWInputRadioLabel, {
    label: label,
    htmlFor: id,
    noAnnotation: true
  }));
};
/**
 * Radio Group
 */


var TWRadioGroupUS = function TWRadioGroupUS(_ref2) {
  var label = _ref2.label,
      name = _ref2.name,
      options = _ref2.options,
      boolean = _ref2.boolean,
      help = _ref2.help,
      validate = _ref2.validate;
  var resolvedOptions = boolean ? {
    yes: 'Yes',
    no: 'No'
  } : options;
  var resolvedValidate = boolean ? ['required'] : validate;
  return React__default.createElement(FastField, {
    name: name,
    validate: TWValidate(resolvedValidate)
  }, function (_ref3) {
    var field = _ref3.field,
        _ref3$form = _ref3.form,
        values = _ref3$form.values,
        errors = _ref3$form.errors,
        touched = _ref3$form.touched,
        submitCount = _ref3$form.submitCount;
    var resolvedError = getInlineError(errors, touched, submitCount, name);
    return React__default.createElement(TWFieldGroup, null, React__default.createElement(TWInputLabel, {
      label: label,
      validate: resolvedValidate,
      htmlFor: name,
      compact: "true"
    }), React__default.createElement(TWRadioGroupContainer, null, Object.keys(resolvedOptions).map(function (value, i) {
      return React__default.createElement(TWRadioButtonUS, _extends({
        key: value,
        checked: value === objectPath.get(values, name),
        id: "options-".concat(name, "-").concat(i),
        label: resolvedOptions[value]
      }, field, {
        value: value
      }));
    })), React__default.createElement(TWInputError, {
      error: resolvedError,
      help: help
    }));
  });
};

var TWRadioGroup = (function (props) {
  return React__default.createElement(TWRadioGroupUS, props);
});
/**
 * Styled Components
 */

var TWRadioGroupContainer = styled.div(_templateObject$d());
var TWInputRadioLabel = styled(TWInputLabel)(_templateObject2$7());
var TWRadioButton = styled.input.attrs({
  type: 'radio'
})(_templateObject3$6(), function (props) {
  return props.theme.colors.greyMid;
}, function (props) {
  return props.theme.colors.pinkDark;
});

function _templateObject3$5() {
  var data = _taggedTemplateLiteral(["\n  margin-top: 0.5rem;\n"]);

  _templateObject3$5 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$6() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  border-radius: 1.2rem;\n  height: 2.4rem;\n  transition: width 0.5s ease-out;\n  width: ", "%;\n"]);

  _templateObject2$6 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$c() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  border-radius: 1.2rem;\n  overflow: hidden;\n  display: flex;\n  height: 2.4rem;\n  margin-bottom: 2rem;\n"]);

  _templateObject$c = function _templateObject() {
    return data;
  };

  return data;
}
var gradientPink = "\n  background-image: linear-gradient(-180deg, #F37283 3%, #F78F9D 99%);\n";
var ProgressBar = (function (_ref) {
  var progress = _ref.progress,
      text = _ref.text,
      disabled = _ref.disabled;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(ProgressContainer, null, React__default.createElement(ProgressBar$1, {
    progress: disabled ? 0 : progress
  })), text && React__default.createElement(ProgressText, null, React__default.createElement(Text$2, null, text)));
});
/**
 * Styled Components
 */

var ProgressContainer = styled.div(_templateObject$c(), function (props) {
  return props.theme.colors.greyLightest;
});
var ProgressBar$1 = styled.div(_templateObject2$6(), gradientPink, function (props) {
  return props.progress;
});
var ProgressText = styled.div(_templateObject3$5());

function _templateObject4$3() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: ", ";\n  flex-wrap: wrap;\n  margin-top: 2rem;\n\n  button {\n    max-width: 45%;\n    width: 45%;\n    align-self: stretch;\n    margin: 0.5rem;\n  }\n\n  ", "\n"]);

  _templateObject4$3 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$4() {
  var data = _taggedTemplateLiteral(["\n  margin-bottom: 3rem;\n"]);

  _templateObject3$4 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$5() {
  var data = _taggedTemplateLiteral(["\n  margin-bottom: 1rem;\n  display: ", ";\n"]);

  _templateObject2$5 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$b() {
  var data = _taggedTemplateLiteral(["\n  padding: 3rem 2rem;\n  border-radius: 1.5rem;\n\n  ", "\n"]);

  _templateObject$b = function _templateObject() {
    return data;
  };

  return data;
}
var FormTypes = {
  text: TWTextInput,
  checkbox: TWCheckboxUS,
  radio: TWRadioGroup,
  select: TWSelect,
  date: TWDate$1
};

function TWSteppedForm(_ref) {
  var schema = _ref.schema,
      _ref$border = _ref.border,
      border = _ref$border === void 0 ? false : _ref$border,
      formHeader = _ref.formHeader,
      children = _ref.children,
      _ref$initialValues = _ref.initialValues,
      initialValues = _ref$initialValues === void 0 ? {} : _ref$initialValues,
      _ref$onSubmit = _ref.onSubmit,
      onSubmit = _ref$onSubmit === void 0 ? function (values) {
    alert(JSON.stringify(values));
  } : _ref$onSubmit,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? function (e) {} : _ref$onBlur,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function (e) {} : _ref$onChange,
      submitText = _ref.submitText,
      nextStepText = _ref.nextStepText,
      hideProgress = _ref.hideProgress,
      _ref$stepTitleVariant = _ref.stepTitleVariant,
      stepTitleVariant = _ref$stepTitleVariant === void 0 ? 'title2' : _ref$stepTitleVariant,
      currentStepState = _ref.currentStepState;

  var _ref2 = currentStepState || useState(0),
      _ref3 = _slicedToArray(_ref2, 2),
      currentStep = _ref3[0],
      setCurrentStep = _ref3[1];

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      submitted = _useState2[0],
      setSubmitted = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isSubmitting = _useState4[0],
      setIsSubmitting = _useState4[1];

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      error = _useState6[0],
      setError = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      submitCount = _useState8[0],
      setSubmitCount = _useState8[1];

  return React__default.createElement(TWFormContainer, {
    border: border
  }, schema.length > 1 && !hideProgress && React__default.createElement(ProgressBar, {
    progress: currentStep / schema.length * 100
  }), submitted && children ? React__default.createElement(React__default.Fragment, null, children) : React__default.createElement(React__default.Fragment, null, formHeader ? formHeader : null, React__default.createElement(TWForm, {
    submitCount: submitCount,
    initialValues: initialValues,
    onSubmit:
    /*#__PURE__*/
    function () {
      var _ref4 = _asyncToGenerator(
      /*#__PURE__*/
      regeneratorRuntime.mark(function _callee(values) {
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(currentStep < schema.length - 1)) {
                  _context.next = 4;
                  break;
                }

                setCurrentStep(currentStep + 1);
                _context.next = 17;
                break;

              case 4:
                setIsSubmitting(true);
                _context.prev = 5;
                _context.next = 8;
                return onSubmit(values);

              case 8:
                setIsSubmitting(false);
                setCurrentStep(currentStep + 1);

                if (children) {
                  setSubmitted(true);
                }

                _context.next = 17;
                break;

              case 13:
                _context.prev = 13;
                _context.t0 = _context["catch"](5);
                setIsSubmitting(false);
                setError(_context.t0);

              case 17:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this, [[5, 13]]);
      }));

      return function (_x) {
        return _ref4.apply(this, arguments);
      };
    }(),
    onBlur: onBlur,
    onChange: onChange,
    render: function render(_ref5) {
      _ref5.resetForm;
          var submitForm = _ref5.submitForm,
          values = _ref5.values,
          submitCount = _ref5.submitCount;
      var valueLength = Object.keys(values).length;
      setSubmitCount(submitCount);
      return React__default.createElement(React__default.Fragment, null, schema.map(function (step, i) {
        return i === currentStep ? React__default.createElement(React__default.Fragment, {
          key: i
        }, step.title && React__default.createElement(StepTitle, {
          variant: stepTitleVariant,
          centered: true
        }, step.title), step.schema.map(function (field) {
          var Field = FormTypes[field.type];
          return field.type === 'component' ? React__default.createElement(React__default.Fragment, {
            key: field.props.name
          }, field.props.component) : React__default.createElement(TWFormField, {
            key: field.props.name,
            hidden: field.props.hidden
          }, React__default.createElement(Field, field.props));
        })) : null;
      }), error && React__default.createElement(Text, {
        spacerTop: true,
        color: colors.pinkDark
      }, error), React__default.createElement(StepActions, {
        currentStep: currentStep
      }, currentStep > 0 && React__default.createElement(Button$1, {
        onClick: function onClick() {
          return setCurrentStep(currentStep - 1);
        },
        light: true,
        disabled: isSubmitting
      }, "Back"), currentStep < schema.length - 1 && React__default.createElement(Button$1, {
        onClick: function onClick() {
          submitForm();
        },
        disabled: valueLength < 1
      }, nextStepText || 'Next'), currentStep === schema.length - 1 && React__default.createElement(Button$1, {
        type: "submit",
        disabled: isSubmitting || valueLength < 1
      }, isSubmitting ? 'Submitting' : submitText || 'Submit')));
    }
  })));
}

var TWFormContainer = styled.div(_templateObject$b(), function (p) {
  if (p.border) {
    return "border: 1px solid ".concat(p.theme.colors.greyLight, ";");
  }
});
var TWFormField = styled.div(_templateObject2$5(), function (p) {
  return p.hidden ? 'none' : 'block';
});
var StepTitle = styled(Heading$1)(_templateObject3$4());
var StepActions = styled.div(_templateObject4$3(), function (p) {
  return p.currentStep > 0 ? 'space-between' : 'space-around';
}, mq.sm("\n    \n    flex-wrap: wrap;\n\n    button {\n      width: 100%;\n      max-width: unset;\n      margin: 0.5rem 0;\n    }\n\n  "));
/** @component */

var TWSteppedForm$1 = (function (props) {
  return React__default.createElement(TWSteppedForm, props);
});

function _templateObject$a() {
  var data = _taggedTemplateLiteral(["\n  li {\n    display: flex;\n  }\n\n  li svg {\n    margin-right: 1rem;\n    position: relative;\n    top: 1px;\n    flex: 0 0 16px;\n    color: ", ";\n  }\n\n  li + li {\n    margin-top: 2rem;\n  }\n\n  ", "\n\n  ", "\n"]);

  _templateObject$a = function _templateObject() {
    return data;
  };

  return data;
}

var CheckmarkListUS = function CheckmarkListUS(_ref) {
  var className = _ref.className,
      items = _ref.items,
      light = _ref.light;
  return React__default.createElement("ul", {
    className: className
  }, items && items.map(function (item, i) {
    return React__default.createElement("li", {
      key: i
    }, React__default.createElement(IconCheck, null), React__default.createElement(Text$2, {
      variant: "headline",
      color: light ? colors.white : colors.greyMid
    }, item));
  }));
};

var CheckmarkList = styled(CheckmarkListUS)(_templateObject$a(), function (props) {
  return props.checkColor || props.theme.colors.green;
}, function (props) {
  if (props.wrap) {
    return "\n        display: flex;\n        flex-wrap: wrap;\n        justify-content: flex-start;\n\n        li {\n          flex: 0 0 50%;\n          margin-bottom: 2rem;\n          padding-right: 2rem;\n        }\n\n        li + li {\n          margin-top: 0;\n        }\n      ";
  }
}, function (props) {
  if (props.wrap) {
    return mq.sm("\n        li {\n          flex: 1 1 100%;\n        }\n      ");
  }
});
/** @component */

var CheckmarkList$1 = (function (props) {
  return React__default.createElement(CheckmarkList, props);
});

function _templateObject$9() {
  var data = _taggedTemplateLiteral(["\n  padding: 0 0 0 4rem;\n  margin-bottom: ", ";\n  position: relative;\n  flex: ", ";\n\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6 {\n    margin-bottom: 1.5rem;\n  }\n\n  svg {\n    position: absolute;\n    top: 0.2rem;\n    left: 0.5rem;\n  }\n\n  ", "\n\n  ", "\n"]);

  _templateObject$9 = function _templateObject() {
    return data;
  };

  return data;
}

var FeatureItem = function FeatureItem(_ref) {
  var className = _ref.className,
      title = _ref.title,
      detail = _ref.detail,
      color = _ref.color,
      light = _ref.light,
      icon = _ref.icon;
  return React__default.createElement("div", {
    className: className
  }, icon ? icon : React__default.createElement(IconStar, {
    color: colors[color]
  }), React__default.createElement(Heading$1, {
    variant: "title3",
    priority: 4,
    color: light ? colors.white : colors.purpleDark
  }, title), React__default.createElement(Text$2, {
    color: light ? colors.white : colors.greyMid
  }, detail));
};

var FeatureItemStyled = styled(FeatureItem)(_templateObject$9(), function (props) {
  return props.compact ? '2rem' : '4rem';
}, function (props) {
  switch (props.baseSize) {
    case 'third':
      return "1 0 33.33%";

    default:
      return "0 0 50%";
  }
}, mq.lg("\n    svg {\n      left: 0;\n    }\n  "), mq.sm("\n    flex: 1 0 100%;\n  "));
/** @component */

var FeatureItem$1 = (function (props) {
  return React__default.createElement(FeatureItemStyled, props);
});

function _templateObject$8() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  min-width: 25rem;\n  align-items: center;\n\n  .feature__visual {\n    color: ", ";\n    margin-right: ", ";\n  }\n"]);

  _templateObject$8 = function _templateObject() {
    return data;
  };

  return data;
}

var GetIcon = function GetIcon(icon) {
  switch (icon) {
    case 'calendar':
      return IconCalendar;

    case 'users':
      return IconUsers;

    case 'family':
      return IconUserFamily;

    case 'clock':
      return IconClock;
  }
};

var FeatureIconUS = function FeatureIconUS(_ref) {
  var className = _ref.className,
      title = _ref.title,
      detail = _ref.detail,
      _ref$icon = _ref.icon,
      icon = _ref$icon === void 0 ? 'clock' : _ref$icon,
      color = _ref.color,
      smallIcon = _ref.smallIcon;
  var Icon = GetIcon(icon);
  return React__default.createElement("div", {
    className: className
  }, React__default.createElement("div", {
    className: "feature__visual"
  }, React__default.createElement(Icon, {
    color: color,
    size: smallIcon ? 24 : 40
  })), React__default.createElement("div", {
    className: "feature__meta"
  }, title && React__default.createElement(Text$2, {
    color: color,
    variant: "headline"
  }, title), detail && React__default.createElement(Text$2, {
    color: color,
    variant: "caption1"
  }, detail)));
};

var FeatureIcon = styled(FeatureIconUS)(_templateObject$8(), function (props) {
  return props.theme.colors.purpleDark;
}, function (p) {
  return p.smallIcon ? "0.5rem" : "1rem";
});
/** @component */

var FeatureIcon$1 = (function (props) {
  return React__default.createElement(FeatureIcon, props);
});

function _templateObject$7() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  display: flex;\n  align-items: center;\n\n  p {\n    margin-left: 0.5rem;\n  }\n"]);

  _templateObject$7 = function _templateObject() {
    return data;
  };

  return data;
}

var LocationMark = function LocationMark(_ref) {
  var _ref$iconColor = _ref.iconColor,
      iconColor = _ref$iconColor === void 0 ? colors.greyIcon : _ref$iconColor,
      _ref$textColor = _ref.textColor,
      textColor = _ref$textColor === void 0 ? colors.grey : _ref$textColor,
      title = _ref.title;
  return React__default.createElement(LocationMarkContainer, null, React__default.createElement(IconLocationPin, {
    color: iconColor
  }), React__default.createElement(Text$2, {
    variant: "caption2",
    color: textColor
  }, title));
};

var LocationMarkContainer = styled.div(_templateObject$7(), function (props) {
  return props.light ? props.theme.colors.white : props.theme.colors.greyMid;
});
/** @component */

var LocationMark$1 = (function (props) {
  return React__default.createElement(LocationMark, props);
});

function _templateObject$6() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  width: ", "px;\n  height: ", "px;\n  border-radius: 50%;\n  overflow: hidden;\n  position: relative;\n\n  > * {\n    width: 100%;\n    height: 100%;\n  }\n\n  > svg {\n    position: absolute;\n    bottom: 0;\n    left: 50%;\n    transform: translateX(-50%);\n  }\n\n  ", "\n\n  ", "\n"]);

  _templateObject$6 = function _templateObject() {
    return data;
  };

  return data;
}

var Avatar = function Avatar(_ref) {
  var src = _ref.src,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 80 : _ref$size,
      _ref$alt = _ref.alt,
      alt = _ref$alt === void 0 ? 'Avatar image' : _ref$alt,
      _ref$fallbackColor = _ref.fallbackColor,
      fallbackColor = _ref$fallbackColor === void 0 ? colors.greyLight : _ref$fallbackColor,
      border = _ref.border,
      borderSize = _ref.borderSize,
      shadow = _ref.shadow;
  return React__default.createElement(AvatarContainer$2, {
    size: size,
    border: border,
    borderSize: borderSize,
    shadow: shadow
  }, src ? React__default.createElement("img", {
    src: src,
    alt: alt,
    width: size,
    height: size
  }) : React__default.createElement(IconProfile, {
    size: size / 1.3,
    color: fallbackColor
  }));
};

var AvatarContainer$2 = styled.div(_templateObject$6(), function (p) {
  return p.theme.colors.greyLightest;
}, function (p) {
  return p.size;
}, function (p) {
  return p.size;
}, function (p) {
  if (p.border) {
    if (p.borderSize) {
      return "border: ".concat(p.borderSize, "px solid white;");
    }

    return "border: ".concat(p.size * 0.03, "px solid white;");
  }
}, function (p) {
  if (p.shadow) {
    return "box-shadow: ".concat(shadows.avatar);
  }
});
/** @component */

var Avatar$1 = (function (props) {
  return React__default.createElement(Avatar, props);
});

function _templateObject3$3() {
  var data = _taggedTemplateLiteral(["\n            .profile__meta {\n              padding-left: 0rem important;\n              h2, h3, h4 {\n                min-height: 0;\n              }\n            }\n          "]);

  _templateObject3$3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$4() {
  var data = _taggedTemplateLiteral(["\n  max-width: 50rem;\n\n  .profile__basic {\n    align-items: center;\n    display: flex;\n  }\n\n  .profile__extended {\n    margin-top: 3rem;\n  }\n\n  .profile__meta {\n    flex-grow: 0;\n    padding-left: 2rem;\n  }\n\n  ", "\n\n  ", "\n  \n  ", ";\n"]);

  _templateObject2$4 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$5() {
  var data = _taggedTemplateLiteral(["\n  height: 6rem;\n  margin-top: 1rem;\n\n  > img {\n    width: auto;\n    height: 100%;\n    transform: translateX(-1rem);\n  }\n"]);

  _templateObject$5 = function _templateObject() {
    return data;
  };

  return data;
}

var MiniProfile = function MiniProfile(_ref) {
  var className = _ref.className,
      img = _ref.img,
      name = _ref.name,
      description = _ref.description,
      bio = _ref.bio,
      icon = _ref.icon;
      _ref.vertical;
      var _ref$headingVariant = _ref.headingVariant,
      headingVariant = _ref$headingVariant === void 0 ? 'title3' : _ref$headingVariant,
      _ref$descriptionVaria = _ref.descriptionVariant,
      descriptionVariant = _ref$descriptionVaria === void 0 ? 'caption2' : _ref$descriptionVaria,
      border = _ref.border,
      shadow = _ref.shadow,
      avatarSize = _ref.avatarSize;
  return React__default.createElement("div", {
    className: className
  }, React__default.createElement("div", {
    className: "profile__basic"
  }, React__default.createElement(Avatar$1, {
    size: avatarSize,
    src: img === null || img === void 0 ? void 0 : img.src,
    alt: img === null || img === void 0 ? void 0 : img.alt,
    border: border,
    shadow: shadow
  }), (name || description) && React__default.createElement("div", {
    className: "profile__meta"
  }, name && React__default.createElement(Heading$1, {
    variant: headingVariant
  }, name), description && React__default.createElement(Text$2, {
    variant: descriptionVariant
  }, description), icon && icon.src && React__default.createElement(IconContainer$1, null, React__default.createElement("img", {
    src: icon.src,
    alt: icon.alt
  })))), bio && React__default.createElement("div", {
    className: "profile__extended"
  }, React__default.createElement(Article$1, {
    dangerouslySetInnerHTML: {
      __html: bio
    }
  })));
};

var IconContainer$1 = styled.div(_templateObject$5());
var MiniProfileStyled = styled(MiniProfile)(_templateObject2$4(), function (props) {
  if (props.preview) {
    return "\n        max-width: none;\n        .profile__visual {\n          border-radius: 0;\n          height: auto;\n          margin: 0;\n          width: 100%;\n        }\n      ";
  }
}, function (props) {
  if (!(props.img && props.img.src)) {
    return "\n        .profile__visual {\n          background-color: ".concat(props.theme.colors.pinkLight, ";\n        }\n      ");
  }
}, function (props) {
  if (props.vertical) {
    return "\n          .profile__basic {\n            flex-direction: column;\n            text-align: center;\n          }\n\n          .profile__visual { \n            margin-right: 0;\n          }\n\n          .profile__meta {\n            padding: 1rem 0;\n\n            h2, h3, h4 {\n              min-height: 9rem;\n            }\n          }\n\n          ".concat(mq.sm(_templateObject3$3()), "\n        ");
  }
});
/** @component */

var MiniProfile$1 = (function (props) {
  return React__default.createElement(MiniProfileStyled, props);
});

function _templateObject2$3() {
  var data = _taggedTemplateLiteral(["\n  margin-bottom: 1rem;\n"]);

  _templateObject2$3 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$4() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  padding: 2rem;\n  max-width: 40rem;\n\n  ", "\n"]);

  _templateObject$4 = function _templateObject() {
    return data;
  };

  return data;
}

var NotificationCard = function NotificationCard(_ref) {
  var title = _ref.title,
      text = _ref.text,
      icon = _ref.icon,
      onClick = _ref.onClick;
  var Icon = icon ? Icons[icon] : null;
  return React__default.createElement(NotificationCardContainer, {
    onClick: onClick
  }, icon && React__default.createElement(IconContainer, null, React__default.createElement(Icon, {
    size: 30,
    color: colors.purpleDark
  })), title && React__default.createElement(Text$2, {
    variant: "headline"
  }, title), text && React__default.createElement(Text$2, {
    spacerTop: true,
    color: colors.purpleDark,
    dangerouslySetInnerHTML: {
      __html: text
    }
  }));
};

var NotificationCardContainer = styled.div(_templateObject$4(), function (p) {
  return p.theme.colors.white;
}, function (p) {
  if (p.onClick) {
    return "\n      ".concat(cardStyle, ";\n        cursor: pointer;\n      ");
  } else {
    return "".concat(cardStyleNoHover, ";");
  }
});
var IconContainer = styled.div(_templateObject2$3());
/** @component */

var NotificationCard$1 = (function (props) {
  return React__default.createElement(NotificationCard, props);
});

function _templateObject$3() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  background-color: ", ";\n  padding: 4rem 2rem;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  max-width: 40rem;\n  h2,\n  h3,\n  h4,\n  h5,\n  p {\n    color: ", ";\n  }\n"]);

  _templateObject$3 = function _templateObject() {
    return data;
  };

  return data;
}

var ProfileCard = function ProfileCard(_ref) {
  var name = _ref.name,
      description = _ref.description,
      img = _ref.img,
      _ref$avatarSize = _ref.avatarSize,
      avatarSize = _ref$avatarSize === void 0 ? 150 : _ref$avatarSize;
  return React__default.createElement(ProfileCardContainer, null, React__default.createElement(MiniProfile$1, {
    name: name,
    description: description,
    img: img,
    headingVariant: "title2",
    vertical: true,
    border: true,
    avatarSize: avatarSize
  }));
};

var ProfileCardContainer = styled.div(_templateObject$3(), function (props) {
  return props.theme.fixedStyle;
}, function (props) {
  return props.theme.colors.purpleMid;
}, function (props) {
  return props.theme.colors.white;
});
/** @component */

var ProfileCard$1 = (function (props) {
  return React__default.createElement(ProfileCard, props);
});

function _templateObject12() {
  var data = _taggedTemplateLiteral(["\n  padding: 2rem 0 3rem;\n"]);

  _templateObject12 = function _templateObject12() {
    return data;
  };

  return data;
}

function _templateObject11() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  height: 8rem;\n"]);

  _templateObject11 = function _templateObject11() {
    return data;
  };

  return data;
}

function _templateObject10() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  width: 100%;\n  text-align: center;\n\n  > * {\n    display: inline-block;\n    margin: 0.5rem 1rem;\n  }\n\n  li > * {\n    min-width: 0;\n  }\n"]);

  _templateObject10 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9() {
  var data = _taggedTemplateLiteral(["\n  margin-top: 1rem;\n"]);

  _templateObject9 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$1() {
  var data = _taggedTemplateLiteral(["\n  margin-top: 1rem;\n"]);

  _templateObject8$1 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$1() {
  var data = _taggedTemplateLiteral(["\n  text-align: center;\n"]);

  _templateObject7$1 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$1() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  bottom: -1rem;\n"]);

  _templateObject6$1 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$2() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  padding: 0.7rem 1.5rem;\n  border-radius: 1.6rem;\n  display: inline-block;\n  text-align: center;\n  margin: 0 0.5rem;\n"]);

  _templateObject5$2 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$2() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n  position: relative;\n  margin-bottom: '1rem';\n"]);

  _templateObject4$2 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$2() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  z-index: 2;\n  padding: 0 2rem;\n"]);

  _templateObject3$2 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$2() {
  var data = _taggedTemplateLiteral(["\n  opacity: ", ";\n  background-size: cover;\n  position: absolute;\n  width: 100%;\n  height: 15rem;\n  top: 0;\n  z-index: 1;\n\n  ", "\n"]);

  _templateObject2$2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$2() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  height: ", ";\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-end;\n  overflow: hidden;\n  position: relative;\n  transition: transform 0.1s;\n  cursor: pointer;\n"]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}

var SearchResultHomeCard = function SearchResultHomeCard(_ref) {
  var onClick = _ref.onClick,
      name = _ref.name,
      area = _ref.area,
      _ref$avatarSize = _ref.avatarSize,
      avatarSize = _ref$avatarSize === void 0 ? 170 : _ref$avatarSize,
      avatarImg = _ref.avatarImg,
      avatarAlt = _ref.avatarAlt,
      image = _ref.image,
      fadeImage = _ref.fadeImage,
      distanceString = _ref.distanceString,
      features = _ref.features,
      tags = _ref.tags,
      noHeight = _ref.noHeight;
  return React__default.createElement(CardContainer, {
    noHeight: noHeight,
    onClick: onClick
  }, React__default.createElement(CardVisual, {
    fadeImage: fadeImage,
    image: image
  }), React__default.createElement(CardMeta, null, React__default.createElement(AvatarContainer$1, {
    size: avatarSize
  }, React__default.createElement(Avatar$1, {
    src: avatarImg,
    alt: avatarAlt || name,
    size: avatarSize,
    border: true
  })), React__default.createElement(HeadingText, {
    variant: "title2"
  }, name || 'No Name'), distanceString && React__default.createElement(MetaLocation, null, React__default.createElement(LocationMark$1, {
    title: distanceString
  })), React__default.createElement(AreaText, {
    variant: "headline"
  }, area || 'No Area'), React__default.createElement(CardMetaDetails, null, features ? React__default.createElement(MetaList, null, features.map(function (f, i) {
    return React__default.createElement("li", {
      key: i
    }, React__default.createElement(FeatureIcon$1, _extends({}, f, {
      smallIcon: true,
      color: colors.greyIcon
    })));
  })) : React__default.createElement(FeatureSpacer, null), tags ? React__default.createElement(MetaList, null, tags.map(function (_ref2, i) {
    var type = _ref2.type,
        text = _ref2.text;
    return React__default.createElement(CardTag, {
      variant: "headline",
      type: type
    }, text);
  })) : null)));
};
/**
 * Styled Components
 */


var CardContainer = styled.div(_templateObject$2(), cardStyle, function (p) {
  return p.noHeight ? 'auto' : '48rem';
});
var CardVisual = styled.div(_templateObject2$2(), function (p) {
  return p.fadeImage ? 0.6 : 1;
}, function (p) {
  if (p.image) {
    return "\n        background-image: url(".concat(p.image, ");\n      ");
  }
});
var CardMeta = styled.div(_templateObject3$2());
var AvatarContainer$1 = styled.div(_templateObject4$2());
var CardTag = styled(Text$2)(_templateObject5$2(), function (p) {
  switch (p.type) {
    case 'availability-yes':
      return "background-color: #d6f7f0;";

    case 'availability-no':
      return "background-color: #ffebe7;";

    default:
      return "background-color: #eeeffd;";
  }
});
styled.div(_templateObject6$1());
var HeadingText = styled(Heading$1)(_templateObject7$1());
var AreaText = styled(Text$2)(_templateObject8$1());
var MetaLocation = styled.div(_templateObject9());
var MetaList = styled.ul(_templateObject10(), function (p) {
  return "\n    ".concat(layoutable(p), "\n  ");
});
var FeatureSpacer = styled.div(_templateObject11());
var CardMetaDetails = styled.div(_templateObject12());
/**
 * @component
 */

var SearchResultHomeCard$1 = (function (props) {
  return React__default.createElement(SearchResultHomeCard, props);
});

function _templateObject5$1() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  right: 0;\n  z-index: 90;\n"]);

  _templateObject5$1 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$1() {
  var data = _taggedTemplateLiteral(["\n  margin-top: 2rem;\n  display: flex;\n  align-items: center;\n  flex-direction: column;\n  text-align: center;\n\n  a {\n    font-weight: bold;\n    display: block;\n  }\n"]);

  _templateObject4$1 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$1() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  text-align: center;\n\n  > * + * {\n    margin-top: 1rem;\n  }\n"]);

  _templateObject3$1 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$1() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  top: -40px;\n  z-index: 100;\n"]);

  _templateObject2$1 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$1() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  border-radius: 1.5rem;\n  padding: ", "px 2rem 2rem;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  position: relative;\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}

var ProviderCard = function ProviderCard(_ref) {
  var name = _ref.name,
      location = _ref.location,
      img = _ref.img,
      _ref$imgSize = _ref.imgSize,
      imgSize = _ref$imgSize === void 0 ? 150 : _ref$imgSize,
      children = _ref.children,
      accessory = _ref.accessory,
      tags = _ref.tags;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(ProviderCardContainer, {
    size: imgSize
  }, img && React__default.createElement(AvatarContainer, null, React__default.createElement(Avatar$1, _extends({}, img, {
    size: imgSize,
    border: true
  }))), name && React__default.createElement(Heading$1, {
    variant: "title3",
    color: colors.white
  }, name), location && React__default.createElement(MetaContainer, {
    spacerTop: true
  }, React__default.createElement(LocationMark$1, {
    title: location,
    textColor: colors.white,
    iconColor: colors.white
  })), tags && React__default.createElement(MetaList, {
    spacerTop2: true
  }, tags.map(function (_ref2, i) {
    var type = _ref2.type,
        text = _ref2.text;
    return React__default.createElement(CardTag, {
      variant: "headline",
      type: type
    }, text);
  })), children && React__default.createElement(ActionContainer, null, children)), accessory && React__default.createElement(AccessoryContainer, null, accessory));
};
/**
 * Styled Components
 */


var ProviderCardContainer = styled.div(_templateObject$1(), function (p) {
  return p.theme.colors.purpleMid;
}, function (p) {
  return p.size - 20;
});
var AvatarContainer = styled.div(_templateObject2$1());
var MetaContainer = styled.div(_templateObject3$1(), function (p) {
  return "\n    ".concat(layoutable(p), "\n  ");
});
var ActionContainer = styled.div(_templateObject4$1());
var AccessoryContainer = styled.div(_templateObject5$1());
/** @component */

var ProviderCard$1 = (function (props) {
  return React__default.createElement(ProviderCard, props);
});

function _templateObject8() {
  var data = _taggedTemplateLiteral(["\n  margin-top: 2rem;\n"]);

  _templateObject8 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7() {
  var data = _taggedTemplateLiteral(["\n    text-align: center;\n    p, h1, h2, h3, h4, h5 {\n      text-align: center;\n    }\n  "]);

  _templateObject7 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6() {
  var data = _taggedTemplateLiteral(["\n  flex: 3;\n  z-index: 2;\n  flex-shrink: 0;\n\n  ", "\n"]);

  _templateObject6 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5() {
  var data = _taggedTemplateLiteral(["\n    margin-bottom: 3rem;\n  "]);

  _templateObject5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4() {
  var data = _taggedTemplateLiteral(["\n  flex: 2;\n  z-index: 2;\n  display: flex;\n  justify-content: center;\n  padding-right: 2rem;\n\n  img {\n    height: 20rem;\n  }\n\n  ", "\n"]);

  _templateObject4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  z-index: 1;\n  bottom: 0;\n  left: 0;\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteral(["\n    flex-direction: column;\n    align-items: center;\n  "]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  border: 1px solid ", ";\n  border-radius: 1rem;\n  padding: 4rem 2rem;\n  display: flex;\n  position: relative;\n  overflow: hidden;\n\n  ", "\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

var PanelCard = function PanelCard(_ref) {
  var title = _ref.title,
      text = _ref.text,
      img = _ref.img,
      buttonText = _ref.buttonText,
      onClick = _ref.onClick;
  return React__default.createElement(PanelCardContainer, null, React__default.createElement(Blob, null, React__default.createElement(PanelBlob, null)), React__default.createElement(Visual, null, img && React__default.createElement("img", {
    src: img
  })), React__default.createElement(Meta, null, React__default.createElement(SectionIntro$1, {
    title: title,
    content: "<p>".concat(text, "</p>")
  }), React__default.createElement(MetaAction, null, buttonText && React__default.createElement(Button$1, {
    onClick: onClick
  }, buttonText))));
};
/**
 * Styled Components
 */


var PanelCardContainer = styled.div(_templateObject(), function (p) {
  return p.theme.colors.greyLight;
}, mq.md(_templateObject2()));
var Blob = styled.div(_templateObject3());
var Visual = styled.div(_templateObject4(), mq.md(_templateObject5()));
var Meta = styled.div(_templateObject6(), mq.md(_templateObject7()));
var MetaAction = styled.div(_templateObject8());
/** @component */

var PanelCard$1 = (function (props) {
  return React__default.createElement(PanelCard, props);
});

export { Article$1 as Article, Avatar$1 as Avatar, BlobBar, BlobBottomSlim, BlobBottomSlimReverse, BlobBottomWhite, BlobCenterPink, BlobCenterPurple, BlobGreenGrad, BlobLeftGrey, BlobPinkGrad, BlobProfile, BlobPurpleBottom, BlobPurpleGrad, BlobTop, BlobTopGrey, BlobTopSlim, BlobTopSlimReverse, BlobTopWhite, BlobTopWhiteLarge, BlobTopWhiteOpposite, BlobYellowGrad, BlobYellowStandalone, Button$1 as Button, CheckmarkList$1 as CheckmarkList, FeatureIcon$1 as FeatureIcon, FeatureItem$1 as FeatureItem, FlowArrowDown, FlowArrowLeft, FlowArrowRight, FooterBubble, Grid$1 as Grid, Heading$1 as Heading, IconArrowLeft, IconArrowRight, IconAssessment, IconBadgeVerified, IconBell, IconBook, IconBookLight, IconButton$1 as IconButton, IconCalendar, IconCheck, IconCheckSolo, IconChevronDown, IconClock, IconClose, IconCurrencyPound, IconDoc, IconEYFSCL, IconEYFSEA, IconBookLight as IconEYFSLD, IconEYFSMD, IconEYFSPD, IconEYFSPSE, IconEYFSUW, IconEducation, IconFacebook, IconGrid, IconHamburger, IconHeart, IconHide, IconHome, IconInstagram, IconLinkedIn, IconLocationPin, IconMail, IconMeal, IconMoney, IconNappy, IconObservation, IconPencil, IconPhone, IconPlay, IconPlus, IconProfile, IconQuote, IconReward, IconRocket, IconSearch, IconSearchClose, IconShow, IconSleep, IconSnapshot, IconStar, IconSuccess, IconTiney, IconTips, IconTwitter, IconUser, IconUserFamily, IconUsers, IconWalk, IconWallet, IconWarning, LightboxTheme, LocationMark$1 as LocationMark, MQ_LG, MQ_MD, MQ_SM, MQ_XL, MQ_XS, MiniProfile$1 as MiniProfile, NotificationCard$1 as NotificationCard, PARAM_REGEX_NO_BRACKETS, PanelBlob, PanelCard$1 as PanelCard, ProfileCard$1 as ProfileCard, ProviderCard$1 as ProviderCard, SearchResultHomeCard$1 as SearchResultHomeCard, Section$1 as Section, SectionIntro$1 as SectionIntro, SelectWrap, SocialRow, TWCheckboxUS as TWCheckbox, TWDate$1 as TWDate, TWForm, TWInputLabel, TWRadioGroup, TWSelect, TWSelectDumb, TWSteppedForm$1 as TWSteppedForm, TWTextInput, TWValidate, Text$2 as Text, ThemeWrapper, TineyLogo, TineyLogoLight, theme as TineyTheme, TrustedMark$1 as TrustedMark, cardStyle, cardStyleNoHover, colors, fixedStyle, fontFamily, fontWeight, getParam, layoutable, mq, resolveDepends, shadows, text };
